import React, { useState, useEffect, useRef } from 'react'
import { Routes, Route, useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { PortalLayout } from '../components/Layout'
import { supabase, getRank, getNextRankThreshold, RANK_BADGES } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import toast from 'react-hot-toast'

// 🎨 Accent color per job status — used for the colored left stripe on signing cards
// so each job is easy to tell apart at a glance.
function jobAccent(status) {
  const m = {
    new:'#C9A84C', awaiting_assignment:'#F59E0B',
    assigned:'#F59E0B', accepted:'#2D7A4F',
    on_the_way:'#185FA5', arrived:'#2D7A6A',
    completed:'#2D7A4F', scanbacks_uploaded:'#2D7A6A',
    client_approved:'#6A3A9A', payment_received:'#6A3A9A',
    notary_paid:'#2D7A4F', closed:'#8A8A8A',
    cancelled:'#E24B4A', declined:'#E24B4A',
  }
  return m[status] || '#C9A84C'
}

function statusLabel(status) {
  const m = {
    new:'New', awaiting_assignment:'Awaiting Assignment',
    assigned:'Awaiting Your Response', accepted:'Accepted',
    on_the_way:'On The Way', arrived:'Arrived',
    completed:'Signing Complete', scanbacks_uploaded:'Docs Uploaded',
    client_approved:'Final Review', payment_received:'Payment Received',
    notary_paid:'Paid', closed:'Order Complete',
    cancelled:'Cancelled', declined:'Declined',
  }
  return m[status] || (status ? status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—')
}
import { sendAdminAlert, sendJobCompleted, sendStatusUpdate, sendCredentialUploadAlert } from '../lib/email'
import { smsNotaryAssigned, smsJobCompleted } from '../lib/sms'
import { trackAccept, trackDecline, trackCompletion, getVettedProgress } from '../lib/metrics'
import JobMessages from '../components/JobMessages'
import NotaryJobDetail from './NotaryJobDetail'
import NotaryAcademy from './NotaryAcademy'
import NotaryResources from './NotaryResources'
import NotaryAssignment from './NotaryAssignment'
import Tribe from './Tribe'
import TribePodcast from './TribePodcast'

// ── AUTO COMPRESS FILE before upload ──────────────────────────
async function compressFile(file) {
  // ✅ Images — compress using canvas (no library needed!!)
  if (file.type.startsWith('image/')) {
    return new Promise((resolve) => {
      const img = new Image()
      const url = URL.createObjectURL(file)
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let { width, height } = img
        const MAX = 1800
        if (width > MAX || height > MAX) {
          const ratio = Math.min(MAX/width, MAX/height)
          width  = Math.round(width  * ratio)
          height = Math.round(height * ratio)
        }
        canvas.width  = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)
        canvas.toBlob(blob => {
          URL.revokeObjectURL(url)
          resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }))
        }, 'image/jpeg', 0.82)
      }
      img.src = url
    })
  }
  // PDFs — return as-is (browser cannot compress PDFs without external library)
  return file
}


// ✅ Safely parse jobs — ensure jsonb fields never break React!!
function safeParseJobs(jobs) {
  return (jobs || []).map(j => ({
    ...j,
    office_prefs: j.office_prefs && typeof j.office_prefs === 'object' ? j.office_prefs : {},
  }))
}

const NAV = [
  { section: 'My Work' },
  { to: '/notary',             icon: '◉', label: 'Dashboard', end: true },
  { to: '/notary/available',   icon: '⊕', label: 'Available Jobs' },
  { to: '/notary/signings',    icon: '◈', label: 'My Signings' },
  { section: 'Financials' },
  { to: '/notary/earnings',    icon: '📊', label: 'P&L Tracker' },
  { to: '/notary/paystubs',    icon: '📄', label: 'Pay Stubs' },
  { section: 'Compliance' },
  { to: '/notary/credentials', icon: '✓', label: 'My Credentials' },
  { section: 'Education' },
  { to: '/notary/academy',     icon: '🎓', label: 'LCA Notary Education' },
  { to: '/notary/resources',   icon: '📚', label: 'Resources' },
  { section: 'The Notary Lounge' },
  { to: '/notary/tribe',       icon: '💜', label: 'My Lounge' },
  { to: '/notary/podcast',     icon: '🎙️', label: 'LCA Podcast' },
  { section: 'Rewards' },
  { to: '/notary/rewards',     icon: '★', label: 'My Rewards' },
  { section: 'Account' },
  { to: '/notary/profile',     icon: '◑', label: 'My Profile' },
]

const NOTARY_STYLES = `
  /* 🎉 CONFETTI CELEBRATION */
  @keyframes lcaConfettiFall {
    0%   { transform: translateY(-20vh) rotate(0deg); opacity: 1; }
    100% { transform: translateY(120vh) rotate(720deg); opacity: 0; }
  }
  .lca-confetti-overlay {
    position: fixed; inset: 0; pointer-events: none; z-index: 9998;
    overflow: hidden;
  }
  .lca-confetti-piece {
    position: absolute; width: 10px; height: 14px; top: -20vh;
    animation: lcaConfettiFall 3.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  }
  @keyframes lcaSparkleSpin {
    0% { transform: scale(1) rotate(0deg); }
    50% { transform: scale(1.1) rotate(180deg); }
    100% { transform: scale(1) rotate(360deg); }
  }
  .lca-celebration-sparkle {
    display: inline-block; animation: lcaSparkleSpin 1.5s ease-in-out infinite;
  }
  .sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
  :focus-visible { outline: 2px solid #C9A84C; outline-offset: 3px; border-radius: 4px; }
  .lca-skip { position:absolute; top:-100px; left:16px; background:#C9A84C; color:#0D1B3E; padding:8px 16px; border-radius:8px; font-size:13px; font-weight:600; text-decoration:none; z-index:9999; transition:top .2s; }
  .lca-skip:focus { top:16px; }
  .np-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:20px; }
  @media(max-width:700px){ .np-stats { grid-template-columns:1fr 1fr; } }
  @media(max-width:400px){ .np-stats { grid-template-columns:1fr; } }
  .np-stats-3 { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; margin-bottom:20px; }
  @media(max-width:600px){ .np-stats-3 { grid-template-columns:1fr; } }
  .np-rank-card { background:#0D1B3E; border-radius:16px; padding:24px; margin-bottom:20px; display:flex; align-items:center; gap:24px; flex-wrap:wrap; }
  @media(max-width:480px){ .np-rank-card { flex-direction:column; align-items:flex-start; gap:16px; padding:18px; } }
  .np-table-wrap { overflow-x:auto; -webkit-overflow-scrolling:touch; }
  .np-form-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
  @media(max-width:580px){ .np-form-grid { grid-template-columns:1fr; } }
  .np-full { grid-column: 1 / -1; }
  .np-upload-zone { display:block; margin-top:16px; border:2px dashed #E8E6DF; border-radius:12px; padding:32px; text-align:center; cursor:pointer; color:#7A7870; font-size:14px; transition:border-color .15s; width:100%; background:none; }
  .np-upload-zone:hover, .np-upload-zone:focus-visible { border-color:#C9A84C; color:#0D1B3E; }
  .np-nav-btns { display:flex; gap:10px; justify-content:center; flex-wrap:wrap; }
  @media(max-width:480px){
    .np-job-actions { flex-direction:column !important; }
    .np-job-actions button { width:100% !important; min-height:56px !important; font-size:16px !important; }
    .np-job-card { margin-bottom:20px; border-radius:16px; }
    .np-job-body { grid-template-columns:1fr !important; }
    .np-job-header { flex-direction:column; align-items:flex-start !important; }
  }
  .np-job-card { border:1px solid #D8D4C8; border-left:6px solid #C9A84C; border-radius:14px; overflow:hidden; margin-bottom:20px; transition:box-shadow 0.2s, transform 0.2s; background:#fff; box-shadow:0 2px 12px rgba(13,27,62,0.10); }
  .np-job-card:hover { box-shadow:0 6px 20px rgba(13,27,62,0.16); transform:translateY(-2px); }
  .np-job-card.assigned { border-color:rgba(201,168,76,0.4); }
  .np-job-header { display:flex; align-items:center; justify-content:space-between; padding:16px 20px; background:#F8F7F4; border-bottom:1px solid #E8E6DF; flex-wrap:wrap; gap:10px; }
  .np-job-number { font-size:13px; font-weight:600; color:#0D1B3E; }
  .np-job-type { font-size:12px; color:#7A7870; margin-top:2px; }
  .np-job-body { display:grid; grid-template-columns:1fr 1fr; gap:12px; padding:16px 20px; }
  @media(max-width:520px){ .np-job-body { grid-template-columns:1fr; } }
  .np-job-field-label { font-size:10px; color:#B8B5AB; letter-spacing:0.06em; margin-bottom:2px; }
  .np-job-field-value { font-size:13px; color:#0D1B3E; font-weight:400; }
  .np-job-pay { font-size:20px; font-family:'Cormorant Garamond',serif; color:#2D7A4F; font-weight:500; }
  .np-job-actions { display:flex; gap:10px; padding:14px 20px; border-top:1px solid #E8E6DF; background:#FAFAF8; flex-wrap:wrap; }
  .np-job-notes { padding:10px 20px; background:#FDF6E3; border-top:1px solid rgba(201,168,76,0.2); font-size:12px; color:#8A5C00; }
  .np-empty { text-align:center; padding:48px 24px; color:#B8B5AB; }
  .np-empty-icon { font-size:40px; margin-bottom:12px; }
  .np-empty-text { font-size:14px; margin-bottom:16px; }
  .np-maps-btn { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; border-radius:8px; background:#EAF5EE; color:#2D7A4F; font-size:12px; font-weight:500; border:1px solid rgba(45,122,79,0.3); text-decoration:none; transition:all 0.15s; }
  .np-maps-btn:hover { background:#2D7A4F; color:#fff; text-decoration:none; }
  .np-tracking-wrap { padding:14px 20px; border-top:2px solid #C9A84C; background:#FFFBF0; }
  .np-tracking-label { font-size:12px; font-weight:700; color:#8A5C00; letter-spacing:0.06em; margin-bottom:8px; }
  .np-tracking-row { display:flex; gap:8px; align-items:center; }
  .np-tracking-input { flex:1; padding:8px 12px; border:1px solid #E8E6DF; border-radius:8px; font-size:13px; }
  .np-rating-wrap { padding:14px 20px; border-top:1px solid #E8E6DF; background:#FAFAF8; }
  .np-rating-label { font-size:11px; font-weight:600; color:#7A7870; letter-spacing:0.06em; margin-bottom:8px; }
  .np-stars { display:flex; gap:4px; }
  .np-star { font-size:24px; cursor:pointer; color:#E8E6DF; transition:color 0.1s; line-height:1; background:none; border:none; padding:0; }
  .np-star.active { color:#C9A84C; }

  /* ── ELITE REWARDS ── */
  .elite-rewards-hero { background:linear-gradient(135deg, #0D1B3E 0%, #1A2F5E 50%, #0D1B3E 100%); border-radius:20px; padding:32px; margin-bottom:24px; position:relative; overflow:hidden; }
  .elite-rewards-hero::before { content:'★'; position:absolute; right:-20px; top:-20px; font-size:160px; color:rgba(201,168,76,0.06); line-height:1; }
  .elite-milestone { background:#fff; border:1px solid #E8E6DF; border-radius:14px; padding:20px; margin-bottom:12px; display:flex; align-items:center; gap:16px; transition:border-color 0.2s; }
  .elite-milestone.unlocked { border-color:rgba(201,168,76,0.4); background:#FAFDF5; }
  .elite-milestone.locked { opacity:0.6; }
  .elite-milestone-icon { width:48px; height:48px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:22px; flex-shrink:0; }
  .elite-milestone.unlocked .elite-milestone-icon { background:linear-gradient(135deg,#C9A84C,#E8C46A); }
  .elite-milestone.locked .elite-milestone-icon { background:#E8E6DF; }
  .elite-download-card { background:linear-gradient(135deg,#0D1B3E,#1A3A5E); border-radius:16px; padding:24px; margin-top:16px; }
  .elite-download-btn { display:inline-flex; align-items:center; gap:8px; padding:12px 24px; border-radius:10px; font-size:13px; font-weight:600; text-decoration:none; transition:all 0.2s; border:none; cursor:pointer; }
  .elite-download-btn.windows { background:#C9A84C; color:#0D1B3E; }
  .elite-download-btn.mac { background:rgba(201,168,76,0.15); color:#C9A84C; border:1px solid rgba(201,168,76,0.3); }
  .elite-download-btn:hover { transform:translateY(-2px); text-decoration:none; }
  .elite-download-btn.windows:hover { background:#E8C46A; }
  .elite-download-btn.mac:hover { background:rgba(201,168,76,0.25); }
  .elite-locked-overlay { background:#F8F7F4; border:2px dashed #E8E6DF; border-radius:16px; padding:32px; text-align:center; margin-top:16px; }
`

/* ── VIEW AGREEMENT MODAL — read-only, shows full text + signature + timestamp ── */
function ViewAgreementModal({ agreementType, signedAt, signature, onClose }) {
  const agreements = {
    ica_agreed_at: {
      title: 'Independent Contractor Agreement',
      icon:  '📋',
      color: '#185FA5',
      bg:    '#EBF4FF',
      text:  'I understand and agree that: (1) I am an independent contractor, not an employee of Loan Closing Agent LLC; (2) I am responsible for my own taxes, insurance, and expenses; (3) I will maintain professional standards and comply with all applicable notary laws; (4) I may accept or decline assignments at my discretion; (5) Payment is contingent upon successful completion of assigned signings.',
    },
    nppi_agreed_at: {
      title: 'Confidentiality & Data Protection',
      icon:  '🔒',
      color: '#6A3A9A',
      bg:    '#F0EBF8',
      text:  'I acknowledge that in performing signing services, I will have access to Non-Public Personal Information (NPPI) including Social Security Numbers, financial records, and other sensitive borrower data. I agree to: (1) Maintain strict confidentiality of all NPPI; (2) Use NPPI only for the purpose of completing assigned signings; (3) Securely store and dispose of all documents containing NPPI; (4) Report any data breaches immediately; (5) Comply with all federal and state privacy laws including GLBA and state-specific regulations.',
    },
    nonsolicitation_agreed_at: {
      title: 'Non-Solicitation Agreement',
      icon:  '🤝',
      color: '#2D7A4F',
      bg:    '#EAF5EE',
      text:  "During the term of this agreement and for 12 months thereafter, I agree not to: (1) Solicit or accept signing business directly from clients referred by LCA; (2) Recruit or solicit other LCA notaries to leave the network; (3) Share LCA's proprietary processes, pricing, or client lists with competitors. I understand this protects LCA's business relationships while allowing me to maintain my independent notary practice.",
    },
    signed_at: {
      title: 'Application Signature & Certification',
      icon:  '✍️',
      color: '#9A7A30',
      bg:    '#FDF6E3',
      text:  "I certify that all information provided in this application is true, complete, and accurate to the best of my knowledge. I understand that any false statements or omissions may result in immediate termination from the LCA network. I have read and agree to all terms and conditions outlined in the Independent Contractor Agreement, Confidentiality Agreement, and Non-Solicitation Agreement.",
    },
  }

  const agreement = agreements[agreementType]
  if (!agreement) return null

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#fff', borderRadius:16, maxWidth:580, width:'100%', maxHeight:'85vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ background:`linear-gradient(135deg,${agreement.bg},#fff)`, padding:'20px 24px', borderBottom:'1px solid #E8E6DF', borderRadius:'16px 16px 0 0' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
            <span style={{ fontSize:22 }}>{agreement.icon}</span>
            <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, fontWeight:600, color:'#0D1B3E' }}>{agreement.title}</div>
          </div>
          <div style={{ fontSize:11, color:'#7A7870' }}>Read-only — this agreement has been signed and is legally binding</div>
        </div>

        {/* Agreement text */}
        <div style={{ padding:'20px 24px' }}>
          <div style={{ background:'#F8F7F4', borderRadius:10, padding:16, fontSize:13, lineHeight:1.85, color:'#4A4840', marginBottom:20 }}>
            {agreement.text}
          </div>

          {/* Signature block */}
          <div style={{ background:`${agreement.bg}`, border:`1px solid ${agreement.color}30`, borderRadius:12, padding:'16px 20px' }}>
            <div style={{ fontSize:10, fontWeight:700, color:agreement.color, letterSpacing:'0.08em', marginBottom:10 }}>✅ DIGITAL SIGNATURE ON FILE</div>
            <div style={{ display:'flex', gap:20, flexWrap:'wrap' }}>
              <div>
                <div style={{ fontSize:10, color:'#B8B5AB', marginBottom:3 }}>SIGNED BY</div>
                <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, color:'#0D1B3E', fontWeight:500 }}>{signature || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize:10, color:'#B8B5AB', marginBottom:3 }}>DATE & TIME</div>
                <div style={{ fontSize:13, color:'#0D1B3E', fontWeight:500 }}>
                  {signedAt ? new Date(signedAt).toLocaleString('en-US', { year:'numeric', month:'long', day:'numeric', hour:'numeric', minute:'2-digit' }) : '—'}
                </div>
                <div style={{ fontSize:11, color:'#7A7870' }}>
                  {signedAt ? new Date(signedAt).toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit', timeZoneName:'short' }) : ''}
                </div>
              </div>
            </div>
            <div style={{ marginTop:10, fontSize:10, color:'#B8B5AB', fontStyle:'italic' }}>
              This digital signature is legally binding under the Electronic Signatures in Global and National Commerce Act (E-SIGN Act).
            </div>
          </div>
        </div>

     {/* Action buttons — Print + Close */}
        <div style={{ padding:'0 24px 20px', display:'flex', gap:10 }}>
          <button onClick={() => {
            const printWindow = window.open('', '_blank')
            if (!printWindow) { alert('Please allow pop-ups to print this agreement!!'); return }
            printWindow.document.write(`<!DOCTYPE html><html><head><title>${agreement.title} — LCA</title><style>
              *{margin:0;padding:0;box-sizing:border-box;}
              body{font-family:'Georgia',serif;color:#0D1B3E;padding:40px 50px;line-height:1.7;}
              .header{text-align:center;border-bottom:3px solid #C9A84C;padding-bottom:20px;margin-bottom:30px;}
              .co-name{font-size:18px;font-weight:700;color:#0D1B3E;letter-spacing:0.04em;}
              .co-sub{font-size:11px;color:#9A7A30;letter-spacing:0.1em;margin-top:4px;text-transform:uppercase;}
              h1{font-family:'Cormorant Garamond',Georgia,serif;font-size:28px;color:${agreement.color};margin-top:18px;font-weight:600;}
              .body-text{background:#F8F7F4;padding:20px;border-left:4px solid ${agreement.color};border-radius:6px;font-size:14px;line-height:1.9;margin-bottom:30px;}
              .sig-block{background:${agreement.bg};border:2px solid ${agreement.color};border-radius:10px;padding:20px;margin-bottom:20px;}
              .sig-label{font-size:11px;font-weight:700;color:${agreement.color};letter-spacing:0.08em;margin-bottom:12px;}
              .sig-row{display:flex;gap:40px;flex-wrap:wrap;}
              .sig-field{margin-bottom:8px;}
              .sig-field-label{font-size:10px;color:#7A7870;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:4px;}
              .sig-field-value{font-family:'Cormorant Garamond',Georgia,serif;font-size:22px;color:#0D1B3E;font-weight:500;}
              .sig-field-date{font-size:13px;color:#0D1B3E;}
              .footer{margin-top:40px;padding-top:20px;border-top:1px solid #E8E6DF;font-size:11px;color:#7A7870;text-align:center;font-style:italic;}
              @media print{@page{margin:0.5in;}body{padding:0;}}
            </style></head><body>
              <div class="header">
                <div class="co-name">Loan Closing Agent, LLC.</div>
                <div class="co-sub">DBA Nationwide Notary Professionals Network</div>
                <h1>${agreement.icon} ${agreement.title}</h1>
              </div>
              <div class="body-text">${agreement.text}</div>
              <div class="sig-block">
                <div class="sig-label">✅ DIGITAL SIGNATURE ON FILE</div>
                <div class="sig-row">
                  <div class="sig-field">
                    <div class="sig-field-label">SIGNED BY</div>
                    <div class="sig-field-value">${signature || '—'}</div>
                  </div>
                  <div class="sig-field">
                    <div class="sig-field-label">DATE & TIME</div>
                    <div class="sig-field-date">${signedAt ? new Date(signedAt).toLocaleString('en-US', { year:'numeric', month:'long', day:'numeric', hour:'numeric', minute:'2-digit' }) : '—'}</div>
                    <div class="sig-field-date">${signedAt ? new Date(signedAt).toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit', timeZoneName:'short' }) : ''}</div>
                  </div>
                </div>
              </div>
              <div class="footer">This digital signature is legally binding under the Electronic Signatures in Global and National Commerce Act (E-SIGN Act).<br/>Printed on ${new Date().toLocaleString()} · Loan Closing Agent, LLC. · loanclosingagent.com</div>
              <script>window.onload=function(){window.print()}<\/script>
            </body></html>`)
            printWindow.document.close()
          }} style={{ flex:1, padding:12, borderRadius:10, border:'none', background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', color:'#C9A84C', cursor:'pointer', fontSize:13, fontWeight:600, fontFamily:'inherit' }}>
            🖨️ Print for My Records
          </button>
          <button onClick={onClose} style={{ flex:1, padding:12, borderRadius:10, border:'1px solid #E0D9CC', background:'#fff', color:'#0D1B3E', cursor:'pointer', fontSize:13, fontWeight:600, fontFamily:'inherit' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}


function SignAgreementModal({ agreementType, onClose, onSign }) {
  const [signature, setSignature] = useState('')
  
  const agreements = {
    ica_agreed_at: {
      title: 'Independent Contractor Agreement',
      text: 'I understand and agree that: (1) I am an independent contractor, not an employee of Loan Closing Agent LLC; (2) I am responsible for my own taxes, insurance, and expenses; (3) I will maintain professional standards and comply with all applicable notary laws; (4) I may accept or decline assignments at my discretion; (5) Payment is contingent upon successful completion of assigned signings.'
    },
    nppi_agreed_at: {
      title: 'Confidentiality & Data Protection',
      text: 'I acknowledge that in performing signing services, I will have access to Non-Public Personal Information (NPPI) including Social Security Numbers, financial records, and other sensitive borrower data. I agree to: (1) Maintain strict confidentiality of all NPPI; (2) Use NPPI only for the purpose of completing assigned signings; (3) Securely store and dispose of all documents containing NPPI; (4) Report any data breaches immediately; (5) Comply with all federal and state privacy laws including GLBA and state-specific regulations.'
    },
    nonsolicitation_agreed_at: {
      title: 'Non-Solicitation Agreement',
      text: 'During the term of this agreement and for 12 months thereafter, I agree not to: (1) Solicit or accept signing business directly from clients referred by LCA; (2) Recruit or solicit other LCA notaries to leave the network; (3) Share LCA\'s proprietary processes, pricing, or client lists with competitors. I understand this protects LCA\'s business relationships while allowing me to maintain my independent notary practice.'
    },
    signed_at: {
      title: 'Application Signature',
      text: 'I certify that all information provided in this application is true, complete, and accurate to the best of my knowledge. I understand that any false statements or omissions may result in immediate termination from the LCA network. I have read and agree to all terms and conditions outlined in the Independent Contractor Agreement, Confidentiality Agreement, and Non-Solicitation Agreement.'
    }
  }
  
  const agreement = agreements[agreementType]
  if (!agreement) {
    onClose()
    return null
  }
  
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#fff', borderRadius:16, maxWidth:600, width:'100%', maxHeight:'90vh', overflowY:'auto', padding:28 }}>
        <h2 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, color:'#0D1B3E', marginBottom:12 }}>{agreement.title}</h2>
        <div style={{ background:'#F8F7F4', padding:16, borderRadius:10, marginBottom:16, maxHeight:200, overflowY:'auto', fontSize:12, lineHeight:1.8, color:'#4A4840' }}>
          {agreement.text}
        </div>
        <div style={{ fontSize:11, color:'#7A7870', marginBottom:12 }}>
          🕐 Signed on {new Date().toLocaleString()}
        </div>
        <input 
          type="text" 
          placeholder="Type your full legal name to sign"
          value={signature}
          onChange={e => setSignature(e.target.value)}
          style={{ width:'100%', padding:12, border:'1.5px solid #E0D9CC', borderRadius:10, marginBottom:16, fontSize:13 }}
        />
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={onClose} style={{ flex:1, padding:12, borderRadius:10, border:'1px solid #E0D9CC', background:'#fff', color:'#7A7870', cursor:'pointer' }}>Cancel</button>
          <button onClick={() => onSign(signature, agreementType)} disabled={!signature} style={{ flex:1, padding:12, borderRadius:10, border:'none', background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', color:'#C9A84C', cursor:'pointer', fontWeight:600, opacity:signature?1:0.5 }}>Sign Agreement</button>
        </div>
      </div>
    </div>
  )
}

/* ── MAPS BUTTON ── */
function MapsButton({ address, city, state, zip }) {
  const fullAddress = encodeURIComponent(`${address}, ${city}, ${state} ${zip || ''}`.trim())
  const googleUrl   = `https://www.google.com/maps/dir/?api=1&destination=${fullAddress}`
  const appleUrl    = `https://maps.apple.com/?daddr=${fullAddress}`
  const isIOS       = /iPad|iPhone|iPod/.test(navigator.userAgent)
  const mapsUrl     = isIOS ? appleUrl : googleUrl
  return (
    <a href={mapsUrl} target="_blank" rel="noreferrer" className="np-maps-btn" aria-label="Open address in maps app">
      🗺️ Open in Maps
    </a>
  )
}

/* ── STAR RATING ── */
function StarRating({ jobId, onRated }) {
  const [hovered,  setHovered]  = useState(0)
  const [selected, setSelected] = useState(0)
  const [saving,   setSaving]   = useState(false)

  async function rate(stars) {
    setSelected(stars)
    setSaving(true)
    try {
      await supabase.from('jobs').update({ client_rating: stars }).eq('id', jobId)
      toast.success(`Thank you for your ${stars}-star rating!!`)
      if (onRated) onRated(stars)
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  return (
    <div className="np-rating-wrap">
      <div className="np-rating-label">RATE THIS SIGNING</div>
      <div className="np-stars" role="group" aria-label="Rate this signing">
        {[1,2,3,4,5].map(star => (
          <button key={star} className={`np-star${(hovered || selected) >= star ? ' active' : ''}`} onMouseEnter={() => setHovered(star)} onMouseLeave={() => setHovered(0)} onClick={() => rate(star)} disabled={saving || selected > 0} aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}>★</button>
        ))}
      </div>
      {selected > 0 && <div style={{ fontSize:11, color:'#2D7A4F', marginTop:6 }}>✓ Rated {selected} stars — thank you!!</div>}
    </div>
  )
}

/* ── NOTARY RATES CLIENT — Admin eyes only!! ── */
function NotaryRatesClient({ jobId, clientName, onRated }) {
  const [rating,    setRating]    = useState(0)
  const [hover,     setHover]     = useState(0)
  const [comment,   setComment]   = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [saving,    setSaving]    = useState(false)

  useEffect(() => {
    supabase.from('jobs').select('notary_rating, notary_rating_comment').eq('id', jobId).single()
      .then(({ data }) => {
        if (data?.notary_rating) {
          setRating(data.notary_rating)
          setComment(data.notary_rating_comment || '')
          setSubmitted(true)
        }
      })
  }, [jobId])

  async function submitRating() {
    if (!rating) { toast.error('Please select a rating!!'); return }
    setSaving(true)
    try {
      await supabase.from('jobs').update({
        notary_rating:         rating,
        notary_rating_comment: comment || null,
        notary_rated_at:       new Date().toISOString(),
      }).eq('id', jobId)
      setSubmitted(true)
      if (onRated) onRated(rating)
      toast.success('Feedback submitted!! Only LCA admin can see this!!')
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  if (submitted) return (
    <div className="np-rating-wrap">
      <div style={{ fontSize:11, color:'#7A7870', fontStyle:'italic' }}>🔒 Client feedback submitted — admin only</div>
    </div>
  )

  return (
    <div className="np-rating-wrap" style={{ background:'#F8F7F4', borderTop:'1px solid #E8E6DF' }}>
      <div className="np-rating-label" style={{ color:'#7A7870' }}>🔒 RATE THIS CLIENT — PRIVATE (Admin eyes only)</div>
      <div className="np-stars" role="group" aria-label="Rate this client">
        {[1,2,3,4,5].map(s => (
          <button key={s} type="button"
            className={`np-star${(hover || rating) >= s ? ' active' : ''}`}
            onMouseEnter={() => setHover(s)}
            onMouseLeave={() => setHover(0)}
            onClick={() => setRating(s)}
            aria-label={`Rate ${s} star${s > 1 ? 's' : ''}`}
          >★</button>
        ))}
        {rating > 0 && <span style={{ fontSize:11, color:'#7A7870', marginLeft:4 }}>{['','Poor','Fair','OK','Good','Excellent'][rating]}</span>}
      </div>
      <textarea
        value={comment}
        onChange={e => setComment(e.target.value)}
        placeholder="Any notes about this client?? (optional — only LCA admin sees this)"
        rows={2}
        style={{ width:'100%', padding:'8px 12px', border:'1.5px solid #E0D9CC', borderRadius:8, fontSize:11, resize:'none', marginTop:8, marginBottom:8, fontFamily:'inherit', background:'#fff' }}
      />
      <button className="btn btn-outline btn-sm" onClick={submitRating} disabled={saving || !rating} style={{ fontSize:11 }}>
        {saving ? 'Submitting...' : '🔒 Submit Private Feedback'}
      </button>
    </div>
  )
}

/* ── TRACKING NUMBER ── */
// 🚚 Build a carrier tracking URL (shared shape with Admin + Client portals)
function trackingUrl(carrier, num) {
  const n = encodeURIComponent((num || '').trim())
  const c = (carrier || '').toUpperCase()
  if (c.includes('UPS'))  return `https://www.ups.com/track?tracknum=${n}`
  if (c.includes('USPS')) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${n}`
  return `https://www.fedex.com/fedextrack/?trknbr=${n}` // default FedEx
}

function TrackingInput({ job }) {
  const jobId = job?.id
  const [tracking, setTracking] = useState('')
  const [carrier,  setCarrier]  = useState('FedEx')
  const [saving,   setSaving]   = useState(false)
  const [savedNum,     setSavedNum]     = useState(job?.tracking_number || '')
  const [savedCarrier, setSavedCarrier] = useState(job?.shipping_carrier || 'FedEx')

  async function saveTracking() {
    if (!tracking.trim()) return toast.error('Please enter a tracking number')
    setSaving(true)
    try {
      await supabase.from('jobs').update({
        tracking_number:  tracking.trim(),
        shipping_carrier: carrier,
        shipped_at:       job?.shipped_at || new Date().toISOString(),
      }).eq('id', jobId)
      setSavedNum(tracking.trim()); setSavedCarrier(carrier)
      toast.success('Tracking number saved — client has been notified!!')
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  // ✅ RIBBON — tracking is on file (entered here, or from Admin)
  if (savedNum) return (
    <div className="np-tracking-wrap" style={{ background:'#F0F7F2', border:'1px solid #B8DCC8' }}>
      <div className="np-tracking-label" style={{ color:'#2D7A4F' }}>🚚 SHIPPED — TRACKING ON FILE</div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, flexWrap:'wrap' }}>
        <div>
          <div style={{ fontSize:13, fontWeight:600, color:'#0D1B3E' }}>{savedCarrier} · {savedNum}</div>
          {job?.shipped_at && <div style={{ fontSize:11, color:'#7A7870' }}>Shipped {new Date(job.shipped_at).toLocaleString()}</div>}
        </div>
        <a href={trackingUrl(savedCarrier, savedNum)} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm" style={{ textDecoration:'none', whiteSpace:'nowrap' }}>
          📍 Track Package
        </a>
      </div>
    </div>
  )

  // ✅ INPUT — pick carrier + enter number
  return (
    <div className="np-tracking-wrap">
      <div className="np-tracking-label">📦 ENTER TRACKING NUMBER</div>
      <div style={{ display:'flex', gap:8, marginBottom:8, flexWrap:'wrap' }}>
        {['FedEx','UPS','USPS'].map(c => (
          <button key={c} type="button" onClick={() => setCarrier(c)}
            style={{ padding:'8px 14px', borderRadius:8, cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:600,
                     border: carrier===c ? '2px solid #0D1B3E' : '1px solid #E8E6DF',
                     background: carrier===c ? '#0D1B3E' : '#fff',
                     color: carrier===c ? '#fff' : '#7A7870' }}>
            {c}
          </button>
        ))}
      </div>
      <div className="np-tracking-row">
        <input className="np-tracking-input" placeholder="e.g. 1Z999AA10123456784" value={tracking} onChange={e => setTracking(e.target.value)} aria-label="Tracking number" />
        <button className="btn btn-primary btn-sm" onClick={saveTracking} disabled={saving} style={{ whiteSpace:'nowrap' }}>
          {saving ? '...' : 'Save & Notify'}
        </button>
      </div>
    </div>
  )
}

/* ── NOTARY DOCUMENT SECTION ── */
function NotaryDocSection({ jobId, status }) {
  const [docs,      setDocs]      = useState([])
  const [uploading, setUploading] = useState(false)
  const [open,      setOpen]      = useState(true)
  // 🔒 Signing package is downloadable ONLY after the notary has accepted (or beyond).
  const canAccessPackage = !['assigned','new','awaiting_assignment','declined','cancelled'].includes(status)

  useEffect(() => {
    if (!open) return
    loadDocs()
  }, [open, jobId])

  async function loadDocs() {
    supabase.from('job_documents').select('*').eq('job_id', jobId)
      .order('created_at', { ascending: false })
      .then(({ data }) => setDocs(data || []))
  }

  // ✅ Record download timestamp for ALTA compliance!!
  async function recordDownload(doc) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      await supabase.from('job_documents').update({
        downloaded_at: new Date().toISOString(),
        downloaded_by: user?.id,
      }).eq('id', doc.id)
      // ✅ Also update job closing_package_uploaded_at for exception reporting!!
      await supabase.from('jobs').update({
        docs_downloaded_at: new Date().toISOString(),
      }).eq('id', jobId)
      // Open the file
      window.open(doc.file_url, '_blank')
      loadDocs()
    } catch (err) {
      console.error('Download tracking error:', err)
      window.open(doc.file_url, '_blank')
    }
  }

  async function uploadCompleted(e) {
    const file = e.target.files[0]
    if (!file) return
    setUploading(true)
    try {
      const compressed = await compressFile(file)
      const ext  = compressed.name.split('.').pop()
      const path = `${jobId}/scanback-${Date.now()}.${ext}`
      const { error } = await supabase.storage.from('job-documents').upload(path, compressed, { upsert: true })
      if (error) throw error
      const { data: { publicUrl } } = supabase.storage.from('job-documents').getPublicUrl(path)
      const { data: { user } } = await supabase.auth.getUser()
      await supabase.from('job_documents').insert({
        job_id: jobId, uploaded_by: user?.id, role: 'notary',
        doc_type: 'scan_back', file_name: file.name,
        file_url: publicUrl, file_size: file.size, qc_status: 'pending',
      })
      // Advance "completed" → "scanbacks_uploaded" so the admin + client tracker move forward (only from completed; no regression)
      await supabase.from('jobs').update({ status: 'scanbacks_uploaded', scanbacks_at: new Date().toISOString() }).eq('id', jobId).eq('status', 'completed')
      toast.success('✅ Scan-back uploaded successfully!! Client will be notified!!')
      e.target.value = ''
      loadDocs()
    } catch (err) {
      toast.error('Upload failed: ' + err.message)
    } finally {
      setUploading(false)
    }
  }

  const signingPkg = docs.filter(d => d.doc_type === 'signing_package')
  const scanBacks  = docs.filter(d => d.doc_type === 'scan_back' || d.doc_type === 'completed_docs')

  return (
    <div style={{ borderTop:'2px solid #C9A84C', background:'#FFFBF0' }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{ width:'100%', padding:'12px 18px', background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:12, fontWeight:600, color:'#8A5C00' }}>
        <span>📁 Document Vault {scanBacks.length > 0 && <span style={{ background:'#2D7A4F', color:'#fff', fontSize:10, padding:'1px 6px', borderRadius:10, marginLeft:6 }}>{scanBacks.length} scan-back{scanBacks.length > 1 ? 's' : ''}</span>}</span>
        <span style={{ color:'#B8B5AB' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ padding:'0 18px 16px' }}>
          {/* SIGNING PACKAGE — 🔒 download locked until the notary has accepted */}
          {!canAccessPackage ? (
            <div style={{ fontSize:12, color:'#9A6800', marginBottom:14, padding:'10px 12px', background:'#FEF3E2', border:'1px solid #F59E0B', borderRadius:8 }}>
              🔒 Accept this assignment to view and download the signing package.
            </div>
          ) : (
            <>
              {signingPkg.length > 0 && (
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:11, fontWeight:600, color:'#185FA5', marginBottom:8, letterSpacing:'0.06em' }}>📦 SIGNING PACKAGE — Download & Print!!</div>
                  {signingPkg.map(d => (
                    <div key={d.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #F0EEE8' }}>
                      <div>
                        <div style={{ fontSize:13, color:'#0D1B3E' }}>{d.file_name}</div>
                        {d.notes && <div style={{ fontSize:11, color:'#7A7870' }}>{d.notes}</div>}
                        {d.downloaded_at && <div style={{ fontSize:10, color:'#2D7A4F', marginTop:2 }}>✅ Downloaded {new Date(d.downloaded_at).toLocaleString()}</div>}
                      </div>
                      <button onClick={() => recordDownload(d)} className="btn btn-primary btn-sm">⬇️ Download</button>
                    </div>
                  ))}
                </div>
              )}
              {signingPkg.length === 0 && <div style={{ fontSize:12, color:'#B8B5AB', marginBottom:14, padding:'8px 0' }}>📦 No signing package uploaded yet!!</div>}
            </>
          )}

          {/* SCAN-BACKS */}
          <div style={{ fontSize:11, fontWeight:600, color:'#2D7A4F', marginBottom:8, letterSpacing:'0.06em' }}>📄 SCAN-BACKS — Upload Completed Documents</div>
          {scanBacks.length === 0 && <div style={{ fontSize:12, color:'#B8B5AB', marginBottom:10 }}>No scan-backs uploaded yet!!</div>}
          {scanBacks.map(d => (
            <div key={d.id} style={{ marginBottom:8 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #F0EEE8', fontSize:12 }}>
                <div>
                  <span style={{ color:'#0D1B3E' }}>📄 {d.file_name}</span>
                  <span style={{ fontSize:10, color:'#B8B5AB', marginLeft:8 }}>{new Date(d.created_at).toLocaleString()}</span>
                </div>
                <span style={{ fontSize:10, fontWeight:600, padding:'2px 8px', borderRadius:10,
                  background: d.qc_status === 'approved' ? '#EAF5EE' : d.qc_status === 'rejected' ? '#FDF2F2' : '#FDF6E3',
                  color: d.qc_status === 'approved' ? '#2D7A4F' : d.qc_status === 'rejected' ? '#C0392B' : '#8A5C00'
                }}>
                  {d.qc_status === 'approved' ? '✓ Approved — Ship Docs!!' : d.qc_status === 'rejected' ? '❌ Corrections Needed' : '⏳ Pending QC'}
                </span>
              </div>
              {/* ✅ Show correction notes to notary!! */}
              {d.qc_status === 'rejected' && d.qc_correction_notes && (
                <div style={{ background:'#FDF2F2', border:'1px solid rgba(226,75,74,0.2)', borderRadius:6, padding:'8px 10px', marginTop:4 }}>
                  <div style={{ fontSize:11, fontWeight:600, color:'#C0392B', marginBottom:3 }}>📝 Correction Notes from Title:</div>
                  <div style={{ fontSize:12, color:'#E24B4A' }}>{d.qc_correction_notes}</div>
                  <div style={{ fontSize:10, color:'#B8B5AB', marginTop:4 }}>Please make the corrections and upload a new scan-back!!</div>
                </div>
              )}
              {d.qc_status === 'approved' && (
                <div style={{ fontSize:10, color:'#2D7A4F', marginTop:2, fontWeight:500 }}>
                  ✅ Title approved!! You can now ship the documents!!
                </div>
              )}
            </div>
          ))}

          <label htmlFor={`scanback-${jobId}`} style={{ display:'inline-block', marginTop:10, cursor: uploading ? 'wait' : 'pointer' }}>
            <input id={`scanback-${jobId}`} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display:'none' }} onChange={uploadCompleted} disabled={uploading} />
            <span className="btn btn-outline btn-sm" style={{ fontSize:11, pointerEvents:'none' }}>
              {uploading ? '⏳ Uploading...' : '📤 Upload Scan-Back'}
            </span>
          </label>
          <div style={{ fontSize:10, color:'#B8B5AB', marginTop:6 }}>PDF, JPG, or PNG — auto-compressed for fast upload!!</div>
        </div>
      )}
    </div>
  )
}

/* ── DASHBOARD ── */
// ── 🎉 CONFETTI CELEBRATION COMPONENT ─────────────────────────
function ConfettiCelebration({ active, onDone }) {
  useEffect(() => {
    if (!active) return
    const timer = setTimeout(() => { onDone?.() }, 4000)
    return () => clearTimeout(timer)
  }, [active, onDone])

  if (!active) return null

  const colors = ['#C9A84C', '#0D1B3E', '#2D7A4F', '#fff', '#FDF6E3']
  const pieces = Array.from({ length: 80 }, (_, i) => {
    const color = colors[i % colors.length]
    const left  = (Math.random() * 100).toFixed(2)
    const delay = (Math.random() * 0.8).toFixed(2)
    const rotate = (Math.random() * 360).toFixed(0)
    const isRect = i % 3 === 0
    return (
      <div
        key={i}
        className="lca-confetti-piece"
        style={{
          left: `${left}vw`,
          background: color,
          borderRadius: isRect ? '2px' : '50%',
          animationDelay: `${delay}s`,
          transform: `rotate(${rotate}deg)`,
        }}
      />
    )
  })

  return <div className="lca-confetti-overlay" aria-hidden="true">{pieces}</div>
}

function NotaryDashboard() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [notary, setNotary] = useState(null)
  const [jobs,   setJobs]   = useState([])
  const [credCount, setCredCount] = useState(null)
  const [vettedProgress, setVettedProgress] = useState(null)
  const [readyToWork, setReadyToWork] = useState(false)
  const [confettiActive, setConfettiActive] = useState(false)
  const [showSignModal, setShowSignModal] = useState(false)
  const [signParam, setSignParam] = useState(null)   
  const [vettedBannerDismissed, setVettedBannerDismissed] = useState(false)
  const [abandonedTrack, setAbandonedTrack] = useState(null) // 📧 Most recently started course not yet finished

  // ✅ Check for sign query param — only show modal if NOT already signed
  useEffect(() => {
    const param = searchParams.get('sign')
    if (!param) return
    if (!['ica_agreed_at', 'nppi_agreed_at', 'nonsolicitation_agreed_at', 'signed_at'].includes(param)) return
    if (!user) return
    // ✅ Check if already signed before showing modal
    supabase.from('notaries').select(param).eq('id', user.id).single()
      .then(({ data }) => {
        if (data?.[param]) {
          // Already signed — show toast, redirect back, do NOT open modal
          toast.success('This agreement is already signed!! ✅', { duration: 3000 })
          navigate('/notary/credentials')
        } else {
          setSignParam(param)
          setShowSignModal(true)
        }
      })
  }, [searchParams, user])

  useEffect(() => {
    if (!user) return
    supabase.from('notaries').select('*').eq('id', user.id).single().then(({ data }) => {
      setNotary(data)
      setReadyToWork(data?.ready_to_work === true)
      // 🏆 Check if the Vetted celebration banner was permanently dismissed.
      // Keyed to vetted_at so a future re-vetting can show a fresh banner.
      if (data?.vetted_at) {
        const dismissKey = `vetted_banner_dismissed_${data.vetted_at}`
        if (localStorage.getItem(dismissKey) === 'true') setVettedBannerDismissed(true)
      }
    })
    supabase.from('jobs').select('*').eq('notary_id', user.id).order('scheduled_at', { ascending: false }).limit(10).then(({ data }) => setJobs(safeParseJobs(data)))
    supabase.from('notary_docs').select('id', { count:'exact', head:true }).eq('notary_id', user.id).then(({ count }) => setCredCount(count || 0))
   // 🏆 Fetch Vetted progress
    getVettedProgress(user.id).then(setVettedProgress)

    // 📧 Detect abandoned course — notary started but hasn't passed the final exam.
    // Persists in the UI until they finish the course (no timer, no dismiss).
    ;(async () => {
      // 1. This notary's module progress rows
      const { data: startedRows } = await supabase
        .from('academy_progress')
        .select('track_id, quiz_passed, created_at')
        .eq('notary_id', user.id)
        .order('created_at', { ascending: false })
      if (!startedRows || startedRows.length === 0) return

      // 2. Tracks finished via a passed final exam (for courses that use one)
      const { data: passedRows } = await supabase
        .from('academy_final_exam_progress')
        .select('track_id')
        .eq('notary_id', user.id)
        .eq('passed', true)
      const passedTrackIds = new Set((passedRows || []).map(r => r.track_id))

      // 3. A track is ALSO finished when every published module quiz is passed —
      //    this is how the Certificate of Proficiency is actually earned.
      const { data: modules } = await supabase
        .from('academy_modules')
        .select('track_id')
        .eq('published', true)
      const moduleCount = {}
      ;(modules || []).forEach(m => { moduleCount[m.track_id] = (moduleCount[m.track_id] || 0) + 1 })
      const passedModuleCount = {}
      startedRows.forEach(r => { if (r.quiz_passed) passedModuleCount[r.track_id] = (passedModuleCount[r.track_id] || 0) + 1 })

      const isComplete = (trackId) =>
        passedTrackIds.has(trackId) ||
        (moduleCount[trackId] > 0 && (passedModuleCount[trackId] || 0) >= moduleCount[trackId])

      // Most-recently-started track that isn't finished yet
      const abandonedRow = startedRows.find(r => !isComplete(r.track_id))
      if (!abandonedRow) return

      // 4. Get the track title to show in the banner
      const { data: track } = await supabase
        .from('academy_tracks')
        .select('id, title')
        .eq('id', abandonedRow.track_id)
        .eq('published', true)
        .single()
      if (track) setAbandonedTrack(track)
    })()
  }, [user])

  // 🎉 Confetti triggers — Vetted promotion + milestone signings
  useEffect(() => {
    if (!notary || !vettedProgress || !user) return
    const celebrationKey = `lca_celebrated_${user.id}`
    let already = {}
    try { already = JSON.parse(sessionStorage.getItem(celebrationKey) || '{}') } catch {}

    // Vetted celebration (first time seen this session)
    if (vettedProgress.is_vetted && !already.vetted) {
      setConfettiActive(true)
      already.vetted = true
      sessionStorage.setItem(celebrationKey, JSON.stringify(already))
      return
    }

    // Milestone signings celebration (100, 500, 1000, 2500, 5000)
    const milestones = [100, 500, 1000, 2500, 5000]
    const signings = notary.total_signings || 0
    const hitMilestone = milestones.find(m => signings === m)
    if (hitMilestone && !already[`signings_${hitMilestone}`]) {
      setConfettiActive(true)
      already[`signings_${hitMilestone}`] = true
      sessionStorage.setItem(celebrationKey, JSON.stringify(already))
      toast.success(`🎉 ${hitMilestone} SIGNINGS!! What a milestone — celebrating YOU, ${profile?.full_name}!!`, { duration: 8000 })
    }
  }, [notary, vettedProgress, user, profile])

  async function handleSign(sig, type) {
    if (!sig.trim()) {
      toast.error('Please enter your full legal name')
      return
    }
    
    try {
      await supabase.from('notaries').update({
        [type]: new Date().toISOString(),
        digital_signature: sig.trim()
      }).eq('id', user.id)
      
      toast.success('Agreement signed successfully!!', { duration: 3000 })
      setShowSignModal(false)
      navigate('/notary')
    } catch (err) {
      toast.error('Error signing agreement: ' + err.message)
    }
  }

  const rank                = getRank(notary?.total_signings || 0)
  const { next, threshold } = getNextRankThreshold(notary?.total_signings || 0)
  const pct                 = Math.min(100, Math.round(((notary?.total_signings || 0) / threshold) * 100))
  const WORK_DONE           = ['completed','scanbacks_uploaded','client_approved','payment_received','notary_paid','closed']
  const earned              = jobs.filter(j => WORK_DONE.includes(j.status)).reduce((a, j) => a + (j.notary_pay || 0), 0)
  const pending             = jobs.filter(j => WORK_DONE.includes(j.status) && !j.notary_paid_at).reduce((a, j) => a + (j.notary_pay || 0), 0)
  const upcoming            = jobs.filter(j => ['assigned','accepted','on_the_way','arrived'].includes(j.status))
  const newJobs             = jobs.filter(j => j.status === 'assigned').length

  // 🏆 Vetted celebration banner — show for 3 days from vetted_at, then gone for good.
  // Also gone for good if dismissed early via the ✕ button (see dismiss handler below).
  const vettedAt            = notary?.vetted_at ? new Date(notary.vetted_at) : null
  const daysSinceVetted     = vettedAt ? (Date.now() - vettedAt.getTime()) / (1000 * 60 * 60 * 24) : null
  const withinVettedWindow  = daysSinceVetted != null && daysSinceVetted <= 3
  const showVettedBanner    = vettedProgress?.is_vetted && vettedAt && withinVettedWindow && !vettedBannerDismissed

  return (
    <>
      <style>{NOTARY_STYLES}</style>
      <a href="#notary-dashboard" className="lca-skip">Skip to main content</a>
      
      {/* 🎉 CONFETTI CELEBRATION */}
      <ConfettiCelebration active={confettiActive} onDone={() => setConfettiActive(false)} />
      
      {/* ✅ SIGN AGREEMENT MODAL */}
      {showSignModal && signParam && (
        <SignAgreementModal 
          agreementType={signParam}
          onClose={() => { setShowSignModal(false); navigate('/notary') }}
          onSign={handleSign}
        />
      )}
      
      <main id="notary-dashboard" tabIndex={-1}>
        <h1 className="page-title">My Dashboard</h1>

        {/* ✅ CREDENTIAL UPLOAD BANNER — shows until credentials are uploaded */}
        {credCount === 0 && (
          <a href="/notary/credentials" style={{ display:'block', textDecoration:'none', marginBottom:20 }}>
            <div style={{ background:'linear-gradient(135deg,#C9A84C,#9A7A30)', borderRadius:14, padding:'20px 24px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, boxShadow:'0 8px 32px rgba(201,168,76,0.4)' }}>
              <div>
                <div style={{ fontSize:18, fontWeight:700, color:'#0D1B3E', fontFamily:'Cormorant Garamond,serif', marginBottom:4 }}>
                  📋 Action Required — Upload Your Credentials!!
                </div>
                <div style={{ fontSize:13, color:'rgba(13,27,62,0.7)', lineHeight:1.6 }}>
                  Your account is approved!! Upload your credentials to unlock all features and start receiving assignments!!
                </div>
              </div>
              <div style={{ background:'#0D1B3E', color:'#C9A84C', fontSize:13, fontWeight:700, padding:'10px 20px', borderRadius:10, whiteSpace:'nowrap', flexShrink:0 }}>
                Upload Now →
              </div>
            </div>
          </a>
        )}
        {newJobs > 0 && (
          <a href="/notary/available" style={{ display:'block', textDecoration:'none', marginBottom:20 }}>
            <div style={{ background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', borderRadius:14, padding:'20px 24px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, boxShadow:'0 8px 32px rgba(13,27,62,0.3)', animation:'lux-pop 0.4s cubic-bezier(0.175,0.885,0.32,1.275)' }}>
              <div>
                <div style={{ fontSize:18, fontWeight:700, color:'#C9A84C', fontFamily:'Cormorant Garamond,serif', marginBottom:4 }}>
                  ⚡ {newJobs} Assignment{newJobs > 1 ? 's' : ''} Waiting for Your Response!!
                </div>
                <div style={{ fontSize:13, color:'rgba(255,255,255,0.7)' }}>Tap here to Accept or Decline — client is waiting!!</div>
              </div>
              <div style={{ background:'linear-gradient(135deg,#8A5C00 0%,#C9A84C 50%,#B8941F 100%)', color:'#fff', fontSize:13, fontWeight:700, padding:'10px 20px', borderRadius:10, whiteSpace:'nowrap' }}>
                View Now →
              </div>
            </div>
          </a>
        )}

        {/* ── ⚠️ PERFORMANCE ALERT — if metrics dropping ── */}
        {notary && notary.total_offers_received >= 10 && (
          <>
            {notary.acceptance_rate != null && notary.acceptance_rate < 70 && (
              <div style={{ background:'#FEF3E2', border:'1px solid #F59E0B', borderRadius:12, padding:'14px 18px', marginBottom:14, display:'flex', alignItems:'flex-start', gap:12 }}>
                <span style={{ fontSize:24, lineHeight:1 }}>⚠️</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'#9A6800', marginBottom:4 }}>
                    Acceptance Rate Alert: {notary.acceptance_rate}%
                  </div>
                  <div style={{ fontSize:11, color:'#7A5500' }}>
                    Your acceptance rate is below 70%. Higher acceptance = More assignments + Vetted eligibility!! Consider toggling OFF "Ready to Work" if you can't accept right now.
                  </div>
                </div>
              </div>
            )}
            {notary.completion_rate != null && notary.completion_rate < 80 && notary.total_completed > 0 && (
              <div style={{ background:'#FEF3E2', border:'1px solid #F59E0B', borderRadius:12, padding:'14px 18px', marginBottom:14, display:'flex', alignItems:'flex-start', gap:12 }}>
                <span style={{ fontSize:24, lineHeight:1 }}>⚠️</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'#9A6800', marginBottom:4 }}>
                    Completion Rate: {notary.completion_rate}%
                  </div>
                  <div style={{ fontSize:11, color:'#7A5500' }}>
                    A little below our 80% goal. If you ever need support finishing a signing, your admin is just a message away — we're a team!! 💜
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── 🟢 READY TO WORK TOGGLE ── */}
        <div style={{ background:'#fff', border:'2px solid', borderColor: readyToWork ? '#2D7A4F' : '#E8E6DF', borderRadius:12, padding:'14px 18px', marginBottom:16, display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
          <button
            type="button"
            onClick={async () => {
              const next = !readyToWork
              setReadyToWork(next)
              try {
                await supabase.from('notaries').update({ ready_to_work: next }).eq('id', user.id)
                toast.success(next ? '🟢 You are now READY TO WORK!! Available for assignments.' : '🔴 You are OFFLINE. You will not receive new offers.', { duration: 4000 })
              } catch(err) {
                setReadyToWork(!next)
                toast.error('Could not update status: ' + err.message)
              }
            }}
            aria-checked={readyToWork}
            role="switch"
            style={{
              width:54, height:30, borderRadius:30,
              background: readyToWork ? '#2D7A4F' : '#B8B5AB',
              border:'none', cursor:'pointer', position:'relative', flexShrink:0,
              transition:'background 0.2s',
            }}
          >
            <div style={{
              position:'absolute', top:3, left: readyToWork ? '26px' : '3px',
              width:24, height:24, borderRadius:'50%', background:'#fff',
              transition:'left 0.2s', boxShadow:'0 2px 4px rgba(0,0,0,0.2)'
            }} />
          </button>
          <div style={{ flex:1, minWidth:200 }}>
            <div style={{ fontSize:14, fontWeight:700, color: readyToWork ? '#2D7A4F' : '#7A7870' }}>
              {readyToWork ? '🟢 Ready to Work' : '⚪ Not Available Right Now'}
            </div>
            <div style={{ fontSize:11, color:'#7A7870', marginTop:2 }}>
              {readyToWork
                ? 'You will be considered for BLAST and direct assignments'
                : 'Toggle ON when you are available for new signing assignments'}
            </div>
          </div>
        </div>

        <div className="np-rank-card" role="region" aria-label="Your rank and progress">
          <div style={{ width:64, height:64, borderRadius:'50%', background:'#C9A84C', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', flexShrink:0 }} aria-hidden="true">
            <span style={{ fontSize:22 }}>★</span>
            <span style={{ fontSize:9, color:'#0D1B3E', fontWeight:700, letterSpacing:'0.05em' }}>{rank.toUpperCase()}</span>
          </div>
          <div style={{ flex:1, minWidth:180 }}>
            <div style={{ fontSize:18, fontFamily:'Cormorant Garamond, serif', color:'#fff', marginBottom:2 }}>{profile?.full_name}</div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.5)', marginBottom:8 }}>{notary?.total_signings || 0} completed signings · {notary?.avg_rating || '—'} avg rating</div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ flex:1 }} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${pct}% progress toward ${next} rank`}>
                <div className="progress-wrap"><div className="progress-bar" style={{ width:`${pct}%` }} /></div>
              </div>
              <span style={{ fontSize:11, color:'rgba(255,255,255,0.4)', whiteSpace:'nowrap' }}>{notary?.total_signings || 0} / {threshold} → {next}</span>
            </div>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginBottom:4 }}>Rating</div>
            <div style={{ color:'#C9A84C', fontSize:20 }} aria-label={`${Math.round(notary?.avg_rating || 5)} out of 5 stars`}>{'★'.repeat(Math.round(notary?.avg_rating || 5))}</div>
          </div>
        </div>

        <div className="np-stats" role="region" aria-label="Performance statistics">
          <div className="stat-card"><div className="stat-label">This Month</div><div className="stat-value">{jobs.filter(j => ['completed','scanbacks_uploaded','client_approved','payment_received','notary_paid','closed'].includes(j.status)).length}</div><div className="stat-sub">signings</div></div>
          <div className="stat-card"><div className="stat-label">Earned</div><div className="stat-value gold">${earned.toLocaleString()}</div><div className="stat-sub">work completed</div></div>
          <div className="stat-card"><div className="stat-label">Upcoming</div><div className="stat-value">{upcoming.length}</div><div className="stat-sub">jobs</div></div>
          <div className="stat-card"><div className="stat-label">Pending Pay</div><div className="stat-value">${pending.toLocaleString()}</div></div>
        </div>

        {/* ── 📊 PERFORMANCE METRICS CARD ── */}
        {notary && (notary.total_offers_received > 0 || notary.total_signings > 0) && (
          <section aria-labelledby="perf-heading" style={{ background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', borderRadius:16, padding:'24px 28px', marginBottom:20, color:'#fff' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18, flexWrap:'wrap', gap:8 }}>
              <h2 id="perf-heading" style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, fontWeight:600, color:'#C9A84C', margin:0 }}>
                📊 My Performance Scorecard
              </h2>
              <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', fontStyle:'italic' }}>Updated in real-time</div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:14 }}>
              <div style={{ background:'rgba(201,168,76,0.08)', border:'1px solid rgba(201,168,76,0.2)', borderRadius:10, padding:'14px 12px', textAlign:'center' }}>
                <div style={{ fontSize:10, color:'rgba(255,255,255,0.5)', letterSpacing:'0.08em', marginBottom:4, fontWeight:600 }}>ACCEPTANCE RATE</div>
                <div style={{ fontSize:24, color:'#C9A84C', fontFamily:'Cormorant Garamond,serif', fontWeight:700, lineHeight:1 }}>
                  {notary.acceptance_rate ? `${notary.acceptance_rate}%` : '—'}
                </div>
                <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', marginTop:4 }}>
                  {notary.total_offers_accepted || 0}/{notary.total_offers_received || 0} offers
                </div>
              </div>
              <div style={{ background:'rgba(201,168,76,0.08)', border:'1px solid rgba(201,168,76,0.2)', borderRadius:10, padding:'14px 12px', textAlign:'center' }}>
                <div style={{ fontSize:10, color:'rgba(255,255,255,0.5)', letterSpacing:'0.08em', marginBottom:4, fontWeight:600 }}>COMPLETION RATE</div>
                <div style={{ fontSize:24, color:'#C9A84C', fontFamily:'Cormorant Garamond,serif', fontWeight:700, lineHeight:1 }}>
                  {notary.completion_rate ? `${notary.completion_rate}%` : '—'}
                </div>
                <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', marginTop:4 }}>
                  {notary.total_completed || 0} completed
                </div>
              </div>
              <div style={{ background:'rgba(201,168,76,0.08)', border:'1px solid rgba(201,168,76,0.2)', borderRadius:10, padding:'14px 12px', textAlign:'center' }}>
                <div style={{ fontSize:10, color:'rgba(255,255,255,0.5)', letterSpacing:'0.08em', marginBottom:4, fontWeight:600 }}>AVG RESPONSE</div>
                <div style={{ fontSize:24, color:'#C9A84C', fontFamily:'Cormorant Garamond,serif', fontWeight:700, lineHeight:1 }}>
                  {notary.avg_response_minutes != null
                    ? notary.avg_response_minutes < 60
                      ? `${notary.avg_response_minutes}m`
                      : `${Math.round(notary.avg_response_minutes / 60 * 10) / 10}h`
                    : '—'}
                </div>
                <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', marginTop:4 }}>to accept</div>
              </div>
              <div style={{ background:'rgba(201,168,76,0.08)', border:'1px solid rgba(201,168,76,0.2)', borderRadius:10, padding:'14px 12px', textAlign:'center' }}>
                <div style={{ fontSize:10, color:'rgba(255,255,255,0.5)', letterSpacing:'0.08em', marginBottom:4, fontWeight:600 }}>AVG RATING</div>
                <div style={{ fontSize:24, color:'#C9A84C', fontFamily:'Cormorant Garamond,serif', fontWeight:700, lineHeight:1 }}>
                  {notary.avg_rating ? `${notary.avg_rating} ★` : '—'}
                </div>
                <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', marginTop:4 }}>client reviews</div>
              </div>
              <div style={{ background:'rgba(201,168,76,0.08)', border:'1px solid rgba(201,168,76,0.2)', borderRadius:10, padding:'14px 12px', textAlign:'center' }}>
                <div style={{ fontSize:10, color:'rgba(255,255,255,0.5)', letterSpacing:'0.08em', marginBottom:4, fontWeight:600 }}>TOTAL SIGNINGS</div>
                <div style={{ fontSize:24, color:'#C9A84C', fontFamily:'Cormorant Garamond,serif', fontWeight:700, lineHeight:1 }}>
                  {notary.total_signings || 0}
                </div>
                <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', marginTop:4 }}>career</div>
              </div>
            </div>
            <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', marginTop:14, textAlign:'center', fontStyle:'italic' }}>
              💡 Higher acceptance + faster response = More LCA assignments & Vetted status!!
            </div>
          </section>
        )}

        {/* ── 🏆 VETTED STATUS PROGRESS CARD ── */}
        {vettedProgress && !vettedProgress.is_vetted && (
          <section aria-labelledby="vetted-heading" style={{ background:'linear-gradient(135deg,#8A5C00,#C9A84C)', borderRadius:16, padding:'24px 28px', marginBottom:20, color:'#0D1B3E' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:8 }}>
              <h2 id="vetted-heading" style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, fontWeight:700, color:'#0D1B3E', margin:0 }}>
                🏆 Earn Your Vetted Badge!!
              </h2>
              <div style={{ background:'#0D1B3E', color:'#C9A84C', padding:'6px 12px', borderRadius:20, fontSize:11, fontWeight:700, letterSpacing:'0.06em' }}>
                AUTO-AWARDED
              </div>
            </div>
            <div style={{ fontSize:12, color:'rgba(13,27,62,0.75)', marginBottom:18, lineHeight:1.5 }}>
              The <strong>Vetted</strong> badge is LCA's mark of excellence — earned by notaries who consistently deliver world-class service. Meet ALL the criteria below and the badge is yours automatically!!
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:10 }}>
              {Object.entries(vettedProgress.criteria).map(([key, c]) => {
                const labels = {
                  acceptance:  { label: 'Acceptance Rate',  fmt: (v) => `${v}%`, unit: '%' },
                  completion:  { label: 'Completion Rate',  fmt: (v) => `${v}%`, unit: '%' },
                  response:    { label: 'Avg Response Time', fmt: (v) => v != null ? `${v} min` : '—', unit: 'min or less' },
                  rating:      { label: 'Avg Client Rating', fmt: (v) => `${v} ★`, unit: '★' },
                  signings:    { label: 'Total Signings',    fmt: (v) => `${v}`, unit: 'completed' },
                  credentials: { label: 'Credentials',       fmt: (v) => v > 0 ? '✓ Uploaded' : 'None', unit: 'uploaded' },
                }
                const meta = labels[key] || { label: key, fmt: v => String(v), unit: '' }
                const isResponse = key === 'response'
                const currentDisplay = meta.fmt(c.current)
                const requiredDisplay = isResponse ? `≤ ${c.required} min` : key === 'credentials' ? '≥ 1' : `≥ ${c.required}${meta.unit === '★' ? ' ★' : meta.unit === '%' ? '%' : ''}`
                return (
                  <div key={key} style={{
                    background: c.met ? '#0D1B3E' : 'rgba(13,27,62,0.1)',
                    border: c.met ? '2px solid #2D7A4F' : '2px solid rgba(13,27,62,0.15)',
                    borderRadius: 10,
                    padding: '12px 14px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                  }}>
                    <div>
                      <div style={{ fontSize:10, fontWeight:700, color: c.met ? '#C9A84C' : 'rgba(13,27,62,0.6)', letterSpacing:'0.06em', marginBottom:3 }}>
                        {meta.label.toUpperCase()}
                      </div>
                      <div style={{ fontSize:14, fontWeight:600, color: c.met ? '#fff' : '#0D1B3E' }}>
                        {currentDisplay} <span style={{ fontSize:10, color: c.met ? 'rgba(255,255,255,0.5)' : 'rgba(13,27,62,0.5)', fontWeight:400 }}>/ {requiredDisplay}</span>
                      </div>
                    </div>
                    <div style={{ fontSize:20, flexShrink:0 }}>
                      {c.met ? '✅' : '⏳'}
                    </div>
                  </div>
                )
              })}
            </div>
            <div style={{ fontSize:11, color:'rgba(13,27,62,0.7)', marginTop:14, textAlign:'center', fontStyle:'italic' }}>
              🎯 Once ALL criteria are met, your Vetted badge is awarded automatically!!
            </div>
          </section>
        )}

       {/* ── 🏆 VETTED CONFIRMED CARD — celebration shown for 3 days after vetted_at, then gone for good (or dismissed early via ✕) ── */}
        {showVettedBanner && (
          <section style={{ position:'relative', background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', borderRadius:16, padding:'20px 28px', marginBottom:20, color:'#fff', textAlign:'center', border:'2px solid #C9A84C' }}>
            <button
              onClick={() => {
                if (notary?.vetted_at) {
                  localStorage.setItem(`vetted_banner_dismissed_${notary.vetted_at}`, 'true')
                }
                setVettedBannerDismissed(true)
              }}
              aria-label="Dismiss Vetted celebration"
              style={{ position:'absolute', top:10, right:10, background:'rgba(255,255,255,0.1)', border:'none', color:'#C9A84C', width:28, height:28, borderRadius:'50%', cursor:'pointer', fontSize:14, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', transition:'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            >✕</button>
            <div style={{ fontSize:32, marginBottom:6 }}><span className="lca-celebration-sparkle">🏆</span></div>
            <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, fontWeight:700, color:'#C9A84C', marginBottom:4 }}>
              You Are VETTED!!
            </div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.7)' }}>
              Welcome to LCA's elite tier of trusted notaries — Title Companies see your VETTED status when reviewing assignments!!
            </div>
          </section>
        )}

        {/* ── 📧 ABANDONED COURSE NUDGE — persists until course is finished, no dismiss button ── */}
        {abandonedTrack && (
          <a href={`/notary/academy?track=${abandonedTrack.id}`} style={{ display:'block', textDecoration:'none', marginBottom:20 }}>
            <div style={{ background:'linear-gradient(135deg,#C9A84C,#9A7A30)', borderRadius:14, padding:'20px 24px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, boxShadow:'0 8px 32px rgba(201,168,76,0.4)' }}>
              <div style={{ flex:1, minWidth:200 }}>
                <div style={{ fontSize:18, fontWeight:700, color:'#0D1B3E', fontFamily:'Cormorant Garamond,serif', marginBottom:4 }}>
                  📚 Pick Up Where You Left Off!!
                </div>
                <div style={{ fontSize:13, color:'rgba(13,27,62,0.75)', lineHeight:1.6 }}>
                  You started <strong>{abandonedTrack.title}</strong> — come back and finish it to earn your Certificate of Proficiency!!
                </div>
              </div>
              <div style={{ background:'#0D1B3E', color:'#C9A84C', fontSize:13, fontWeight:700, padding:'10px 20px', borderRadius:10, whiteSpace:'nowrap', flexShrink:0 }}>
                Resume Course →
              </div>
            </div>
          </a>
        )}

        <section aria-labelledby="upcoming-heading" className="section-card">
          <div className="section-header">
            <h2 id="upcoming-heading" className="section-title">Upcoming Assignments</h2>
            <a href="/notary/available" className="btn btn-primary btn-sm">View Available →</a>
          </div>
          <div className="np-table-wrap">
            <table className="data-table">
              <thead><tr><th>Job #</th><th>Type</th><th>Address</th><th>Date/Time</th><th>Pay</th><th>Status</th></tr></thead>
              <tbody>
                {upcoming.length === 0 && <tr><td colSpan={6} style={{ textAlign:'center', color:'#B8B5AB', padding:24 }}>No upcoming assignments</td></tr>}
                {upcoming.map(j => (
                  <tr key={j.id}>
                    <td style={{ fontWeight:500 }}>{j.job_number}</td>
                    <td>{j.signing_type}</td>
                    <td>{j.signing_address}</td>
                    <td>{j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : '—'}</td>
                    <td style={{ color:'#2D7A4F', fontWeight:500 }}>${j.notary_pay}</td>
                    <td><span className={`badge badge-${j.status}`}>{statusLabel(j.status)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </>
  )
}

/* ── AVAILABLE JOBS ── */
// ── P&L ESTIMATOR COMPONENT ──────────────────────────────────
function PLEstimator({ job }) {
  const [miles, setMiles] = useState('')
  const [pages, setPages] = useState('')
  const [hours, setHours] = useState('')
  const [open,  setOpen]  = useState(false)

  const IRS_RATE   = 0.67
  const PRINT_RATE = 0.05
  const TAX_RATE   = 0.30

  const fee         = job.notary_pay || 0
  const mileageCost = miles ? Math.round(parseFloat(miles) * IRS_RATE * 100) / 100 : 0
  const printCost   = pages ? Math.round(parseInt(pages) * PRINT_RATE * 100) / 100 : 0
  const totalCost   = Math.round((mileageCost + printCost) * 100) / 100
  const preTax      = Math.round((fee - totalCost) * 100) / 100
  const tax         = Math.round(preTax * TAX_RATE * 100) / 100
  const takeHome    = Math.round((preTax - tax) * 100) / 100
  const hourly      = hours && parseFloat(hours) > 0 ? Math.round((takeHome / parseFloat(hours)) * 100) / 100 : 0

  const isGood = takeHome >= 50
  const isOk   = takeHome >= 25 && takeHome < 50
  const isPoor = takeHome < 25 && (miles || pages)

  const PL_EST_STYLES = `
    .pl-est-wrap { background:#F8F7F4; border-top:1px solid #E8E6DF; }
    .pl-est-toggle { width:100%; padding:10px 20px; background:none; border:none; cursor:pointer; display:flex; align-items:center; justify-content:space-between; font-size:12px; font-weight:600; color:#0D1B3E; }
    .pl-est-toggle:hover { background:#F0EEE8; }
    .pl-est-body { padding:16px 20px; border-top:1px solid #E8E6DF; }
    .pl-est-inputs { display:grid; grid-template-columns:1fr 1fr 1fr; gap:10px; margin-bottom:14px; }
    @media(max-width:400px){ .pl-est-inputs { grid-template-columns:1fr; } }
    .pl-est-input-group label { font-size:10px; font-weight:600; color:#7A7870; letter-spacing:0.06em; display:block; margin-bottom:4px; }
    .pl-est-input-group input { width:100%; padding:7px 10px; border:1px solid #E8E6DF; border-radius:8px; font-size:13px; }
    .pl-est-result { background:#0D1B3E; border-radius:12px; padding:16px; }
    .pl-est-row { display:flex; justify-content:space-between; font-size:12px; color:rgba(255,255,255,0.6); padding:3px 0; }
    .pl-est-divider { height:1px; background:rgba(201,168,76,0.3); margin:8px 0; }
    .pl-est-total { display:flex; justify-content:space-between; align-items:center; }
    .pl-est-total-label { font-size:13px; color:#fff; font-weight:500; }
    .pl-est-total-amount { font-size:22px; font-family:'Cormorant Garamond',serif; font-weight:700; }
    .pl-est-verdict { margin-top:10px; padding:8px 12px; border-radius:8px; font-size:11px; font-weight:500; text-align:center; }
    .pl-est-disclaimer { background:rgba(201,168,76,0.1); border:1px solid rgba(201,168,76,0.3); border-radius:8px; padding:10px 12px; font-size:11px; color:rgba(255,255,255,0.7); line-height:1.6; margin-top:12px; }
    .pl-est-pay-banner { background:#EAF5EE; border:1px solid rgba(45,122,79,0.25); border-radius:10px; padding:12px 14px; margin-bottom:14px; display:flex; align-items:center; gap:10px; }
  `

  return (
    <>
      <style>{PL_EST_STYLES}</style>
      <div className="pl-est-wrap">
        <button className="pl-est-toggle" onClick={() => setOpen(o => !o)} type="button">
          <span>📊 Estimate My P&L — Is this job worth it??</span>
          <span style={{ color:'#B8B5AB' }}>{open ? '▲' : '▼'}</span>
        </button>
        {open && (
          <div className="pl-est-body">

            {/* ✅ JOB PAY BANNER — crystal clear what they ACTUALLY get paid!! */}
            <div className="pl-est-pay-banner">
              <div style={{ fontSize:28 }}>💵</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:11, fontWeight:600, color:'#2D7A4F', marginBottom:2 }}>YOUR JOB PAY — What LCA pays you!!</div>
                <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:26, fontWeight:700, color:'#0D1B3E', lineHeight:1 }}>${fee}</div>
                <div style={{ fontSize:10, color:'#4A7A5C', marginTop:3 }}>This is the amount LCA will pay you upon completion of this signing — regardless of your expenses!!</div>
              </div>
            </div>

            <div style={{ fontSize:11, fontWeight:600, color:'#7A7870', marginBottom:10, letterSpacing:'0.06em' }}>ESTIMATE YOUR PERSONAL EXPENSES & TAX RESERVE</div>

            <div className="pl-est-inputs">
              <div className="pl-est-input-group">
                <label>ROUND TRIP MILES</label>
                <input type="number" placeholder="e.g. 30" value={miles} onChange={e => setMiles(e.target.value)} min="0" />
              </div>
              <div className="pl-est-input-group">
                <label>PAGES TO PRINT</label>
                <input type="number" placeholder="e.g. 150" value={pages} onChange={e => setPages(e.target.value)} min="0" />
              </div>
              <div className="pl-est-input-group">
                <label>EST. HOURS</label>
                <input type="number" placeholder="e.g. 2" value={hours} onChange={e => setHours(e.target.value)} min="0" step="0.5" />
              </div>
            </div>

            <div className="pl-est-result">
              <div className="pl-est-row">
                <span>Job pay from LCA</span>
                <span style={{ color:'#2D7A4F', fontWeight:600 }}>${fee}</span>
              </div>
              {mileageCost > 0 && (
                <div className="pl-est-row">
                  <span>Est. mileage cost ({miles} mi × $0.67 IRS rate)</span>
                  <span style={{ color:'#E24B4A' }}>−${mileageCost}</span>
                </div>
              )}
              {printCost > 0 && (
                <div className="pl-est-row">
                  <span>Est. print cost ({pages} pages × $0.05)</span>
                  <span style={{ color:'#E24B4A' }}>−${printCost}</span>
                </div>
              )}
              {totalCost > 0 && (
                <>
                  <div className="pl-est-divider" />
                  <div className="pl-est-row">
                    <span>Pre-tax profit</span>
                    <span>${preTax}</span>
                  </div>
                  <div className="pl-est-row">
                    <span>Suggested tax reserve (30%)</span>
                    <span style={{ color:'#C9A84C' }}>−${tax}</span>
                  </div>
                </>
              )}
              <div className="pl-est-divider" />
              <div className="pl-est-total">
                <span className="pl-est-total-label">Est. Take-Home After Expenses</span>
                <span className="pl-est-total-amount" style={{ color: isGood ? '#5BA882' : isOk ? '#C9A84C' : isPoor ? '#FCA5A5' : '#C9A84C' }}>
                  ${takeHome > 0 ? takeHome : fee}
                </span>
              </div>
              {hourly > 0 && (
                <div className="pl-est-row" style={{ marginTop:6 }}>
                  <span>Estimated hourly rate</span>
                  <span style={{ color:'#C9A84C', fontWeight:600 }}>${hourly}/hr</span>
                </div>
              )}
              {(miles || pages) && (
                <div className="pl-est-verdict" style={{
                  background: isGood ? 'rgba(45,122,79,0.2)' : isOk ? 'rgba(201,168,76,0.2)' : 'rgba(226,75,74,0.2)',
                  color:      isGood ? '#5BA882' : isOk ? '#C9A84C' : '#FCA5A5',
                }}>
                  {isGood ? '✅ Great assignment — worth accepting!!' : isOk ? '⚠️ Acceptable — your call!!' : '❌ Low profit after expenses — consider declining'}
                </div>
              )}
              {!miles && !pages && (
                <div style={{ fontSize:11, color:'rgba(255,255,255,0.3)', textAlign:'center', marginTop:8 }}>
                  Enter your estimated miles and pages above to calculate your expenses!!
                </div>
              )}

              {/* ✅ DISCLAIMER — Nancy's brilliant idea!! */}
              <div className="pl-est-disclaimer">
                <strong style={{ color:'#C9A84C' }}>⚠️ Important:</strong> LCA will pay you <strong style={{ color:'#fff' }}>${fee}</strong> upon signing completion — that is your guaranteed job pay!! The "Est. Take-Home" above is a personal planning tool only and varies based on: (1) actual miles driven, (2) actual pages printed, (3) your personal tax situation and filing status. The 30% tax reserve is a suggested guideline — consult a tax professional for advice specific to your situation. This estimate does not represent your LCA payment amount!!
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

// 📅 Self-contained schedule calendar for the notary's Available Jobs tab
function NotaryCalendar({ jobs }) {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDay, setSelectedDay] = useState(null)
  const year  = currentDate.getFullYear()
  const month = currentDate.getMonth()
  const firstDay    = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const today       = new Date()
  const monthName   = currentDate.toLocaleString('en-US', { month:'long', year:'numeric' })

  const isClaimable = j => j.status === 'assigned' || j.blast_active

  const jobsByDay = {}
  jobs.forEach(j => {
    if (!j.scheduled_at) return
    const d = new Date(j.scheduled_at)
    if (d.getFullYear() === year && d.getMonth() === month) {
      const day = d.getDate()
      ;(jobsByDay[day] = jobsByDay[day] || []).push(j)
    }
  })

  const cells = []
  for (let i = 0; i < firstDay; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)

  const selectedJobs = selectedDay ? (jobsByDay[selectedDay] || []) : []
  const gridStyle = { display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:6 }

  return (
    <div style={{ background:'#fff', border:'1px solid #ECEAE3', borderRadius:16, padding:'18px 18px 20px', marginBottom:24 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <button onClick={() => { setCurrentDate(new Date(year, month - 1, 1)); setSelectedDay(null) }} style={{ background:'none', border:'1px solid #E8E6DF', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:15, color:'#0D1B3E' }} aria-label="Previous month">←</button>
        <span style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, fontWeight:700, color:'#0D1B3E' }}>{monthName}</span>
        <button onClick={() => { setCurrentDate(new Date(year, month + 1, 1)); setSelectedDay(null) }} style={{ background:'none', border:'1px solid #E8E6DF', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:15, color:'#0D1B3E' }} aria-label="Next month">→</button>
      </div>
      <div style={{ ...gridStyle, marginBottom:6 }}>
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
          <div key={d} style={{ textAlign:'center', fontSize:10, fontWeight:700, color:'#B8B5AB', letterSpacing:'0.06em' }}>{d}</div>
        ))}
      </div>
      <div style={gridStyle}>
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />
          const isToday  = day === today.getDate() && month === today.getMonth() && year === today.getFullYear()
          const dayJobs  = jobsByDay[day] || []
          const has      = dayJobs.length > 0
          const hasClaim = dayJobs.some(isClaimable)
          const isSel    = selectedDay === day
          return (
            <button key={day} onClick={() => has && setSelectedDay(isSel ? null : day)} disabled={!has}
              style={{
                minHeight:54, borderRadius:10, padding:'5px 4px', textAlign:'left', cursor: has ? 'pointer' : 'default',
                background: isSel ? '#0D1B3E' : has ? (hasClaim ? '#FFF8E8' : '#F0FAF5') : '#FAFAF8',
                border: isToday ? '2px solid #C9A84C' : '1px solid #EFEDE7',
                color: isSel ? '#fff' : '#0D1B3E'
              }}>
              <div style={{ fontWeight:isToday?700:500, fontSize:12 }}>{day}</div>
              {dayJobs.slice(0,2).map((j, idx) => (
                <div key={idx} title={j.borrower_name} style={{ fontSize:8, marginTop:2, padding:'1px 4px', borderRadius:4, background: isClaimable(j) ? '#C9A84C' : '#2D7A4F', color:'#fff', overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>
                  {j.borrower_name || 'Signing'}
                </div>
              ))}
              {dayJobs.length > 2 && <div style={{ fontSize:8, marginTop:1, color: isSel ? '#C9A84C' : '#B8B5AB' }}>+{dayJobs.length - 2} more</div>}
            </button>
          )
        })}
      </div>
      <div style={{ display:'flex', gap:16, marginTop:14, fontSize:10, color:'#7A7870' }}>
        <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:10, height:10, borderRadius:3, background:'#C9A84C', display:'inline-block' }} /> Available to claim</span>
        <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:10, height:10, borderRadius:3, background:'#2D7A4F', display:'inline-block' }} /> Your signings</span>
      </div>
      {selectedJobs.length > 0 && (
        <div style={{ marginTop:16, display:'flex', flexDirection:'column', gap:10 }}>
          {selectedJobs.map(j => (
            <div key={j.id} style={{ padding:14, background:'#0D1B3E', borderRadius:12 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, flexWrap:'wrap', gap:6 }}>
                <span style={{ fontFamily:'Cormorant Garamond,serif', fontSize:16, color:'#C9A84C', fontWeight:700 }}>{j.signing_type}</span>
                <span style={{ fontSize:10, fontWeight:700, padding:'3px 9px', borderRadius:20, background: isClaimable(j) ? 'rgba(201,168,76,0.2)' : 'rgba(45,122,79,0.25)', color: isClaimable(j) ? '#C9A84C' : '#7FD4A3' }}>{isClaimable(j) ? 'Available to claim' : statusLabel(j.status)}</span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, fontSize:12, color:'rgba(255,255,255,0.75)' }}>
                <div><span style={{ color:'rgba(255,255,255,0.4)', fontSize:9 }}>JOB #</span><br/><span style={{ color:'#C9A84C', fontWeight:600 }}>{j.job_number}</span></div>
                <div><span style={{ color:'rgba(255,255,255,0.4)', fontSize:9 }}>BORROWER</span><br/>{j.borrower_name || '—'}</div>
                <div><span style={{ color:'rgba(255,255,255,0.4)', fontSize:9 }}>WHEN</span><br/>{j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : 'TBD'}</div>
                <div><span style={{ color:'rgba(255,255,255,0.4)', fontSize:9 }}>WHERE</span><br/>{j.signing_city}, {j.signing_state}</div>
              </div>
              <a href={`/notary/jobs/${j.id}`} style={{ display:'inline-block', marginTop:12, background:'#C9A84C', color:'#0D1B3E', fontWeight:700, fontSize:12, padding:'8px 16px', borderRadius:8, textDecoration:'none' }}>View Details →</a>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function AvailableJobs() {
  const { user, profile } = useAuth()
  const [jobs,    setJobs]    = useState([])
  const [loading, setLoading] = useState(true)
  const [acting,  setActing]  = useState(null)
  const [myUpcoming, setMyUpcoming] = useState([])

  useEffect(() => {
    if (!user) return
    loadJobs()
    // Real-time — new jobs assigned to this notary
    const ch = supabase.channel('notary-available-jobs-realtime')
      .on('postgres_changes', {
        event:  '*',
        schema: 'public',
        table:  'jobs',
        filter: `notary_id=eq.${user.id}`,
      }, payload => {
        if (payload.eventType === 'INSERT') {
          setJobs(prev => [payload.new, ...prev])
          toast.success('🔔 New job available!! Check your queue!!', { duration:6000 })
        } else if (payload.eventType === 'UPDATE') {
          setJobs(prev => prev.map(j => j.id === payload.new.id ? { ...j, ...payload.new } : j))
        } else if (payload.eventType === 'DELETE') {
          setJobs(prev => prev.filter(j => j.id !== payload.old.id))
        }
      })
      .subscribe()
    return () => supabase.removeChannel(ch)
  }, [user])

  async function loadJobs() {
    // 1. Jobs directly assigned to this notary (awaiting their acceptance)
    const { data: assignedJobs, error: assignedErr } = await supabase.from('jobs')
      .select('*').eq('notary_id', user.id).eq('status', 'assigned')
      .order('created_at', { ascending: false })
    if (assignedErr) console.error('loadJobs (assigned) error:', assignedErr)

    // 2. Blast jobs where this notary is a recipient (pending outcome)
    const { data: blastRecipients, error: blastErr } = await supabase.from('job_blast_recipients')
      .select('job_id').eq('notary_id', user.id).eq('outcome', 'pending')
    if (blastErr) console.error('loadJobs (blast recipients) error:', blastErr)

    let blastJobs = []
    if (blastRecipients && blastRecipients.length > 0) {
      const jobIds = blastRecipients.map(r => r.job_id)
      const { data: blastData } = await supabase.from('jobs')
        .select('*').in('id', jobIds).eq('blast_active', true)
        .order('created_at', { ascending: false })
      blastJobs = blastData || []
    }

    // Merge + dedupe (assigned jobs take priority)
    const allJobs = [...(assignedJobs || []), ...blastJobs]
    const uniqueJobs = Array.from(new Map(allJobs.map(j => [j.id, j])).values())
    setJobs(safeParseJobs(uniqueJobs))

    // 3. Notary's committed signings — shown on the calendar overview (not as claim cards)
    const { data: committed } = await supabase.from('jobs')
      .select('*').eq('notary_id', user.id)
      .in('status', ['accepted','on_the_way','arrived','completed','scanbacks_uploaded'])
      .order('scheduled_at', { ascending: true })
    setMyUpcoming(safeParseJobs(committed || []))
    setLoading(false)
  }

  async function acceptJob(job) {
    setActing(job.id)
    try {
      // 🏃 BLAST race-condition handling
      if (job.blast_active) {
        // Atomic check: is the blast still open?
        const { data: currentJob } = await supabase.from('jobs')
          .select('blast_active, blast_winner_id, notary_id, status')
          .eq('id', job.id).single()

        if (!currentJob?.blast_active || currentJob?.blast_winner_id) {
          // Another notary already won!
          toast.error(`⏱️ Sorry — this job was just claimed by another notary!! Better luck next time!!`, { duration: 6000 })
          // Mark this notary's outcome as 'lost'
          try {
            await supabase.from('job_blast_recipients').update({
              outcome: 'lost',
              responded_at: new Date().toISOString(),
            }).eq('job_id', job.id).eq('notary_id', user.id)
          } catch(e) { console.error('Mark lost error:', e) }
          loadJobs()
          return
        }

        // 🏆 First to accept WINS!
        await supabase.from('jobs').update({
          notary_id: user.id,
          status: 'accepted',
          blast_active: false,
          blast_winner_id: user.id,
        }).eq('id', job.id)

        // Mark winner's outcome
        await supabase.from('job_blast_recipients').update({
          outcome: 'won',
          responded_at: new Date().toISOString(),
        }).eq('job_id', job.id).eq('notary_id', user.id)

        // Mark all other recipients as 'lost'
        await supabase.from('job_blast_recipients').update({
          outcome: 'lost',
        }).eq('job_id', job.id).neq('notary_id', user.id).eq('outcome', 'pending')

        // Log status event
        try {
          await supabase.from('job_status_events').insert({
            job_id: job.id,
            status: 'accepted',
            from_status: 'awaiting_assignment',
            note: `🏆 BLAST WON by ${profile?.full_name || 'notary'}`,
            created_by_role: 'notary',
          })
        } catch(e) { console.error('Status event error:', e) }
      } else {
        // Normal accept flow (non-blast)
        await supabase.from('jobs').update({ status: 'accepted' }).eq('id', job.id)
      }

      // 📊 Track acceptance metrics
      try { await trackAccept(user.id, job.id) } catch(e) { console.error('Metrics error:', e) }

      if (job.borrower_email) {
        await sendStatusUpdate({
          client_name:   job.borrower_name,
          client_email:  job.borrower_email,
          job_number:    job.job_number,
          service_name:  job.signing_type,
          new_status:    'accepted',
          notary_name:   profile?.full_name  || null,
          notary_phone:  profile?.phone      || null,
          notary_email:  user?.email         || null,
          appointment:   job.scheduled_at ? new Date(job.scheduled_at).toLocaleString() : null,
        })
      }
      // ✅ Also SMS client!!
      if (job.borrower_phone) {
        await smsNotaryAssigned({
          client_phone:  job.borrower_phone,
          notary_name:   profile?.full_name || 'Your LCA Notary',
          notary_phone:  profile?.phone     || null,
          job_number:    job.job_number,
          appointment:   job.scheduled_at ? new Date(job.scheduled_at).toLocaleString() : null,
        })
      }
      toast.success(`✅ Job ${job.job_number} accepted!! The client has been notified!!`)
      loadJobs()
    } catch (err) { toast.error(err.message) }
    finally { setActing(null) }
  }

  async function declineJob(job) {
    setActing(job.id)
    try {
      await supabase.from('jobs').update({ status: 'awaiting_assignment', notary_id: null }).eq('id', job.id)
      await sendAdminAlert({
        client_name:   'DECLINED by notary — needs reassignment',
        client_phone:  'N/A',
        client_email:  'nancy@loanclosingagent.com',
        service_name:  job.signing_type,
        amount:        job.total_fee,
        address:       `${job.signing_address || ''}, ${job.signing_city || ''}, ${job.signing_state || ''}`.trim(),
        appointment:   job.scheduled_at ? new Date(job.scheduled_at).toLocaleString() : null,
        is_veteran:    job.is_veteran,
        special_notes: `Job ${job.job_number} was DECLINED by the assigned notary. Please log in to reassign immediately.`,
      })
      // 🤝 Soft reassurance to customer (no alarm, just transparency)
      if (job.borrower_email) {
        try {
          await sendStatusUpdate({
            client_name:  job.borrower_name,
            client_email: job.borrower_email,
            job_number:   job.job_number,
            service_name: job.signing_type,
            status:       'Finding another notary for you',
            message:      'Our team is reassigning your signing to ensure the best match. You will be notified as soon as a new notary is confirmed. Thank you for your patience!!'
          })
        } catch(emailErr) {
          console.error('Customer reassurance email error:', emailErr)
        }
      }
      // ✏️ Log status event for audit trail
      try {
        await supabase.from('job_status_events').insert({
          job_id: job.id,
          status: 'declined',
          from_status: job.status,
          note: '🔄 Order declined by notary — returned to assignment pool',
          created_by_role: 'notary'
        })
      } catch(evtErr) {
        console.error('Status event error:', evtErr)
      }
      // 📊 Track decline metrics
      try { await trackDecline(user.id, job.id) } catch(e) { console.error('Metrics error:', e) }
      toast.success(`Job ${job.job_number} declined — Our Team has been notified to reassign.`)
      loadJobs()
    } catch (err) { toast.error(err.message) }
    finally { setActing(null) }
  }

  return (
    <>
      <style>{NOTARY_STYLES}</style>
      <a href="#available-main" className="lca-skip">Skip to available jobs</a>
      <main id="available-main" tabIndex={-1}>
        <h1 className="page-title">Available Jobs</h1>
        <p className="page-sub">These signings have been assigned to you — check the P&L estimate before you accept!!</p>

        {/* 📅 Schedule at a glance — available + your committed signings */}
        <div style={{ fontSize:12, fontWeight:700, color:'#0D1B3E', letterSpacing:'0.06em', textTransform:'uppercase', margin:'0 0 12px' }}>📅 Your Schedule at a Glance</div>
        <NotaryCalendar jobs={Array.from(new Map([...jobs, ...myUpcoming].map(j => [j.id, j])).values())} />
        {loading && <div style={{ textAlign:'center', padding:48 }} role="status" aria-label="Loading..."><div className="spinner" style={{ margin:'auto' }} /></div>}
        {!loading && jobs.length === 0 && (
          <div className="np-empty">
            <div className="np-empty-icon">🎉</div>
            <div className="np-empty-text">No pending assignments right now!</div>
            <div style={{ fontSize:12, color:'#B8B5AB' }}>Check back soon — Our Team will assign new jobs as they come in.</div>
          </div>
        )}
        {jobs.map(j => (
          <div key={j.id} className="np-job-card assigned">
            <div className="np-job-header">
              <div>
                <div className="np-job-number">{j.job_number}</div>
                <div className="np-job-type">{j.signing_type}</div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span className="badge badge-assigned">New Assignment</span>{j.job_type === 'individual' && <span style={{ background:'#F0E8FF', color:'#6A3A9A', fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:20, marginLeft:4 }}>👤 Individual</span>}
                <div className="np-job-pay">${j.notary_pay}</div>
              </div>
            </div>
            <div className="np-job-body">
              <div><div className="np-job-field-label">BORROWER</div><div className="np-job-field-value">{j.borrower_name || '—'}</div></div>
              <div>
                <div className="np-job-field-label">PHONE</div>
                <div className="np-job-field-value">
                  {j.borrower_phone ? <a href={`tel:${j.borrower_phone}`} style={{ color:'#0D1B3E', textDecoration:'none' }}>{j.borrower_phone}</a> : '—'}
                </div>
              </div>
              <div><div className="np-job-field-label">LOCATION</div><div className="np-job-field-value">{j.signing_address}<br />{j.signing_city}, {j.signing_state} {j.signing_zip}</div></div>
              <div><div className="np-job-field-label">DATE & TIME</div><div className="np-job-field-value">{j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : 'Flexible — contact borrower to schedule'}</div></div>
            </div>
            {j.special_instructions && <div className="np-job-notes"><strong>📋 Special instructions:</strong> {j.special_instructions}</div>}
            {j.order_notes && <div className="np-job-notes" style={{ background:'#FDF6E3', borderColor:'rgba(201,168,76,0.3)' }}><strong>💬 Client notes:</strong> {j.order_notes}</div>}
            {j.office_prefs && Object.keys(j.office_prefs).some(k => k !== 'custom_note' && j.office_prefs[k] === true) && (
              <div className="np-job-notes" style={{ background:'#EBF4FF', borderColor:'rgba(24,95,165,0.2)', color:'#1A4A6B' }}>
                <strong>🏛️ Office Requirements:</strong>{' '}
                {[
                  j.office_prefs.blue_ink    && '🖊️ Blue Ink Only',
                  j.office_prefs.no_staples  && '📎 No Staples',
                  j.office_prefs.gps_checkin && '📍 GPS Check-In Required',
                  j.office_prefs.borrower_id && '🪪 Upload Borrower ID',
                  j.office_prefs.call_before && '📞 Call 30 Min Before',
                  j.office_prefs.scan_backs  && '📄 Scan-Backs Required',
                ].filter(Boolean).join(' · ')}
                {j.office_prefs.custom_note && <div style={{ marginTop:4, fontStyle:'italic' }}>{j.office_prefs.custom_note}</div>}
              </div>
            )}
            <div style={{ padding:'10px 20px', borderTop:'1px solid #E8E6DF', background:'#F8F7F4' }}>
              <MapsButton address={j.signing_address} city={j.signing_city} state={j.signing_state} zip={j.signing_zip} />
            </div>

            {/* ✅ P&L ESTIMATOR — before accept/decline!! */}
            <PLEstimator job={j} />

            <div className="np-job-actions" style={{ padding:'16px 20px', gap:12 }}>
              <button className="btn btn-primary" style={{ flex:2, minHeight:52, fontSize:15, fontWeight:600, letterSpacing:'0.04em', borderRadius:12 }} onClick={() => acceptJob(j)} disabled={acting === j.id} aria-label={`Accept job ${j.job_number}`}>
                {acting === j.id ? 'Processing...' : '✓ Accept This Job'}
              </button>
              <button className="btn btn-outline" style={{ flex:1, minHeight:52, fontSize:14, borderRadius:12, color:'#E24B4A', borderColor:'#E24B4A' }} onClick={() => declineJob(j)} disabled={acting === j.id} aria-label={`Decline job ${j.job_number}`}>
                Decline
              </button>
            </div>
          </div>
        ))}
      </main>
    </>
  )
}

/* ── MY SIGNINGS ── */
function MySignings() {
  const { user } = useAuth()
  const [jobs,    setJobs]    = useState([])
  const [loading, setLoading] = useState(true)
  const [search,  setSearch]  = useState('')

  useEffect(() => {
    if (!user) return
    supabase.from('jobs').select('*')
      .eq('notary_id', user.id)
      .in('status', ['assigned','accepted','on_the_way','arrived','completed','scanbacks_uploaded','client_approved','payment_received','notary_paid','closed'])
      .order('created_at', { ascending: false })
      .then(({ data }) => { setJobs(safeParseJobs(data)); setLoading(false) })

    // Real-time — job status updates for this notary
    const ch = supabase.channel('notary-my-signings-realtime')
      .on('postgres_changes', {
        event:  'UPDATE',
        schema: 'public',
        table:  'jobs',
        filter: `notary_id=eq.${user.id}`,
      }, payload => {
        setJobs(prev => prev.map(j => j.id === payload.new.id ? { ...j, ...payload.new } : j))
        const msgs = {
          accepted:    '📅 Assignment accepted!!',
          on_the_way:  '🚗 You are marked en route!!',
          arrived:     '📍 You are marked as arrived!!',
          completed:   '🎉 Job marked as completed!!',
          notary_paid: '💰 Payment received!!',
        }
        if (msgs[payload.new.status]) toast.success(msgs[payload.new.status], { duration:5000 })
      })
      .subscribe()
    return () => supabase.removeChannel(ch)
  }, [user])

  const filtered = jobs.filter(j => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      j.borrower_name?.toLowerCase().includes(q) ||
      j.signing_city?.toLowerCase().includes(q) ||
      j.signing_zip?.includes(q) ||
      j.company_name?.toLowerCase().includes(q) ||
      j.job_number?.toLowerCase().includes(q)
    )
  })

  // 🧹 Glow-up: active signings stay as full work-cards; finished/closed ones collapse into clean, airy rows
  const isArchived   = j => ['closed','cancelled'].includes(j.status)
  const activeJobs   = filtered.filter(j => !isArchived(j))
  const archivedJobs = filtered.filter(isArchived)

  // 🖨️ Get today's active jobs for batch printing
  const todayJobs = jobs.filter(j => {
    if (!j.scheduled_at) return false
    if (!['assigned','accepted','on_the_way','arrived'].includes(j.status)) return false
    const today = new Date()
    today.setHours(0,0,0,0)
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)
    const scheduled = new Date(j.scheduled_at)
    return scheduled >= today && scheduled < tomorrow
  })

  // 🖨️ Print all today's jobs as a single document
  function printTodaysJobs() {
    if (todayJobs.length === 0) {
      toast.error('No active jobs scheduled for today!!')
      return
    }
    const win = window.open('', '_blank')
    if (!win) {
      toast.error('Pop-up blocked. Please allow pop-ups to print.')
      return
    }
    const today = new Date().toLocaleString()
    const html = `<!DOCTYPE html>
<html>
<head>
<title>LCA Today's Jobs — ${today}</title>
<style>
  @media print { @page { margin: 0.5in; } }
  body { font-family: -apple-system, sans-serif; color:#0D1B3E; padding: 20px; }
  h1 { font-family: 'Cormorant Garamond', serif; font-size:28px; border-bottom: 3px solid #C9A84C; padding-bottom:10px; }
  h2 { font-family: 'Cormorant Garamond', serif; font-size:22px; color:#C9A84C; margin-top:30px; border-bottom:1px solid #E8E6DF; padding-bottom:6px; }
  .job-card { page-break-inside: avoid; margin-bottom:30px; padding:18px; border:1.5px solid #E8E6DF; border-radius:8px; }
  .row { display:flex; gap:20px; margin-bottom:8px; }
  .label { font-weight:700; color:#7A7870; font-size:11px; text-transform:uppercase; letter-spacing:0.06em; width:120px; flex-shrink:0; }
  .val { font-size:13px; color:#0D1B3E; }
  .notes { background:#FDF6E3; padding:10px 14px; border-radius:6px; margin-top:10px; font-size:12px; }
  .footer { margin-top:40px; padding-top:14px; border-top:2px solid #C9A84C; font-size:11px; color:#7A7870; text-align:center; }
  @media print { .job-card { page-break-after: always; } }
</style>
</head>
<body>
  <h1>📋 Today's Signing Assignments — ${today}</h1>
  <p style="font-size:13px;color:#7A7870;">Total assignments today: ${todayJobs.length}</p>
  ${todayJobs.map((j, idx) => `
    <div class="job-card">
      <h2>${idx + 1}. ${j.job_number || 'Job'} — ${j.borrower_name || 'Borrower'}</h2>
      <div class="row"><div class="label">Service</div><div class="val">${j.signing_type || '—'}</div></div>
      <div class="row"><div class="label">Date & Time</div><div class="val">${j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : 'TBD'}</div></div>
      <div class="row"><div class="label">Address</div><div class="val">${[j.signing_address, j.signing_city, j.signing_state, j.signing_zip].filter(Boolean).join(', ') || '—'}</div></div>
      <div class="row"><div class="label">Borrower Phone</div><div class="val">${j.borrower_phone || '—'}</div></div>
      <div class="row"><div class="label">Borrower Email</div><div class="val">${j.borrower_email || '—'}</div></div>
      <div class="row"><div class="label">Signing Company</div><div class="val">${j.company_name || j.signing_company || '—'}</div></div>
      <div class="row"><div class="label">File #</div><div class="val">${j.file_number || '—'}</div></div>
      <div class="row"><div class="label">Notary Pay</div><div class="val">$${j.notary_pay || 0}</div></div>
      ${j.special_instructions ? `<div class="notes"><strong>Special Instructions:</strong><br>${j.special_instructions}</div>` : ''}
      ${j.special_notes ? `<div class="notes"><strong>Notes:</strong><br>${j.special_notes}</div>` : ''}
    </div>
  `).join('')}
  <div class="footer">
    Generated by LCA Notary Portal · loanclosingagent.com · ${new Date().toLocaleString()}<br>
    Veteran &amp; Hispanic Family Owned Business 🇺🇸🇵🇷
  </div>
</body>
</html>`
    win.document.write(html)
    win.document.close()
    setTimeout(() => win.print(), 500)
    toast.success(`🖨️ Printing ${todayJobs.length} jobs for today!!`)
  }

  async function markComplete(job) {
    // 🛡️ QC GATE — must have at least 1 scanback before marking complete!!
    const { count, error: countErr } = await supabase
      .from('job_documents')
      .select('id', { count: 'exact', head: true })
      .eq('job_id', job.id)
      .in('doc_type', ['scan_back', 'completed_docs'])

    if (countErr) {
      toast.error('Could not verify scan-backs — please try again!!')
      return
    }

    // ✅ Skip QC gate for: (a) individual customer jobs OR (b) clients who opted out of scanbacks
    const skipScanbackGate = job.job_type === 'individual' || job.scan_backs_required === false
    if (!skipScanbackGate && (!count || count === 0)) {
      toast.error('🛡️ You must upload at least 1 scan-back before marking this job complete!! Tap "📋 View Full Job Details" to upload!!', { duration: 7000 })
      return
    }

    if (!window.confirm(`Mark job ${job.job_number} as complete??\n\nThis will:\n✅ Notify the client by email\n✅ Trigger payment workflow\n✅ Move job to "Awaiting Final Review"\n\nProceed??`)) {
      return
    }

    await supabase.from('jobs').update({ status: 'completed' }).eq('id', job.id)
    if (job.borrower_email) {
      await sendJobCompleted({
        client_name:  job.borrower_name,
        client_email: job.borrower_email,
        job_number:   job.job_number,
        service_name: job.signing_type,
        notary_name:  null,
        amount:       job.total_fee,
      })
    }
    // 📱 SMS notification to client (if phone on file)
    if (job.borrower_phone) {
      try {
        await smsJobCompleted({
          to: job.borrower_phone,
          jobNumber: job.job_number,
          borrowerName: job.borrower_name
        })
      } catch(smsErr) {
        console.error('SMS notification error:', smsErr)
      }
    }
    // ✏️ Log status event for audit trail
    try {
      await supabase.from('job_status_events').insert({
        job_id: job.id,
        status: 'completed',
        from_status: job.status,
        note: '✅ Signing completed by notary',
        created_by_role: 'notary'
      })
    } catch(evtErr) {
      console.error('Status event error:', evtErr)
    }
    // 📊 Track completion metrics
    try { await trackCompletion(user.id) } catch(e) { console.error('Metrics error:', e) }
    toast.success(`Job ${job.job_number} marked as completed — client notified by email!`)
    setJobs(j => j.map(x => x.id === job.id ? { ...x, status: 'completed' } : x))
  }

  return (
    <>
      <style>{NOTARY_STYLES}</style>
      <a href="#signings-main" className="lca-skip">Skip to signings</a>
      <main id="signings-main" tabIndex={-1}>
        <h1 className="page-title">My Signings</h1>

        {/* 🖨️ PRINT TODAY'S JOBS */}
        {todayJobs.length > 0 && (
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>
            <button
              onClick={printTodaysJobs}
              style={{
                background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)',
                color:'#C9A84C',
                border:'1px solid #C9A84C',
                borderRadius:8,
                padding:'10px 16px',
                fontSize:13,
                fontWeight:700,
                cursor:'pointer',
                fontFamily:'inherit',
                letterSpacing:'0.04em',
              }}
            >
              🖨️ Print Today's Jobs ({todayJobs.length})
            </button>
          </div>
        )}

        {/* SEARCH BOX */}
        {!loading && jobs.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <input
              type="search"
              placeholder="🔍 Search by Last Name, First Name, City, ZIP, Company, File #..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width:'100%', padding:'12px 16px', border:'1.5px solid #E0D9CC', borderRadius:12, fontSize:13, fontFamily:'inherit', background:'#fff' }}
            />
            {search && (
              <div style={{ fontSize:12, color:'#7A7870', marginTop:6 }}>
                {filtered.length} result{filtered.length !== 1 ? 's' : ''} for "{search}"
                <button onClick={() => setSearch('')} style={{ background:'none', border:'none', color:'#C9A84C', cursor:'pointer', fontSize:12, marginLeft:8 }}>Clear ×</button>
              </div>
            )}
          </div>
        )}

        {loading && <div style={{ textAlign:'center', padding:48 }} role="status" aria-label="Loading..."><div className="spinner" style={{ margin:'auto' }} /></div>}
        {!loading && jobs.length === 0 && (
          <div className="np-empty">
            <div className="np-empty-icon">📋</div>
            <div className="np-empty-text">No confirmed signings yet</div>
            <a href="/notary/available" className="btn btn-primary btn-sm">Check available jobs →</a>
          </div>
        )}
        {!loading && search && filtered.length === 0 && (
          <div className="np-empty">
            <div className="np-empty-icon">🔍</div>
            <div className="np-empty-text">No signings match "{search}"</div>
            <button onClick={() => setSearch('')} className="btn btn-outline btn-sm">Clear search</button>
          </div>
        )}
        {archivedJobs.length > 0 && activeJobs.length > 0 && (
          <div style={{ fontSize:12, fontWeight:700, color:'#0D1B3E', letterSpacing:'0.08em', textTransform:'uppercase', margin:'0 0 14px', display:'flex', alignItems:'center', gap:8 }}>
            🟢 Active Signings <span style={{ background:'#E8F5EE', color:'#2D7A4F', borderRadius:20, padding:'2px 9px', fontSize:11 }}>{activeJobs.length}</span>
          </div>
        )}
        {activeJobs.map(j => (
          <div key={j.id} className="np-job-card" style={{ borderLeftColor: jobAccent(j.status) }}>
            <div className="np-job-header">
              <div>
                <div className="np-job-number">{j.job_number}</div>
                <div className="np-job-type">{j.signing_type}</div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span className={`badge badge-${j.status}`}>{statusLabel(j.status)}</span>{j.job_type === 'individual' && <span style={{ background:'#F0E8FF', color:'#6A3A9A', fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:20, marginLeft:4 }}>👤 Individual</span>}
                <div className="np-job-pay">${j.notary_pay}</div>
              </div>
            </div>
            <div className="np-job-body">
              <div><div className="np-job-field-label">BORROWER</div><div className="np-job-field-value">{j.borrower_name}</div></div>
              <div><div className="np-job-field-label">DATE & TIME</div><div className="np-job-field-value">{j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : '—'}</div></div>
              <div><div className="np-job-field-label">LOCATION</div><div className="np-job-field-value">{j.signing_city}, {j.signing_state}</div></div>
              <div><div className="np-job-field-label">ADDRESS</div><div className="np-job-field-value">{j.signing_address}</div></div>
            </div>
            {j.special_instructions && <div className="np-job-notes"><strong>📋 Special instructions:</strong> {j.special_instructions}</div>}
            {j.order_notes && <div className="np-job-notes" style={{ background:'#FDF6E3', borderColor:'rgba(201,168,76,0.3)' }}><strong>💬 Client notes:</strong> {j.order_notes}</div>}
            {j.office_prefs && Object.keys(j.office_prefs).some(k => k !== 'custom_note' && j.office_prefs[k] === true) && (
              <div className="np-job-notes" style={{ background:'#EBF4FF', borderColor:'rgba(24,95,165,0.2)', color:'#1A4A6B' }}>
                <strong>🏛️ Office Requirements:</strong>{' '}
                {[
                  j.office_prefs.blue_ink    && '🖊️ Blue Ink Only',
                  j.office_prefs.no_staples  && '📎 No Staples',
                  j.office_prefs.gps_checkin && '📍 GPS Check-In Required',
                  j.office_prefs.borrower_id && '🪪 Upload Borrower ID',
                  j.office_prefs.call_before && '📞 Call 30 Min Before',
                  j.office_prefs.scan_backs  && '📄 Scan-Backs Required',
                ].filter(Boolean).join(' · ')}
                {j.office_prefs.custom_note && <div style={{ marginTop:4, fontStyle:'italic' }}>{j.office_prefs.custom_note}</div>}
              </div>
            )}
            <div style={{ padding:'10px 20px', borderTop:'1px solid #E8E6DF', background:'#F8F7F4', display:'flex', gap:8, flexWrap:'wrap' }}>
              <MapsButton address={j.signing_address} city={j.signing_city} state={j.signing_state} zip={j.signing_zip} />
              {j.status === 'assigned'
                ? <a href="/notary/available" className="np-maps-btn" style={{ background:'#FEF3E2', color:'#9A6800', borderColor:'#F59E0B' }} aria-label={`Accept job ${j.job_number} to unlock full details and documents`}>🔒 Accept to unlock details & documents</a>
                : <a href={`/notary/jobs/${j.id}`} className="np-maps-btn" style={{ background:'#0D1B3E', color:'#C9A84C', borderColor:'#0D1B3E' }} aria-label={`View full details for job ${j.job_number}`}>
                    📋 View Full Details →
                  </a>
              }
            </div>
            {/* ✅ MESSAGING — notary ↔ admin */}
            <JobMessages
              jobId={j.id}
              jobNumber={j.job_number}
              role="notary"
            />
            {j.status === 'assigned' && (
              <div style={{ margin:'0 0 4px', padding:'16px 20px', background:'#FEF3E2', border:'1px solid #F59E0B', borderRadius:12, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
                <div>
                  <div style={{ fontSize:14, fontWeight:700, color:'#9A6800', marginBottom:2 }}>⏳ Awaiting your response</div>
                  <div style={{ fontSize:12, color:'#7A5500' }}>You haven't accepted this assignment yet — the client is waiting!!</div>
                </div>
                <a href="/notary/available" className="btn btn-primary" style={{ minHeight:48, fontSize:14, fontWeight:700, borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', textDecoration:'none', padding:'0 20px' }} aria-label={`Accept or decline job ${j.job_number}`}>
                  Accept / Decline →
                </a>
              </div>
            )}
            {['accepted','on_the_way','arrived'].includes(j.status) && (
              <>
                {/* ✅ Only show doc upload for CLIENT jobs — not individual!! */}
                {j.job_type !== 'individual' && <NotaryDocSection jobId={j.id} status={j.status} />}
                <div className="np-job-actions">
                  <a href={`/notary/assignment/${j.id}`} className="btn btn-outline" style={{ flex:1, minHeight:52, fontSize:14, borderRadius:12, background:'linear-gradient(135deg,#C9A84C,#E8C46A)', color:'#0D1B3E', border:'none', fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', textDecoration:'none' }} aria-label={`Start assignment for job ${j.job_number}`}>
                    📲 Start Assignment
                  </a>
                  <button className="btn btn-primary" style={{ flex:1, minHeight:52, fontSize:15, fontWeight:600, borderRadius:12 }} onClick={() => markComplete(j)} aria-label={`Mark job ${j.job_number} as completed`}>
                    ✓ Mark as Completed
                  </button>
                  <a href={`/notary/jobs/${j.id}`} className="btn btn-outline" style={{ flex:1, minHeight:52, fontSize:14, fontWeight:600, borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', textDecoration:'none', color:'#9A7A30', borderColor:'#C9A84C', background:'#FFFBF0' }} aria-label={`View full details for job ${j.job_number}`}>
                    📋 View Full Job Details →
                  </a>
                </div>
              </>
            )}
            {['completed','scanbacks_uploaded','client_approved','payment_received','notary_paid','closed'].includes(j.status) && (
              <>
                {/* 📲 Re-enter the 9-step app to finish scans / shipping / tracking */}
                {j.job_type !== 'individual' && ['completed','scanbacks_uploaded','client_approved'].includes(j.status) && (
                  <a href={`/notary/assignment/${j.id}`} className="btn btn-outline" style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:48, fontSize:14, fontWeight:700, borderRadius:12, textDecoration:'none', color:'#0D1B3E', borderColor:'#C9A84C', background:'linear-gradient(135deg,#FFFBF0,#FDF6E3)', marginBottom:10 }} aria-label={`Continue assignment in app for job ${j.job_number}`}>
                    📲 Continue in App →
                  </a>
                )}
                {/* 📄 Scan-backs upload — stays available after "Signing Complete," through QC */}
                {j.job_type !== 'individual' && ['completed','scanbacks_uploaded'].includes(j.status) && <NotaryDocSection jobId={j.id} status={j.status} />}
                {/* ✅ Only show tracking for CLIENT jobs — not individual!! */}
                {j.job_type !== 'individual' && <TrackingInput job={j} />}
                {j.receipt_url && (
                  <a href={j.receipt_url} target="_blank" rel="noreferrer" style={{ display:'flex', alignItems:'center', gap:8, margin:'8px 0', padding:'10px 14px', background:'#F0FAF5', border:'1px solid #B8E0C8', borderRadius:8, fontSize:12, color:'#1A4A2E', fontWeight:600, textDecoration:'none' }}>
                    🧾 Shipping receipt uploaded — view →
                  </a>
                )}
                {/* ✅ Show client's rating if they've rated — read only for notary */}
                {j.client_rating ? (
                  <div className="np-rating-wrap" style={{ background:'#FAFDF5' }}>
                    <div className="np-rating-label">CLIENT RATING</div>
                    <div style={{ display:'flex', gap:4, marginBottom:4 }}>
                      {[1,2,3,4,5].map(s => (
                        <span key={s} style={{ fontSize:22, color: s <= j.client_rating ? '#C9A84C' : '#E8E6DF' }}>★</span>
                      ))}
                    </div>
                    <div style={{ fontSize:11, color:'#2D7A4F', fontWeight:500 }}>✓ Client rated you {j.client_rating} star{j.client_rating > 1 ? 's' : ''}!!</div>
                  </div>
                ) : (
                  <div className="np-rating-wrap">
                    <div style={{ fontSize:11, color:'#B8B5AB', fontStyle:'italic' }}>⏳ Awaiting client rating...</div>
                  </div>
                )}
                {/* ✅ Private notary feedback on client — admin eyes only */}
                <NotaryRatesClient jobId={j.id} clientName={j.borrower_name} onRated={stars => setJobs(prev => prev.map(x => x.id === j.id ? { ...x, notary_rating: stars } : x))} />
              </>
            )}
          </div>
        ))}

        {/* ✅ Finished & closed — collapsed into clean, airy rows (nothing left to do here) */}
        {archivedJobs.length > 0 && (
          <div style={{ marginTop: activeJobs.length > 0 ? 30 : 0 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#7A7870', letterSpacing:'0.08em', textTransform:'uppercase', margin:'0 0 14px', display:'flex', alignItems:'center', gap:8 }}>
              ✅ Completed &amp; Closed <span style={{ background:'#F0EEE8', color:'#9A958A', borderRadius:20, padding:'2px 9px', fontSize:11 }}>{archivedJobs.length}</span>
            </div>
            {archivedJobs.map(j => (
              <div key={j.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, padding:'14px 16px', background:'#fff', border:'1px solid #ECEAE3', borderLeft:'4px solid #2D7A4F', borderRadius:12, marginBottom:8, flexWrap:'wrap' }}>
                <div style={{ minWidth:0, flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <span style={{ fontFamily:'Cormorant Garamond,serif', fontWeight:700, color:'#0D1B3E', fontSize:16, lineHeight:1.1 }}>{j.borrower_name || 'Borrower'}</span>
                    <span className={`badge badge-${j.status}`}>{statusLabel(j.status)}</span>
                  </div>
                  <div style={{ fontSize:12, color:'#7A7870', marginTop:4 }}>
                    {j.job_number} · {j.signing_type} · {j.signing_city}, {j.signing_state}
                  </div>
                  <div style={{ fontSize:11, color:'#9A958A', marginTop:2 }}>
                    🗓️ {j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : '—'}
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:14, flexShrink:0 }}>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:9, color:'#B8B5AB', fontWeight:600, letterSpacing:'0.06em' }}>YOUR PAY</div>
                    <div style={{ fontWeight:700, color:'#2D7A4F', fontSize:16 }}>${j.notary_pay || '—'}</div>
                  </div>
                  <a href={`/notary/jobs/${j.id}`} className="np-maps-btn" style={{ background:'#0D1B3E', color:'#C9A84C', borderColor:'#0D1B3E', whiteSpace:'nowrap' }} aria-label={`View full details for job ${j.job_number}`}>
                    View →
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </>
  )
}


/* ── CREDENTIALS ── */
function Credentials() {
  const { user, profile } = useAuth()
  const [notary,    setNotary]    = useState(null)  // ✅ Load notary record for signed agreement dates!!
  const [creds,     setCreds]     = useState([])
  const [uploading,    setUploading]    = useState(null) // track which doc_type is uploading
  const [reUploading,  setReUploading]  = useState(null) // track which cred id is re-uploading
  const [expires,      setExpires]      = useState({})   // expiry per doc_type

  const CRED_TYPES = [
    { value: 'commission',       label: 'Notary Commission Certificate',        required: true,  icon: '📜', hasExpiry: true,  hasIssued: false },
    { value: 'eo_insurance',     label: 'E&O Insurance Declaration Page',       required: true,  icon: '🛡️', hasExpiry: true,  hasIssued: false },
    { value: 'government_id',    label: 'Government-issued Photo ID',           required: true,  icon: '🪪', hasExpiry: true,  hasIssued: true  },
    { value: 'background_check', label: 'Background Check',                     required: true,  icon: '🔍', hasExpiry: false, hasIssued: true  },
    { value: 'w9_form',          label: 'W-9 Form (completed & signed)',        required: true,  icon: '📋', hasExpiry: false, hasIssued: false },
    { value: 'nna_cert',         label: 'Notary Signing Agent Certifications',  required: false, icon: '🏅', hasExpiry: false, hasIssued: false },
    { value: 'extra_cert_1',     label: 'Additional Certification / Credential 1', required: false, icon: '📄', hasExpiry: false, hasIssued: false },
    { value: 'extra_cert_2',     label: 'Additional Certification / Credential 2', required: false, icon: '📄', hasExpiry: false, hasIssued: false },
    { value: 'extra_cert_3',     label: 'Additional Certification / Credential 3', required: false, icon: '📄', hasExpiry: false, hasIssued: false },
    { value: 'extra_cert_4',     label: 'Additional Certification / Credential 4', required: false, icon: '📄', hasExpiry: false, hasIssued: false },
  ]

  // ✅ Custom labels for optional credentials — saved to Supabase!!
  const [customLabels, setCustomLabels] = useState({
    extra_cert_1: '', extra_cert_2: '', extra_cert_3: '', extra_cert_4: '',
  })
  const [editingLabel, setEditingLabel] = useState(null)
  const [labelInput,   setLabelInput]   = useState('')
  const [viewModal,    setViewModal]    = useState(null) // { field, signedAt, signature }

  function getCredLabel(credType) {
    // First check Supabase-persisted custom name from the uploaded doc
    const doc = getLatestByType(credType.value)
    if (doc?.custom_name) return doc.custom_name
    // Then check local state
    if (credType.value.startsWith('extra_cert') && customLabels[credType.value]) {
      return customLabels[credType.value]
    }
    return credType.label
  }

  async function saveCustomLabel(credTypeValue) {
    if (!labelInput.trim()) { setEditingLabel(null); return }
    // Save to local state immediately
    setCustomLabels(prev => ({ ...prev, [credTypeValue]: labelInput.trim() }))
    setEditingLabel(null)
    // Persist to Supabase — update custom_name on the latest doc of this type
    const doc = getLatestByType(credTypeValue)
    if (doc?.id) {
      await supabase.from('notary_docs').update({ custom_name: labelInput.trim() }).eq('id', doc.id)
    }
    toast.success('✅ Credential renamed and saved!!')
  }

useEffect(() => {
    if (!user) return
    // ✅ Load notary record for signed agreement timestamps!!
    supabase.from('notaries').select('*').eq('id', user.id).single()
      .then(({ data }) => setNotary(data))
    supabase.from('notary_docs').select('*').eq('notary_id', user.id)
      .order('uploaded_at', { ascending: false })
      .then(({ data }) => {
        setCreds(data || [])
        // Restore custom labels from Supabase
        if (data) {
          const labels = {}
          data.forEach(d => {
            if (d.custom_name && d.doc_type.startsWith('extra_cert')) {
              labels[d.doc_type] = d.custom_name
            }
          })
          if (Object.keys(labels).length > 0) {
            setCustomLabels(prev => ({ ...prev, ...labels }))
          }
        }
      })
  }, [user])

  // ✅ Get LATEST doc per type — no messy duplicates!!
  function getLatestByType(docType) {
    return creds.filter(c => c.doc_type === docType)[0] || null
  }

  // ✅ Progress calculations — reactive to creds state!!
  const credRequired   = CRED_TYPES.filter(t => t.required)
  const credVerified   = credRequired.filter(t => getLatestByType(t.value)?.status === 'approved').length
  const credUploaded   = credRequired.filter(t => ['approved','pending'].includes(getLatestByType(t.value)?.status)).length
  const credPct        = Math.round((credUploaded / credRequired.length) * 100)
  const credAllVerified = credVerified === credRequired.length
  const credAllUploaded = credUploaded === credRequired.length

  async function uploadDoc(e, docType, replaceId = null) {
    const file = e.target.files[0]
    if (!file || !user) return

    // ✅ FILE SIZE CHECK — warn if over 20MB (auto-compression handles most cases)
    if (file.size > 20 * 1024 * 1024) {
      toast.error(`📁 File is too large (${(file.size/1024/1024).toFixed(1)}MB)!! Maximum size is 20MB. Please scan at a lower resolution and try again!!`, { duration: 6000 })
      e.target.value = ''
      return
    }

    // ✅ FILE TYPE CHECK
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
    if (!allowedTypes.includes(file.type)) {
      toast.error('❌ Only PDF, JPG, and PNG files are accepted!!', { duration: 5000 })
      e.target.value = ''
      return
    }

    // ✅ DATE VALIDATION — must fill dates BEFORE uploading!!
    const credType = CRED_TYPES.find(t => t.value === docType)
    if (!replaceId && credType) {
      if (credType.hasExpiry && !expires[docType]) {
        toast.error(`📅 Please enter the Expiration Date for your ${credType.label} before uploading!!`, { duration: 4000 })
        e.target.value = ''
        return
      }
      if (credType.hasIssued && !expires[docType + '_issued']) {
        toast.error(`📅 Please enter the Issued Date for your ${credType.label} before uploading!!`, { duration: 4000 })
        e.target.value = ''
        return
      }
    }

    replaceId ? setReUploading(replaceId) : setUploading(docType)
    try {
      // ✅ AUTO COMPRESS — silently compress before upload!!
      const processedFile = await compressFile(file)
      const ext  = processedFile.name.split('.').pop().toLowerCase()
      const path = `${user.id}/${docType}-${Date.now()}.${ext}`

      // ✅ upsert:true prevents duplicate file errors on retry!!
      const { error: uploadErr } = await supabase.storage.from('credentials').upload(path, processedFile, { upsert: true })
      if (uploadErr) throw uploadErr
      const { data: { publicUrl } } = supabase.storage.from('credentials').getPublicUrl(path)

      if (replaceId) {
        await supabase.from('notary_docs').update({
          file_url:      publicUrl,
          status:        'pending',
          rejected_at:   null,
          rejected_by:   null,
          reject_reason: null,
          expires_at:    expires[docType] || null,
        }).eq('id', replaceId)
        toast.success('✅ Document re-uploaded successfully — pending review!!')
      } else {
        const label = CRED_TYPES.find(t => t.value === docType)?.label
        const existing = creds.find(c => c.doc_type === docType && c.status !== 'rejected')
        if (existing) {
          await supabase.from('notary_docs').update({
            file_url:    publicUrl,
            status:      'pending',
            expires_at:  expires[docType]            || null,
            issued_date: expires[docType + '_issued'] || null,
          }).eq('id', existing.id)
          toast.success('✅ Document updated — pending review!!')
        } else {
          await supabase.from('notary_docs').insert({
            notary_id:   user.id,
            doc_type:    docType,
            doc_label:   label,
            file_url:    publicUrl,
            expires_at:  expires[docType]            || null,
            issued_date: expires[docType + '_issued'] || null,
            status:      'pending',
          })
          toast.success('✅ Document uploaded successfully — our team will review within 1 business day!!', { duration: 5000 })
          // ✅ Only send admin alert on FIRST document upload — not every single one!!
          const existingDocs = creds.filter(c => c.status !== 'rejected')
          if (existingDocs.length === 0) {
            await sendCredentialUploadAlert({
              notary_name:  profile?.full_name || 'A notary',
              notary_email: user.email,
              doc_label:    'credentials',
            })
          }
        }
      }
      // ✅ Refresh with delay to ensure DB commits first!!
      setTimeout(() => {
        supabase.from('notary_docs').select('*').eq('notary_id', user.id)
          .order('uploaded_at', { ascending: false })
          .then(({ data }) => setCreds(data || []))
      }, 800)
    } catch (err) {
      console.error('Upload error:', err)
      if (err.message?.includes('JWT')) {
        toast.error('⚠️ Session expired — please refresh the page and try again!!', { duration: 8000 })
      } else if (err.message?.includes('network') || err.message?.includes('fetch')) {
        toast.error('⚠️ Network error — please check your internet connection and try again!!', { duration: 8000 })
      } else {
        toast.error(`❌ Upload failed: ${err.message || 'Please try again!!'}`, { duration: 8000 })
      }
    } finally {
      setUploading(null)
      setReUploading(null)
      e.target.value = ''
    }
  }

  const CRED_STYLES = `
    .cred-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
    @media(max-width:640px){ .cred-grid { grid-template-columns:1fr; } }
    .cred-card { border:1px solid #E8E6DF; border-radius:14px; padding:16px 18px; display:flex; flex-direction:column; gap:8px; transition:border-color 0.15s; }
    .cred-card.approved { border-color:rgba(45,122,79,0.3); background:#FAFDF5; }
    .cred-card.pending  { border-color:rgba(201,168,76,0.3); background:#FDFCF5; }
    .cred-card.rejected { border-color:rgba(226,75,74,0.3);  background:#FFF8F8; }
    .cred-card.empty    { border-style:dashed; background:#FAFAF8; }
    .cred-card-top { display:flex; align-items:center; gap:10px; }
    .cred-icon { font-size:22px; flex-shrink:0; }
    .cred-body { flex:1; min-width:0; }
    .cred-title { font-size:13px; font-weight:600; color:#0D1B3E; margin-bottom:4px; display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
    .cred-meta { font-size:11px; color:#7A7870; margin-bottom:6px; }
    .cred-reject-banner { background:#FFF0F0; border:1px solid rgba(226,75,74,0.2); border-radius:8px; padding:8px 10px; font-size:11px; color:#A32D2D; line-height:1.5; }
    .cred-actions { display:flex; gap:6px; align-items:center; flex-wrap:wrap; margin-top:auto; padding-top:4px; }
    .cred-upload-label { display:inline-flex; align-items:center; gap:5px; padding:6px 12px; border-radius:8px; font-size:11px; font-weight:600; cursor:pointer; transition:all 0.15s; }
    .cred-upload-label.primary { background:#C9A84C; color:#0D1B3E; }
    .cred-upload-label.danger  { background:#E24B4A; color:#fff; }
    .cred-upload-label:hover { opacity:0.85; }
    .cred-required { font-size:9px; background:#FDF2F2; color:#C0392B; padding:2px 6px; border-radius:10px; font-weight:600; }
    .cred-optional { font-size:9px; background:#F8F7F4; color:#B8B5AB; padding:2px 6px; border-radius:10px; }
    .cred-date-input { display:block; margin-top:3px; padding:6px 10px; border:2px solid #C9A84C; border-radius:8px; font-size:12px; width:100%; background:#FFFDF5; color:#0D1B3E; outline:none; transition:all 0.2s; }
    .cred-date-input:focus { border-color:#9A7A30; box-shadow:0 0 0 3px rgba(201,168,76,0.2); }
    .cred-date-label { font-size:10px; font-weight:600; color:#7A7870; display:block; margin-top:8px; margin-bottom:2px; letter-spacing:0.04em; }
  `

  return (
    <>
      <style>{NOTARY_STYLES}{CRED_STYLES}</style>
      {viewModal && (
        <ViewAgreementModal
          agreementType={viewModal.field}
          signedAt={viewModal.signedAt}
          signature={viewModal.signature}
          onClose={() => setViewModal(null)}
        />
      )}
      <a href="#credentials-main" className="lca-skip">Skip to credentials</a>
      <main id="credentials-main" tabIndex={-1}>
        <h1 className="page-title">My Credentials</h1>
        <p className="page-sub">Upload your documents below — Our Team reviews within 1 business day!!</p>

        {/* Summary badges */}
        <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
          {['approved','pending','rejected'].map(s => {
            const count = CRED_TYPES.filter(t => getLatestByType(t.value)?.status === s).length
            if (count === 0) return null
            const colors = { approved:'#2D7A4F', pending:'#C9A84C', rejected:'#E24B4A' }
            const labels = { approved:'Verified', pending:'Under Review', rejected:'Action Required' }
            return (
              <div key={s} style={{ background: s==='approved'?'#EAF5EE':s==='pending'?'#FDF6E3':'#FDF2F2', border:`1px solid ${colors[s]}30`, borderRadius:10, padding:'6px 14px', fontSize:12, fontWeight:500, color:colors[s] }}>
                {count} {labels[s]}
              </div>
            )
          })}
          {CRED_TYPES.filter(t => t.required && !getLatestByType(t.value)).length > 0 && (
            <div style={{ background:'#F8F7F4', border:'1px solid #E8E6DF', borderRadius:10, padding:'6px 14px', fontSize:12, fontWeight:500, color:'#B8B5AB' }}>
              {CRED_TYPES.filter(t => t.required && !getLatestByType(t.value)).length} Not uploaded yet
            </div>
          )}
        </div>

        {/* ✅ PROGRESS BAR — reactive to creds!! */}
        <div style={{ background: credAllVerified ? 'linear-gradient(135deg,#0D1B3E,#1A3A5E)' : credAllUploaded ? 'linear-gradient(135deg,#1A3A5E,#0D2A4E)' : 'linear-gradient(135deg,#0D1B3E,#1A3A5E)', borderRadius:14, padding:18, marginBottom:20 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
            <div>
              <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:16, color:'#fff', fontWeight:500 }}>
                {credAllVerified ? '🎉 All credentials verified!!' : credAllUploaded ? '⏳ All uploaded — pending review!!' : 'Credential Upload Progress'}
              </div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', marginTop:2 }}>
                {credAllVerified
                  ? 'You are fully verified and eligible for all LCA assignments!!'
                  : credAllUploaded
                  ? `All ${credRequired.length} documents uploaded!! Our Team is reviewing — you'll be notified once approved!!`
                  : `${credUploaded} of ${credRequired.length} documents uploaded · ${credVerified} verified · ${credUploaded - credVerified} pending review`
                }
              </div>
            </div>
           <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:28, fontWeight:700, color:'#C9A84C' }}>{credPct}%</div>
          </div>
          {/* Progress bar */}
          <div style={{ height:8, background:'rgba(255,255,255,0.1)', borderRadius:20, overflow:'hidden' }}>
            <div style={{ height:8, borderRadius:20, background: 'linear-gradient(to right,#C9A84C,#9A7A30)', width:`${credPct}%`, transition:'width 0.5s' }} />
          </div>
          {/* Per-doc dots */}
          <div style={{ display:'flex', gap:6, marginTop:10, flexWrap:'wrap' }}>
            {credRequired.map(t => {
              const doc    = getLatestByType(t.value)
              const status = doc?.status || 'empty'
              return (
                <div key={t.value} style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'rgba(255,255,255,0.6)' }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', background: status==='approved'?'#5BA882':status==='pending'?'#C9A84C':status==='rejected'?'#FCA5A5':'rgba(255,255,255,0.2)', flexShrink:0 }} />
                  {t.label.split(' ')[0]}
                </div>
              )
            })}
            <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', marginLeft:4 }}>
              ⚪ Not uploaded · 🟡 Pending · 🟢 Verified
            </div>
          </div>
        </div>

        {/* ✅ DRAG & DROP CREDENTIAL CARDS */}
        <div className="cred-grid">
        {CRED_TYPES.map(credType => {
          const latest      = getLatestByType(credType.value)
          const status      = latest?.status || 'empty'
          const isUploading = uploading === credType.value
          const [isExpanded, setIsExpanded] = React.useState(false)
          const isReUp      = reUploading === latest?.id
          const inputId     = `cred-input-${credType.value}`
          const reUpId      = `reup-input-${credType.value}`
          const [isDragging, setIsDragging] = [false, () => {}]

          // Colors by status
          const colors = {
            empty:    { border:'2px dashed #E8E6DF', bg:'#FAFAF8',  zone:'#F0EEE8' },
            pending:  { border:'2px solid #C9A84C',  bg:'#FFFDF5',  zone:'#FDF6E3' },
            approved: { border:'2px solid #2D7A4F',  bg:'#F0FDF4',  zone:'#EAF5EE' },
            rejected: { border:'2px solid #E24B4A',  bg:'#FFF8F8',  zone:'#FDF2F2' },
          }
          const c = colors[status] || colors.empty

          return (
            <div key={credType.value} style={{ border:c.border, borderRadius:16, background:c.bg, overflow:'hidden', transition:'all 0.2s' }}>

              {/* HEADER */}
              <button type="button" onClick={() => setIsExpanded(e => !e)} style={{ width:'100%', background:'none', border:'none', cursor:'pointer', padding:'14px 16px', borderBottom:`1px solid ${status==='empty'?'#E8E6DF':'transparent'}`, display:'flex', alignItems:'center', gap:10, textAlign:'left', fontFamily:'inherit' }}>
                <span style={{ fontSize:28 }}>{credType.icon}</span>
                <div style={{ flex:1 }}>
                  {/* ✅ Optional creds can be renamed!! */}
                  {credType.value.startsWith('extra_cert') && editingLabel === credType.value ? (
                    <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                      <input
                        value={labelInput}
                        onChange={e => setLabelInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && saveCustomLabel(credType.value)}
                        placeholder="e.g. NNA Certification, LSS Certificate..."
                        autoFocus
                        style={{ flex:1, padding:'5px 8px', border:'2px solid #C9A84C', borderRadius:6, fontSize:12, fontFamily:'inherit' }}
                      />
                      <button onClick={() => saveCustomLabel(credType.value)} className="btn btn-primary btn-sm" style={{ fontSize:10, padding:'4px 8px' }}>✓</button>
                      <button onClick={() => setEditingLabel(null)} className="btn btn-outline btn-sm" style={{ fontSize:10, padding:'4px 8px' }}>✕</button>
                    </div>
                  ) : (
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'#0D1B3E' }}>{getCredLabel(credType)}</div>
                      {credType.value.startsWith('extra_cert') && (
                        <button onClick={() => { setEditingLabel(credType.value); setLabelInput(customLabels[credType.value] || '') }}
                          style={{ background:'none', border:'none', cursor:'pointer', fontSize:11, color:'#B8B5AB', padding:0 }}
                          title="Rename this credential">✏️</button>
                      )}
                    </div>
                  )}
                  <div style={{ fontSize:11, marginTop:2 }}>
                    {credType.required
                      ? <span style={{ color:'#C0392B', fontWeight:600 }}>✦ Required</span>
                      : <span style={{ color:'#B8B5AB' }}>Optional</span>
                    }
                  </div>
                </div>
                {/* BIG STATUS BADGE */}
                {status === 'approved' && <div style={{ background:'#2D7A4F', color:'#fff', fontSize:12, fontWeight:700, padding:'6px 14px', borderRadius:20 }}>✓ Verified</div>}
                {status === 'pending'  && <div style={{ background:'#C9A84C', color:'#0D1B3E', fontSize:12, fontWeight:700, padding:'6px 14px', borderRadius:20 }}>⏳ Pending</div>}
                {status === 'rejected' && <div style={{ background:'#E24B4A', color:'#fff', fontSize:12, fontWeight:700, padding:'6px 14px', borderRadius:20 }}>❌ Rejected</div>}
                {status === 'empty' && <span style={{ fontSize:11, color:'#B8B5AB', marginLeft:8 }}>{isExpanded ? '▲' : '▼'}</span>}
              </button>

              {/* BODY — only shown when expanded */}
              {isExpanded && (<>

              {/* REJECTION REASON */}
              {status === 'rejected' && latest?.reject_reason && (
                <div style={{ background:'#FFF0F0', padding:'10px 16px', fontSize:12, color:'#A32D2D', borderBottom:'1px solid #FCD5D5' }}>
                  <strong>Reason:</strong> {latest.reject_reason}
                </div>
              )}

              {/* META — approved/pending */}
              {latest && status !== 'rejected' && (
                <div style={{ padding:'8px 16px', fontSize:11, color:'#7A7870', borderBottom:'1px solid #F0EEE8' }}>
                  {status === 'approved' && `✓ Verified ${latest.verified_at ? new Date(latest.verified_at).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }) + ' · ' + new Date(latest.verified_at).toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit' }) : ''}${latest.expires_at ? ` · Expires ${latest.expires_at}` : ''}`}
                  {status === 'pending'  && `📄 ${latest.doc_label || credType.label} · Uploaded ${new Date(latest.uploaded_at).toLocaleString()} · Awaiting review`}
                </div>
              )}

              {/* DATE FIELDS — only when empty */}
              {status === 'empty' && (credType.hasIssued || credType.hasExpiry) && (
                <div style={{ padding:'12px 16px', background:'#F8F7F4', borderBottom:'1px solid #E8E6DF', display:'grid', gridTemplateColumns: credType.hasIssued && credType.hasExpiry ? '1fr 1fr' : '1fr', gap:8 }}>
                  {credType.hasIssued && (
                    <div>
                      <label style={{ fontSize:11, fontWeight:600, color:'#7A7870', display:'block', marginBottom:4 }}>
                        📅 Issued Date{credType.value === 'background_check' && <span style={{ color:'#E24B4A' }}> *</span>}
                      </label>
                      <input type="date" className="cred-date-input"
                        value={expires[credType.value + '_issued'] || ''}
                        onChange={e => setExpires(x => ({ ...x, [credType.value + '_issued']: e.target.value }))}
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  )}
                  {credType.hasExpiry && (
                    <div>
                      <label style={{ fontSize:11, fontWeight:600, color:'#7A7870', display:'block', marginBottom:4 }}>📅 Expiration Date</label>
                      <input type="date" className="cred-date-input"
                        value={expires[credType.value] || ''}
                        onChange={e => setExpires(x => ({ ...x, [credType.value]: e.target.value }))}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* ✅ DRAG & DROP ZONE — empty state */}
              {status === 'empty' && (
                <label htmlFor={inputId}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.background='#FDF6E3' }}
                  onDragLeave={e => { e.currentTarget.style.background=c.zone }}
                  onDrop={e => {
                    e.preventDefault()
                    e.currentTarget.style.background = c.zone
                    const file = e.dataTransfer.files[0]
                    if (file) uploadDoc({ target:{ files:[file] } }, credType.value)
                  }}
                  style={{ display:'block', padding:'28px 16px', background:c.zone, cursor:'pointer', textAlign:'center', transition:'background 0.2s' }}
                >
                  <input id={inputId} type="file" accept=".pdf,.jpg,.jpeg,.png" capture="environment" style={{ display:'none' }} onChange={e => uploadDoc(e, credType.value)} disabled={isUploading} />
                  {isUploading ? (
                    <div>
                      <div style={{ fontSize:32, marginBottom:8 }}>⏳</div>
                      <div style={{ fontSize:13, fontWeight:600, color:'#C9A84C' }}>Uploading — Please wait!!</div>
                      <div style={{ fontSize:11, color:'#B8A060', marginTop:4 }}>This may take 1-2 minutes depending on file size and your connection speed!!</div>
                      <div style={{ fontSize:11, color:'#B8A060', marginTop:4 }}>⚠️ Please do NOT close this page or upload again!!</div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize:36, marginBottom:8 }}>📂</div>
                      <div style={{ fontSize:14, fontWeight:600, color:'#0D1B3E', marginBottom:4 }}>Drag & drop your file here</div>
                      <div style={{ fontSize:12, color:'#7A7870', marginBottom:6 }}>or tap to browse — PDF, JPG, PNG</div>
                      <div style={{ fontSize:11, color:'#B8A060', marginBottom:10 }}>⏱️ Uploads may take 1-2 minutes — please be patient!!</div>
                      <div style={{ display:'inline-block', background:'#C9A84C', color:'#0D1B3E', fontSize:12, fontWeight:700, padding:'8px 20px', borderRadius:8 }}>+ Choose File</div>
                    </div>
                  )}
                </label>
              )}

              {/* ✅ PENDING STATE — big confirmation */}
              {status === 'pending' && (
                <div style={{ padding:'20px 16px', background:c.zone, textAlign:'center' }}>
                  <div style={{ fontSize:36, marginBottom:8 }}>⏳</div>
                  <div style={{ fontSize:14, fontWeight:700, color:'#8A5C00', marginBottom:4 }}>Under Review</div>
                  <div style={{ fontSize:12, color:'#B8A060', marginBottom:12 }}>Our Team will review within 1 business day!!</div>
                  <div style={{ display:'flex', gap:8, justifyContent:'center', flexWrap:'wrap' }}>
                    {latest?.file_url && <a href={latest.file_url} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm" style={{ fontSize:11 }}>View Uploaded File</a>}
                    <label style={{ cursor:'pointer' }}>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display:'none' }} onChange={e => uploadDoc(e, credType.value, latest?.id)} />
                      <span className="btn btn-outline btn-sm" style={{ fontSize:11, color:'#9A7A30', borderColor:'#C9A84C' }}>✏️ Replace</span>
                    </label>
                  </div>
                </div>
              )}

              {/* ✅ APPROVED STATE — big green confirmation */}
              {status === 'approved' && (
                <div style={{ padding:'20px 16px', background:c.zone, textAlign:'center' }}>
                  <div style={{ fontSize:36, marginBottom:8 }}>✅</div>
                  <div style={{ fontSize:14, fontWeight:700, color:'#2D7A4F', marginBottom:4 }}>Verified & Approved!!</div>
                  <div style={{ fontSize:12, color:'#4A7A5C', marginBottom:12 }}>Our Team will notify you when renewal is needed!!</div>
                  <div style={{ display:'flex', gap:8, justifyContent:'center', flexWrap:'wrap' }}>
                    {latest?.file_url && <a href={latest.file_url} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm" style={{ fontSize:11 }}>View File</a>}
                    <label style={{ cursor:'pointer' }}>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display:'none' }} onChange={e => uploadDoc(e, credType.value, latest?.id)} />
                      <span className="btn btn-outline btn-sm" style={{ fontSize:11, color:'#9A7A30', borderColor:'#C9A84C' }}>✏️ Update</span>
                    </label>
                  </div>
                </div>
              )}

              {/* ✅ REJECTED — re-upload zone */}
              {status === 'rejected' && (
                <label htmlFor={reUpId}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.background='#FFF0F0' }}
                  onDragLeave={e => { e.currentTarget.style.background=c.zone }}
                  onDrop={e => {
                    e.preventDefault()
                    e.currentTarget.style.background = c.zone
                    const file = e.dataTransfer.files[0]
                    if (file) uploadDoc({ target:{ files:[file] } }, credType.value, latest.id)
                  }}
                  style={{ display:'block', padding:'24px 16px', background:c.zone, cursor:'pointer', textAlign:'center', transition:'background 0.2s' }}
                >
                  <input id={reUpId} type="file" accept=".pdf,.jpg,.jpeg,.png" capture="environment" style={{ display:'none' }} onChange={e => uploadDoc(e, credType.value, latest.id)} disabled={isReUp} />
                  {isReUp ? (
                    <div style={{ fontSize:13, color:'#C9A84C' }}>⏳ Uploading...</div>
                  ) : (
                    <div>
                      <div style={{ fontSize:32, marginBottom:8 }}>🔄</div>
                      <div style={{ fontSize:13, fontWeight:600, color:'#E24B4A', marginBottom:4 }}>Please re-upload a corrected document</div>
                      <div style={{ fontSize:12, color:'#7A7870', marginBottom:10 }}>Drag & drop or tap to browse</div>
                      <div style={{ display:'inline-block', background:'#E24B4A', color:'#fff', fontSize:12, fontWeight:700, padding:'8px 20px', borderRadius:8 }}>🔄 Re-upload</div>
                    </div>
                  )}
                </label>
              )}
              </>)}
            </div>
          )
        })}
        </div>

        {/* ✅ SIGNED ONBOARDING DOCUMENTS — Legal record of what notary agreed to!! */}
        <div style={{ marginTop:24 }}>
          <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, color:'#0D1B3E', fontWeight:600, marginBottom:4 }}>
            ⚖️ My Signed Agreements
          </div>
          <div style={{ fontSize:12, color:'#7A7870', marginBottom:14 }}>
            Legal agreements you signed during onboarding. These are your binding commitments as an LCA contractor.
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {[
              {
                icon:      '📋',
                title:     'Independent Contractor Agreement (ICA)',
                desc:      'Establishes your relationship with Loan Closing Agent LLC as an independent contractor.',
                field:     'ica_agreed_at',
                color:     '#185FA5',
                bg:        '#EBF4FF',
              },
              {
                icon:      '🔒',
                title:     'Confidentiality & Data Protection (NPPI)',
                desc:      'Governs your handling of Non-Public Personal Information including borrower data, SSNs, and financial records.',
                field:     'nppi_agreed_at',
                color:     '#6A3A9A',
                bg:        '#F0EBF8',
              },
              {
                icon:      '🤝',
                title:     'Non-Solicitation Agreement',
                desc:      'Protects LCA\'s client and notary relationships during and after your engagement.',
                field:     'nonsolicitation_agreed_at',
                color:     '#2D7A4F',
                bg:        '#EAF5EE',
              },
              {
                icon:      '✍️',
                title:     'Certification Statement & Digital Signature',
                desc:      'Your digital signature certifying that all information provided in your application is true and accurate.',
                field:     'signed_at',
                color:     '#9A7A30',
                bg:        '#FDF6E3',
                extra:     profile?.digital_signature ? `Signed as: "${profile.digital_signature}"` : null,
              },
           ].map(doc => {
              const signedAt = notary?.[doc.field]  // ✅ Read from notaries table, not profiles!!
              return (
                <div key={doc.field} style={{
                  background:   '#fff',
                  border:       `1px solid ${signedAt ? doc.color + '40' : '#E8E6DF'}`,
                  borderRadius: 10,
                  padding:      '14px 16px',
                  display:      'flex',
                  alignItems:   'flex-start',
                  gap:          12,
                }}>
                  <div style={{
                    width:          36,
                    height:         36,
                    borderRadius:   8,
                    background:     signedAt ? doc.bg : '#F8F7F4',
                    display:        'flex',
                    alignItems:     'center',
                    justifyContent: 'center',
                    fontSize:       18,
                    flexShrink:     0,
                  }}>{doc.icon}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:'#0D1B3E', marginBottom:2 }}>{doc.title}</div>
                    <div style={{ fontSize:11, color:'#7A7870', marginBottom:6, lineHeight:1.5 }}>{doc.desc}</div>
                    {doc.extra && signedAt && (
                      <div style={{ fontSize:11, color:doc.color, fontStyle:'italic', marginBottom:4 }}>{doc.extra}</div>
                    )}
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:4 }}>
                      {signedAt ? (
                        <>
                          {/* SIGNED — timestamp pill */}
                          <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:doc.bg, border:`1px solid ${doc.color}40`, borderRadius:20, padding:'3px 12px' }}>
                            <div style={{ width:6, height:6, borderRadius:'50%', background:doc.color }} />
                            <span style={{ fontSize:10, fontWeight:600, color:doc.color }}>
                              ✅ Signed {new Date(signedAt).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' })} at {new Date(signedAt).toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit' })}
                            </span>
                          </div>
                          {/* ✅ VIEW AGREEMENT — opens read-only modal */}
                          <button
                            onClick={() => setViewModal({ field: doc.field, signedAt, signature: notary?.digital_signature || notary?.full_name || profile?.full_name })}
                            style={{ fontSize:10, fontWeight:600, color:doc.color, background:'none', border:`1px solid ${doc.color}40`, borderRadius:20, padding:'3px 12px', cursor:'pointer', fontFamily:'inherit' }}>
                            📄 View Agreement
                          </button>
                        </>
                      ) : (
                        /* UNSIGNED — Sign Agreement button */
                        <a
                          href={`/notary/onboarding?sign=${doc.field}`}
                          style={{ display:'inline-flex', alignItems:'center', gap:6, background:doc.bg, border:`1px solid ${doc.color}40`, borderRadius:20, padding:'4px 14px', fontSize:11, fontWeight:600, color:doc.color, textDecoration:'none', cursor:'pointer' }}
                        >
                          ✍️ Sign Agreement
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </main>
    </>
  )
}



function EliteRewards() {
  const { user } = useAuth()
  const [notary, setNotary] = useState(null)

  useEffect(() => {
    if (!user) return
    supabase.from('notaries').select('*').eq('id', user.id).single().then(({ data }) => setNotary(data))
  }, [user])

  const signings    = notary?.total_signings || 0
  const isBronze    = signings >= 50
  const isSilver    = signings >= 100
  const isGold      = signings >= 250
  const isPlatinum  = signings >= 500
  const isElite     = signings >= 1000

  const MILESTONES = [
    {
      rank:      'Bronze',
      signings:  50,
      unlocked:  isBronze,
      icon:      '🥉',
      badge:     RANK_BADGES['Bronze'],
      reward:    '1 Free Month of Print PDF Utility — professional dual-tray printing software!!',
      perk:      '1 Month FREE',
      color:     '#CD7F32',
    },
    {
      rank:      'Silver',
      signings:  100,
      unlocked:  isSilver,
      icon:      '🥈',
      badge:     RANK_BADGES['Silver'],
      reward:    '2 Free Months of Print PDF Utility!!',
      perk:      '2 Months FREE',
      color:     '#B8B5AB',
    },
    {
      rank:      'Gold',
      signings:  250,
      unlocked:  isGold,
      icon:      '🥇',
      badge:     RANK_BADGES['Gold'],
      reward:    '3 Free Months of Print PDF Utility!!',
      perk:      '3 Months FREE',
      color:     '#C9A84C',
    },
    {
      rank:      'Platinum',
      signings:  500,
      unlocked:  isPlatinum,
      icon:      '💎',
      badge:     RANK_BADGES['Platinum'],
      reward:    '6 Free Months of Print PDF Utility!!',
      perk:      '6 Months FREE',
      color:     '#5B7DB1',
    },
    {
      rank:      'Elite',
      signings:  1000,
      unlocked:  isElite,
      icon:      '👑',
      badge:     RANK_BADGES['Elite'],
      reward:    'FREE Print PDF Utility — Forever!! Professional dual-tray printing software, yours for life!!',
      perk:      'FREE Forever!!',
      color:     '#7B3FA0',
      isPremium: true,
    },
  ]

  // Next milestone
  const nextMilestone = MILESTONES.find(m => !m.unlocked)
  const progressTarget = nextMilestone?.signings || 1000
  const progressPct = Math.min((signings / progressTarget) * 100, 100)

  return (
    <>
      <style>{NOTARY_STYLES}</style>
      <a href="#rewards-main" className="lca-skip">Skip to rewards</a>
      <main id="rewards-main" tabIndex={-1}>
        <h1 className="page-title">My Rewards</h1>

        {/* Hero */}
        <div className="elite-rewards-hero">
          <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:20, flexWrap:'wrap' }}>
            <div style={{ width:56, height:56, borderRadius:'50%', background:'rgba(201,168,76,0.15)', border:'2px solid rgba(201,168,76,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, flexShrink:0 }}>★</div>
            <div>
              <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, color:'#fff', fontWeight:500 }}>LCA Rewards Program</div>
              <div style={{ fontSize:12, color:'rgba(255,255,255,0.5)', marginTop:2 }}>Complete signings to unlock exclusive perks — the more you work, the more you earn!!</div>
            </div>
          </div>

          {/* Progress */}
          <div style={{ background:'rgba(255,255,255,0.05)', borderRadius:12, padding:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
              <span style={{ fontSize:12, color:'rgba(255,255,255,0.6)' }}>Your progress{nextMilestone ? ` to ${nextMilestone.rank}` : ' — Maximum achieved!!'}</span>
              <span style={{ fontSize:12, color:'#C9A84C', fontWeight:600 }}>{signings} / {progressTarget} signings</span>
            </div>
            <div style={{ height:8, background:'rgba(255,255,255,0.1)', borderRadius:20 }}>
              <div style={{ height:8, borderRadius:20, background:'linear-gradient(to right,#C9A84C,#2D7A4F)', width:`${progressPct}%`, transition:'width 0.5s' }} />
            </div>
            {nextMilestone && (
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:8 }}>
                {nextMilestone.signings - signings} more signings to unlock {nextMilestone.perk} Print PDF Utility!!
              </div>
            )}
            {isElite && (
              <div style={{ fontSize:11, color:'#C9A84C', marginTop:8, fontWeight:500 }}>
                🎉 Congratulations!! You've reached Elite status — Print PDF Utility is yours FOREVER!!
              </div>
            )}
          </div>
        </div>

        {/* Milestones */}
        <h2 style={{ fontSize:16, fontWeight:500, color:'#0D1B3E', marginBottom:14 }}>Reward Milestones</h2>
        {MILESTONES.map(m => (
          <div key={m.rank} className={`elite-milestone ${m.unlocked ? 'unlocked' : 'locked'}`}>
            <img src={m.badge} alt={`${m.rank} badge`} style={{ width:48, height:48, objectFit:'contain', opacity: m.unlocked ? 1 : 0.3, flexShrink:0 }} />
            <div style={{ flex:1 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                <span style={{ fontWeight:600, fontSize:14, color:'#0D1B3E' }}>{m.rank} — {m.signings} signings</span>
                {m.unlocked
                  ? <span style={{ fontSize:10, background:'#EAF5EE', color:'#2D7A4F', padding:'2px 8px', borderRadius:10, fontWeight:600 }}>✓ UNLOCKED</span>
                  : <span style={{ fontSize:10, background:'#F8F7F4', color:'#B8B5AB', padding:'2px 8px', borderRadius:10 }}>{m.signings - signings} to go</span>
                }
                <span style={{ fontSize:10, background: m.isPremium ? '#7B3FA0' : '#0D1B3E', color:'#C9A84C', padding:'2px 8px', borderRadius:10, fontWeight:600 }}>
                  🖨️ {m.perk}
                </span>
              </div>
              <div style={{ fontSize:12, color:'#7A7870' }}>{m.reward}</div>
            </div>
          </div>
        ))}

        {/* Print PDF Utility — Claim Your Reward */}
        {isBronze ? (
          (() => {
            const tier = isElite    ? { name: 'Elite',    code: 'LCAELITE',    months: 'FREE Forever' }
                       : isPlatinum ? { name: 'Platinum', code: 'LCAPLATINUM', months: '6 months FREE' }
                       : isGold     ? { name: 'Gold',     code: 'LCAGOLD',     months: '3 months FREE' }
                       : isSilver   ? { name: 'Silver',   code: 'LCASILVER',   months: '2 months FREE' }
                       :              { name: 'Bronze',   code: 'LCABRONZE',   months: '1 month FREE' }
            // One-click redemption: the Stripe checkout opens with the notary's
            // reward code already applied — no copying or typing needed.
            const redeemLink = `https://buy.stripe.com/bJe5kD9Zedgj52k3Ioak008?prefilled_promo_code=${tier.code}`
            return (
              <div className="elite-download-card">
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
                  <div style={{ width:48, height:48, borderRadius:12, background:'rgba(201,168,76,0.15)', border:'1px solid rgba(201,168,76,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>🖨️</div>
                  <div>
                    <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:18, color:'#fff', fontWeight:500 }}>Print PDF Utility</div>
                    <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', marginTop:2 }}>
                      {tier.months} — your {tier.name} reward!!
                    </div>
                  </div>
                </div>
                <div style={{ background:'rgba(255,255,255,0.05)', borderRadius:10, padding:14, marginBottom:16, fontSize:12, color:'rgba(255,255,255,0.7)', lineHeight:1.7 }}>
                  🎯 <strong style={{ color:'#C9A84C' }}>What it does:</strong> Automatically prints letter (8.5" × 11") and legal (8.5" × 14") pages to the correct tray — no more manual sorting!! Works on Mac and Windows. Perfect for loan signing packages!!
                </div>

                {/* Reward code */}
                <div style={{ background:'rgba(201,168,76,0.1)', border:'1px dashed rgba(201,168,76,0.5)', borderRadius:10, padding:14, marginBottom:16 }}>
                  <div style={{ fontSize:10, color:'rgba(255,255,255,0.5)', letterSpacing:'0.08em', marginBottom:6 }}>YOUR {tier.name.toUpperCase()} REWARD CODE</div>
                  <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                    <span style={{ fontFamily:'monospace', fontSize:18, fontWeight:700, color:'#C9A84C', letterSpacing:'0.06em' }}>{tier.code}</span>
                    <button
                      onClick={() => { navigator.clipboard.writeText(tier.code).then(() => toast.success('Code copied!!')).catch(() => toast.error('Could not copy — please select and copy the code manually')) }}
                      style={{ background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', color:'#C9A84C', border:'1px solid rgba(201,168,76,0.4)', borderRadius:8, padding:'5px 12px', fontSize:11, fontWeight:700, cursor:'pointer' }}>
                      📋 Copy
                    </button>
                  </div>
                </div>

                {/* How to claim */}
                <div style={{ fontSize:12, color:'rgba(255,255,255,0.7)', lineHeight:1.9, marginBottom:16 }}>
                  <strong style={{ color:'#C9A84C' }}>How to claim your reward:</strong><br/>
                  1️⃣ Click Redeem below — checkout opens with your code <strong style={{ color:'#C9A84C' }}>{tier.code}</strong> already applied ({tier.months.toLowerCase()}, on us!!)<br/>
                  2️⃣ Subscribe with your email address<br/>
                  3️⃣ Download the app at theprintpdf.com — it activates automatically with that same email
                </div>

                <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                  <a href={redeemLink} target="_blank" rel="noreferrer" className="elite-download-btn windows" aria-label={`Redeem your ${tier.name} reward — checkout opens with code ${tier.code} pre-applied`}>
                    🎁 Redeem {tier.months} →
                  </a>
                  <a href="https://theprintpdf.com" target="_blank" rel="noreferrer" style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(201,168,76,0.4)', color:'#C9A84C', borderRadius:10, padding:'10px 18px', fontSize:13, fontWeight:700, textDecoration:'none' }} aria-label="Learn more and download the app at theprintpdf.com">
                    🖨️ Get the App →
                  </a>
                </div>
                <div style={{ fontSize:10, color:'rgba(255,255,255,0.3)', marginTop:12 }}>
                  Exclusively for LCA Notaries · Veteran & Hispanic Family Owned Software · theprintpdf.com
                </div>
              </div>
            )
          })()
        ) : (
          <div className="elite-locked-overlay">
            <div style={{ fontSize:40, marginBottom:12 }}>🔒</div>
            <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, color:'#0D1B3E', marginBottom:8 }}>Print PDF Utility — Unlock at 50 Signings</div>
            <div style={{ fontSize:13, color:'#7A7870', marginBottom:16, maxWidth:400, margin:'0 auto 16px' }}>
              Complete {50 - signings} more signing{50 - signings !== 1 ? 's' : ''} to unlock your first FREE month of our professional printing software!!
            </div>
            <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
              <div style={{ background:'#F8F7F4', border:'1px solid #E8E6DF', borderRadius:10, padding:'12px 20px', fontSize:12, color:'#B8B5AB', textAlign:'center' }}>🖨️ Reward Code<div style={{ fontSize:10, marginTop:2 }}>Locked</div></div>
              <div style={{ background:'#F8F7F4', border:'1px solid #E8E6DF', borderRadius:10, padding:'12px 20px', fontSize:12, color:'#B8B5AB', textAlign:'center' }}>⬇ Download<div style={{ fontSize:10, marginTop:2 }}>Locked</div></div>
            </div>
          </div>
        )}
      </main>
    </>
  )
}

/* ── EARNINGS ── */

function Earnings() {
  const { user } = useAuth()
  const [jobs,       setJobs]       = useState([])
  const [loading,    setLoading]    = useState(true)
  const [activeMonth,setActiveMonth]= useState(new Date().getMonth())
  const [editingId,  setEditingId]  = useState(null)
  const [editForm,   setEditForm]   = useState({})
  const [saving,     setSaving]     = useState(false)
  const [activeTab,  setActiveTab]  = useState('pl') // 'pl' or 'summary'

  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December']
  const IRS_MILEAGE = 0.67
  const PRINT_COST  = 0.05
  const TAX_RATE    = 0.30

  useEffect(() => {
    if (!user) return
    supabase.from('jobs').select('*')
      .eq('notary_id', user.id)
      .in('status', ['completed','scanbacks_uploaded','client_approved','payment_received','notary_paid','closed'])
      .order('scheduled_at', { ascending: true })
      .then(({ data }) => { setJobs(safeParseJobs(data)); setLoading(false) })
  }, [user])

  // ── P&L Calculations (Jacob's formulas) ──
  function calcRow(j) {
    const fee          = j.notary_pay        || 0
    const miles        = j.round_trip_miles  || 0
    const pages        = j.printed_pages     || 0
    const hours        = j.hours_worked      || 0
    const mileageCost  = Math.round(miles * IRS_MILEAGE * 100) / 100
    const printCost    = Math.round(pages * PRINT_COST  * 100) / 100
    const totalCost    = Math.round((mileageCost + printCost) * 100) / 100
    const preTaxProfit = Math.round((fee - totalCost) * 100) / 100
    const taxWithhold  = Math.round(preTaxProfit * TAX_RATE * 100) / 100
    const totalProfit  = Math.round((preTaxProfit - taxWithhold) * 100) / 100
    const hourlyRate   = hours > 0 ? Math.round((totalProfit / hours) * 100) / 100 : 0
    return { fee, miles, pages, hours, mileageCost, printCost, totalCost, preTaxProfit, taxWithhold, totalProfit, hourlyRate }
  }

  // Filter by month
  const monthJobs = jobs.filter(j => {
    const d = j.scheduled_at ? new Date(j.scheduled_at) : new Date(j.created_at)
    return d.getMonth() === activeMonth
  })

  // Month totals
  const monthTotals = monthJobs.reduce((acc, j) => {
    const c = calcRow(j)
    return {
      fee:          acc.fee          + c.fee,
      miles:        acc.miles        + c.miles,
      pages:        acc.pages        + c.pages,
      hours:        acc.hours        + c.hours,
      mileageCost:  acc.mileageCost  + c.mileageCost,
      printCost:    acc.printCost    + c.printCost,
      totalCost:    acc.totalCost    + c.totalCost,
      preTaxProfit: acc.preTaxProfit + c.preTaxProfit,
      taxWithhold:  acc.taxWithhold  + c.taxWithhold,
      totalProfit:  acc.totalProfit  + c.totalProfit,
    }
  }, { fee:0,miles:0,pages:0,hours:0,mileageCost:0,printCost:0,totalCost:0,preTaxProfit:0,taxWithhold:0,totalProfit:0 })

  // Year totals
  const yearTotals = jobs.reduce((acc, j) => {
    const c = calcRow(j)
    return {
      fee:          acc.fee          + c.fee,
      signings:     acc.signings     + 1,
      miles:        acc.miles        + c.miles,
      totalCost:    acc.totalCost    + c.totalCost,
      preTaxProfit: acc.preTaxProfit + c.preTaxProfit,
      taxWithhold:  acc.taxWithhold  + c.taxWithhold,
      totalProfit:  acc.totalProfit  + c.totalProfit,
      hours:        acc.hours        + c.hours,
    }
  }, { fee:0,signings:0,miles:0,totalCost:0,preTaxProfit:0,taxWithhold:0,totalProfit:0,hours:0 })

  // Monthly summary for all months
  const monthlySummary = MONTHS.map((month, idx) => {
    const mJobs = jobs.filter(j => {
      const d = j.scheduled_at ? new Date(j.scheduled_at) : new Date(j.created_at)
      return d.getMonth() === idx
    })
    const totals = mJobs.reduce((acc, j) => {
      const c = calcRow(j)
      return { fee: acc.fee+c.fee, profit: acc.profit+c.totalProfit, tax: acc.tax+c.taxWithhold, signings: acc.signings+1 }
    }, { fee:0, profit:0, tax:0, signings:0 })
    return { month, ...totals }
  })

  function startEdit(j) {
    setEditingId(j.id)
    setEditForm({
      signing_company:   j.signing_company   || '',
      file_number:       j.file_number       || '',
      round_trip_miles:  j.round_trip_miles  || '',
      printed_pages:     j.printed_pages     || '',
      hours_worked:      j.hours_worked      || '',
      pay_received_date: j.pay_received_date || '',
      notary_notes:      j.notary_notes      || '',
    })
  }

  // 💰 Export YTD earnings to CSV for tax preparation
  function exportTaxCSV() {
    if (jobs.length === 0) {
      toast.error('No jobs to export yet!!')
      return
    }
    const year = new Date().getFullYear()
    const rows = [
      // Header row
      ['Job #','Date','Signing Company','File #','Service','Borrower','Address','Gross Fee','Miles','Mileage Cost ($0.67/mi)','Pages','Print Cost ($0.05/pg)','Hours','Total Expenses','Pre-Tax Profit','Tax Withhold (30%)','Take-Home','Pay Date','Status']
    ]
    jobs.forEach(j => {
      const c = calcRow(j)
      rows.push([
        j.job_number || '',
        j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : '',
        j.signing_company || '',
        j.file_number || '',
        j.signing_type || '',
        j.borrower_name || '',
        `${j.signing_address || ''} ${j.signing_city || ''} ${j.signing_state || ''}`.trim(),
        c.fee.toFixed(2),
        c.miles,
        c.mileageCost.toFixed(2),
        c.pages,
        c.printCost.toFixed(2),
        c.hours,
        c.totalCost.toFixed(2),
        c.preTaxProfit.toFixed(2),
        c.taxWithhold.toFixed(2),
        c.totalProfit.toFixed(2),
        j.pay_received_date || '',
        j.status || ''
      ])
    })
    // Add totals row
    rows.push([])
    rows.push(['YEAR TOTALS', '', '', '', '', '', '', yearTotals.fee.toFixed(2), yearTotals.miles, yearTotals.mileageCost.toFixed(2), yearTotals.pages, yearTotals.printCost.toFixed(2), yearTotals.hours, yearTotals.totalCost.toFixed(2), yearTotals.preTaxProfit.toFixed(2), yearTotals.taxWithhold.toFixed(2), yearTotals.totalProfit.toFixed(2), '', ''])
    // Convert to CSV
    const csv = rows.map(row => row.map(cell => {
      const str = String(cell)
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
    }).join(',')).join('\n')
    // Trigger download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `LCA_Tax_Export_${year}.csv`
    link.click()
    URL.revokeObjectURL(url)
    toast.success(`📊 Exported ${jobs.length} signings for ${year} taxes!!`)
  }

  async function saveEdit(jobId) {
    setSaving(true)
    try {
      await supabase.from('jobs').update({
        signing_company:   editForm.signing_company   || null,
        file_number:       editForm.file_number       || null,
        round_trip_miles:  parseFloat(editForm.round_trip_miles) || 0,
        printed_pages:     parseInt(editForm.printed_pages)      || 0,
        hours_worked:      parseFloat(editForm.hours_worked)     || 0,
        pay_received_date: editForm.pay_received_date            || null,
        notary_notes:      editForm.notary_notes                 || null,
      }).eq('id', jobId)
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, ...editForm,
        round_trip_miles: parseFloat(editForm.round_trip_miles) || 0,
        printed_pages:    parseInt(editForm.printed_pages)      || 0,
        hours_worked:     parseFloat(editForm.hours_worked)     || 0,
      } : j))
      toast.success('P&L updated!!')
      setEditingId(null)
    } catch (err) {
      toast.error(err.message)
    } finally {
      setSaving(false)
    }
  }

  const PL_STYLES = `
    .pl-month-tabs { display:flex; gap:4px; flex-wrap:wrap; margin-bottom:16px; }
    .pl-month-tab { padding:4px 10px; border-radius:20px; font-size:11px; font-weight:500; cursor:pointer; border:1px solid #E8E6DF; background:#F8F7F4; color:#7A7870; transition:all 0.15s; }
    .pl-month-tab.active { background:#0D1B3E; color:#C9A84C; border-color:#0D1B3E; }
    .pl-month-tab.has-data { border-color:rgba(45,122,79,0.4); color:#2D7A4F; }
    .pl-month-tab.active.has-data { background:#2D7A4F; border-color:#2D7A4F; color:#fff; }
    .pl-table-wrap { overflow-x:auto; -webkit-overflow-scrolling:touch; font-size:12px; }
    .pl-table { width:100%; border-collapse:collapse; min-width:900px; }
    .pl-table th { background:#0D1B3E; color:#C9A84C; padding:8px 10px; text-align:left; font-size:10px; font-weight:600; letter-spacing:0.06em; white-space:nowrap; position:sticky; top:0; }
    .pl-table td { padding:8px 10px; border-bottom:1px solid #F0EEE8; color:#0D1B3E; white-space:nowrap; }
    .pl-table tr:hover td { background:#FDF6E3; }
    .pl-table tr.editing td { background:#EBF4FF; }
    .pl-table tfoot td { background:#F8F7F4; font-weight:600; border-top:2px solid #C9A84C; }
    .pl-edit-input { padding:4px 8px; border:1px solid #C9A84C; border-radius:6px; font-size:11px; width:100%; min-width:80px; }
    .pl-profit { color:#2D7A4F; font-weight:600; }
    .pl-cost { color:#E24B4A; }
    .pl-tax { color:#C9A84C; font-weight:500; }
    .pl-summary-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:20px; }
    @media(max-width:700px){ .pl-summary-grid { grid-template-columns:1fr 1fr; } }
    .pl-summary-month { display:grid; grid-template-columns:repeat(4,1fr); gap:0; border:1px solid #E8E6DF; border-radius:10px; overflow:hidden; margin-bottom:8px; }
    .pl-summary-cell { padding:10px 12px; border-right:1px solid #E8E6DF; font-size:12px; }
    .pl-summary-cell:last-child { border-right:none; }
    .pl-summary-month-name { font-weight:600; color:#0D1B3E; }
    .pl-tabs { display:flex; gap:8px; margin-bottom:20px; }
    .pl-tab { padding:6px 18px; border-radius:20px; font-size:12px; font-weight:500; cursor:pointer; border:1px solid #E8E6DF; background:#F8F7F4; color:#7A7870; transition:all 0.15s; }
    .pl-tab.active { background:#0D1B3E; color:#C9A84C; border-color:#0D1B3E; }
  `

  return (
    <>
      <style>{NOTARY_STYLES}{PL_STYLES}</style>
      <a href="#earnings-main" className="lca-skip">Skip to earnings</a>
      <main id="earnings-main" tabIndex={-1}>
        <h1 className="page-title">P&L Tracker</h1>
        <p className="page-sub">Track every signing, expense, and your real take-home profit!!</p>

        {/* 📊 EXPORT FOR TAXES — always visible, disabled when no jobs */}
        <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
          <button
            onClick={exportTaxCSV}
            disabled={jobs.length === 0}
            title={jobs.length === 0 ? 'No signings yet — your CSV will be available once you complete signings' : 'Download your YTD tax CSV'}
            style={{
              background: jobs.length === 0 ? '#E8E6DF' : 'linear-gradient(135deg,#0D1B3E,#1A3A5E)',
              color: jobs.length === 0 ? '#7A7870' : '#C9A84C',
              border: jobs.length === 0 ? '1px solid #D8D4C8' : '1px solid #C9A84C',
              borderRadius:8,
              padding:'10px 18px',
              fontSize:13,
              fontWeight:700,
              cursor: jobs.length === 0 ? 'not-allowed' : 'pointer',
              fontFamily:'inherit',
              letterSpacing:'0.04em',
              opacity: jobs.length === 0 ? 0.7 : 1,
            }}
          >
            📊 Export CSV for Taxes ({new Date().getFullYear()})
          </button>
        </div>

        {/* YEAR KPI STRIP */}
        <div className="pl-summary-grid">
          <div className="stat-card"><div className="stat-label">Total Signings</div><div className="stat-value">{yearTotals.signings}</div><div className="stat-sub">this year</div></div>
          <div className="stat-card"><div className="stat-label">Gross Fees</div><div className="stat-value gold">${yearTotals.fee.toLocaleString()}</div><div className="stat-sub">before expenses</div></div>
          <div className="stat-card"><div className="stat-label">Set Aside for Tax</div><div className="stat-value" style={{ color:'#C9A84C' }}>${Math.round(yearTotals.taxWithhold).toLocaleString()}</div><div className="stat-sub">30% of profit</div></div>
          <div className="stat-card"><div className="stat-label">Take-Home Profit</div><div className="stat-value" style={{ color:'#2D7A4F' }}>${Math.round(yearTotals.totalProfit).toLocaleString()}</div><div className="stat-sub">after tax & expenses</div></div>
        </div>

        {/* TAX ALERT */}
        {yearTotals.taxWithhold > 0 && (
          <div style={{ background:'#FDF6E3', border:'1px solid rgba(201,168,76,0.3)', borderRadius:12, padding:'12px 18px', marginBottom:20, display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:20 }}>💡</span>
            <div>
              <div style={{ fontSize:13, fontWeight:500, color:'#8A5C00' }}>Tax Reminder — Set aside ${Math.round(yearTotals.taxWithhold).toLocaleString()} for taxes!!</div>
              <div style={{ fontSize:11, color:'#B8A060' }}>Based on 30% withholding rate · Total miles driven: {yearTotals.miles} · IRS rate: $0.67/mile</div>
            </div>
          </div>
        )}

        {/* VIEW TABS */}
        <div className="pl-tabs">
          <button className={`pl-tab${activeTab==='pl'?' active':''}`} onClick={() => setActiveTab('pl')} type="button">📋 Monthly P&L</button>
          <button className={`pl-tab${activeTab==='summary'?' active':''}`} onClick={() => setActiveTab('summary')} type="button">📊 Year Summary</button>
        </div>

        {/* ── MONTHLY P&L VIEW ── */}
        {activeTab === 'pl' && (
          <>
            {/* Month tabs */}
            <div className="pl-month-tabs">
              {MONTHS.map((m, i) => {
                const hasData = jobs.some(j => {
                  const d = j.scheduled_at ? new Date(j.scheduled_at) : new Date(j.created_at)
                  return d.getMonth() === i
                })
                return (
                  <button key={m} className={`pl-month-tab${activeMonth===i?' active':''} ${hasData?'has-data':''}`} onClick={() => setActiveMonth(i)} type="button">
                    {m.slice(0,3)} {hasData && activeMonth!==i ? '●' : ''}
                  </button>
                )
              })}
            </div>

            <div style={{ fontSize:14, fontWeight:500, color:'#0D1B3E', marginBottom:12 }}>
              {MONTHS[activeMonth]} — {monthJobs.length} signing{monthJobs.length!==1?'s':''}
              <span style={{ fontSize:11, color:'#7A7870', marginLeft:12 }}>Click <span style={{ color:'#C9A84C', cursor:'pointer', textDecoration:'underline' }} onClick={() => document.querySelector('.btn.btn-outline.btn-sm')?.click()}>✏️</span> to add miles, printed pages, and worked hours</span>
            </div>

            {loading && <div style={{ textAlign:'center', padding:40 }}><div className="spinner" style={{ margin:'auto' }} /></div>}

            {!loading && monthJobs.length === 0 && (
              <div style={{ textAlign:'center', padding:40, color:'#B8B5AB' }}>
                <div style={{ fontSize:32, marginBottom:12 }}>📋</div>
                <div>No signings in {MONTHS[activeMonth]} yet!!</div>
              </div>
            )}

            {!loading && monthJobs.length > 0 && (
              <div className="section-card" style={{ padding:0 }}>
                <div className="pl-table-wrap">
                  <table className="pl-table">
                    <thead>
                      <tr>
                        <th>Job #</th>
                        <th>Date</th>
                        <th>Signing Co.</th>
                        <th>Signer</th>
                        <th>File #</th>
                        <th>Miles R/T</th>
                        <th>Fee</th>
                        <th>Mileage $</th>
                        <th>Pages</th>
                        <th>Print $</th>
                        <th>Total Cost</th>
                        <th>PreTax Profit</th>
                        <th>Tax (30%)</th>
                        <th>Hours</th>
                        <th>$/hr</th>
                        <th>Take-Home</th>
                        <th>Paid Date</th>
                        <th>Notes</th>
                        <th>Edit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthJobs.map(j => {
                        const c      = calcRow(j)
                        const isEdit = editingId === j.id
                        return (
                          <tr key={j.id} className={isEdit ? 'editing' : ''}>
                            <td style={{ fontWeight:600, color:'#9A7A30' }}>{j.job_number}</td>
                            <td>{j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : '—'}</td>
                            <td>
                              {isEdit
                                ? <input className="pl-edit-input" value={editForm.signing_company} onChange={e => setEditForm(f => ({ ...f, signing_company: e.target.value }))} placeholder="LCA" />
                                : (j.signing_company || <span style={{ color:'#B8B5AB' }}>LCA</span>)
                              }
                            </td>
                            <td>{j.borrower_name || '—'}</td>
                            <td>
                              {isEdit
                                ? <input className="pl-edit-input" value={editForm.file_number} onChange={e => setEditForm(f => ({ ...f, file_number: e.target.value }))} placeholder="File #" />
                                : (j.file_number || '—')
                              }
                            </td>
                            <td>
                              {isEdit
                                ? <input className="pl-edit-input" type="number" value={editForm.round_trip_miles} onChange={e => setEditForm(f => ({ ...f, round_trip_miles: e.target.value }))} placeholder="0" style={{ width:60 }} />
                                : c.miles
                              }
                            </td>
                            <td style={{ color:'#2D7A4F', fontWeight:500 }}>${c.fee}</td>
                            <td className="pl-cost">${c.mileageCost}</td>
                            <td>
                              {isEdit
                                ? <input className="pl-edit-input" type="number" value={editForm.printed_pages} onChange={e => setEditForm(f => ({ ...f, printed_pages: e.target.value }))} placeholder="0" style={{ width:60 }} />
                                : c.pages
                              }
                            </td>
                            <td className="pl-cost">${c.printCost}</td>
                            <td className="pl-cost">${c.totalCost}</td>
                            <td className="pl-profit">${c.preTaxProfit}</td>
                            <td className="pl-tax">${c.taxWithhold}</td>
                            <td>
                              {isEdit
                                ? <input className="pl-edit-input" type="number" value={editForm.hours_worked} onChange={e => setEditForm(f => ({ ...f, hours_worked: e.target.value }))} placeholder="0" style={{ width:50 }} />
                                : (c.hours || '—')
                              }
                            </td>
                            <td>{c.hourlyRate > 0 ? `$${c.hourlyRate}` : '—'}</td>
                            <td className="pl-profit" style={{ fontSize:13, fontWeight:700 }}>${c.totalProfit}</td>
                            <td>
                              {isEdit
                                ? <input className="pl-edit-input" type="date" value={editForm.pay_received_date} onChange={e => setEditForm(f => ({ ...f, pay_received_date: e.target.value }))} />
                                : (j.pay_received_date ? new Date(j.pay_received_date).toLocaleDateString() : '—')
                              }
                            </td>
                            <td>
                              {isEdit
                                ? <input className="pl-edit-input" value={editForm.notary_notes} onChange={e => setEditForm(f => ({ ...f, notary_notes: e.target.value }))} placeholder="Notes..." style={{ width:120 }} />
                                : (j.notary_notes || '—')
                              }
                            </td>
                            <td>
                              {isEdit ? (
                                <div style={{ display:'flex', gap:4 }}>
                                  <button className="btn btn-primary btn-sm" onClick={() => saveEdit(j.id)} disabled={saving} style={{ fontSize:10, padding:'3px 8px' }}>
                                    {saving ? '...' : '✓ Save'}
                                  </button>
                                  <button className="btn btn-outline btn-sm" onClick={() => setEditingId(null)} style={{ fontSize:10, padding:'3px 8px' }}>✕</button>
                                </div>
                              ) : (
                                <button className="btn btn-outline btn-sm" onClick={() => startEdit(j)} style={{ fontSize:10, padding:'3px 8px' }}>✏️</button>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={6} style={{ color:'#0D1B3E' }}>TOTALS — {monthJobs.length} signings</td>
                        <td style={{ color:'#2D7A4F' }}>${Math.round(monthTotals.fee)}</td>
                        <td style={{ color:'#E24B4A' }}>${Math.round(monthTotals.mileageCost)}</td>
                        <td>{monthTotals.pages}</td>
                        <td style={{ color:'#E24B4A' }}>${Math.round(monthTotals.printCost)}</td>
                        <td style={{ color:'#E24B4A' }}>${Math.round(monthTotals.totalCost)}</td>
                        <td style={{ color:'#2D7A4F' }}>${Math.round(monthTotals.preTaxProfit)}</td>
                        <td style={{ color:'#C9A84C' }}>${Math.round(monthTotals.taxWithhold)}</td>
                        <td>{Math.round(monthTotals.hours * 10) / 10}h</td>
                        <td>—</td>
                        <td style={{ color:'#2D7A4F', fontSize:14 }}>${Math.round(monthTotals.totalProfit)}</td>
                        <td colSpan={3} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── YEAR SUMMARY VIEW ── */}
        {activeTab === 'summary' && (
          <>
            <div style={{ fontSize:12, color:'#7A7870', marginBottom:16 }}>Full year breakdown — every month at a glance!!</div>
            <div className="section-card" style={{ padding:0 }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Signings</th>
                    <th>Gross Fees</th>
                    <th>Expenses</th>
                    <th>PreTax Profit</th>
                    <th>Tax Set Aside</th>
                    <th>Take-Home</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlySummary.map((m, i) => {
                    const mJobs    = jobs.filter(j => { const d = j.scheduled_at ? new Date(j.scheduled_at) : new Date(j.created_at); return d.getMonth() === i })
                    const expenses = mJobs.reduce((a, j) => { const c = calcRow(j); return a + c.totalCost }, 0)
                    const preTax   = mJobs.reduce((a, j) => { const c = calcRow(j); return a + c.preTaxProfit }, 0)
                    return (
                      <tr key={m.month} style={{ background: m.signings > 0 ? 'transparent' : '#FAFAF8', opacity: m.signings > 0 ? 1 : 0.5 }} onClick={() => { setActiveMonth(i); setActiveTab('pl') }} style={{ cursor:'pointer' }}>
                        <td style={{ fontWeight:500 }}>{m.month}</td>
                        <td>{m.signings || '—'}</td>
                        <td style={{ color: m.fee > 0 ? '#2D7A4F' : '#B8B5AB' }}>{m.fee > 0 ? `$${Math.round(m.fee)}` : '—'}</td>
                        <td style={{ color: expenses > 0 ? '#E24B4A' : '#B8B5AB' }}>{expenses > 0 ? `$${Math.round(expenses)}` : '—'}</td>
                        <td style={{ color: preTax > 0 ? '#0D1B3E' : '#B8B5AB' }}>{preTax > 0 ? `$${Math.round(preTax)}` : '—'}</td>
                        <td style={{ color: m.tax > 0 ? '#C9A84C' : '#B8B5AB' }}>{m.tax > 0 ? `$${Math.round(m.tax)}` : '—'}</td>
                        <td style={{ color: m.profit > 0 ? '#2D7A4F' : '#B8B5AB', fontWeight: m.profit > 0 ? 600 : 400 }}>{m.profit > 0 ? `$${Math.round(m.profit)}` : '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ fontWeight:700 }}>YEAR TOTAL</td>
                    <td style={{ fontWeight:700 }}>{yearTotals.signings}</td>
                    <td style={{ fontWeight:700, color:'#2D7A4F' }}>${Math.round(yearTotals.fee).toLocaleString()}</td>
                    <td style={{ fontWeight:700, color:'#E24B4A' }}>${Math.round(yearTotals.totalCost).toLocaleString()}</td>
                    <td style={{ fontWeight:700 }}>${Math.round(yearTotals.preTaxProfit).toLocaleString()}</td>
                    <td style={{ fontWeight:700, color:'#C9A84C' }}>${Math.round(yearTotals.taxWithhold).toLocaleString()}</td>
                    <td style={{ fontWeight:700, color:'#2D7A4F', fontSize:14 }}>${Math.round(yearTotals.totalProfit).toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div style={{ fontSize:11, color:'#B8B5AB', marginTop:8, textAlign:'center' }}>Click any month row to see the detailed P&L breakdown!!</div>
          </>
        )}
      </main>
    </>
  )
}

/* ── PROFILE ── */
/* ── MFA SECURITY SECTION ── */

function MFASection() {
  const [factors,    setFactors]    = useState([])
  const [loading,    setLoading]    = useState(true)
  const [enrolling,  setEnrolling]  = useState(false)
  const [qrCode,     setQrCode]     = useState(null)
  const [secret,     setSecret]     = useState(null)
  const [factorId,   setFactorId]   = useState(null)
  const [verifyCode, setVerifyCode] = useState('')
  const [verifying,  setVerifying]  = useState(false)
  const [unenrolling, setUnenrolling] = useState(false)

  useEffect(() => { loadFactors() }, [])

  async function loadFactors() {
    setLoading(true)
    try {
            const { data } = await supabase.auth.mfa.listFactors()
      setFactors(data?.totp || [])
    } catch (err) { console.error('MFA load error:', err) }
    finally { setLoading(false) }
  }

  async function startEnroll() {
    setEnrolling(true)
    try {
            const { data, error } = await supabase.auth.mfa.enroll({ factorType:'totp', issuer:'LCA Platform', friendlyName:'LCA Authenticator' })
      if (error) throw error
      setQrCode(data.totp.qr_code)
      setSecret(data.totp.secret)
      setFactorId(data.id)
    } catch (err) { toast.error(err.message) }
    finally { setEnrolling(false) }
  }

  async function verifyEnroll() {
    setVerifying(true)
    try {
            const { data: challengeData } = await supabase.auth.mfa.challenge({ factorId })
      const { error } = await supabase.auth.mfa.verify({ factorId, challengeId: challengeData.id, code: verifyCode.trim() })
      if (error) throw error
      toast.success('🔐 Two-Factor Authentication enabled successfully!!')
      setQrCode(null); setSecret(null); setFactorId(null); setVerifyCode('')
      loadFactors()
    } catch (err) { toast.error('Invalid code — please try again!!') }
    finally { setVerifying(false) }
  }

  async function unenroll(factorId) {
    if (!window.confirm('Are you sure you want to disable Two-Factor Authentication?? This will make your account less secure!!')) return
    setUnenrolling(true)
    try {
            const { error } = await supabase.auth.mfa.unenroll({ factorId })
      if (error) throw error
      toast.success('Two-Factor Authentication disabled!!')
      loadFactors()
    } catch (err) { toast.error(err.message) }
    finally { setUnenrolling(false) }
  }

  const isEnabled = factors.some(f => f.status === 'verified')

  return (
    <section className="section-card" style={{ marginTop:16 }}>
      <div className="section-header">
        <h2 className="section-title">🔐 Two-Factor Authentication</h2>
        {isEnabled && <span style={{ fontSize:11, background:'#EAF5EE', color:'#2D7A4F', padding:'3px 10px', borderRadius:10, fontWeight:600 }}>✓ Enabled</span>}
      </div>
      <div className="section-body">
        {loading && <div style={{ color:'#B8B5AB', fontSize:13 }}>Loading security settings...</div>}

        {!loading && !isEnabled && !qrCode && (
          <div>
            <p style={{ fontSize:13, color:'#7A7870', marginBottom:16 }}>
              Add an extra layer of security!! With 2FA enabled you'll need your phone to log in — protecting your account even if your password is stolen!!
            </p>
            <button onClick={startEnroll} disabled={enrolling} className="btn btn-primary">
              {enrolling ? 'Setting up...' : '🔐 Enable Two-Factor Authentication'}
            </button>
          </div>
        )}

        {!loading && qrCode && (
          <div>
            <p style={{ fontSize:13, color:'#7A7870', marginBottom:16 }}>
              <strong>Step 1:</strong> Download <strong>Google Authenticator</strong> or <strong>Authy</strong> on your phone!!<br/>
              <strong>Step 2:</strong> Scan the QR code below!!<br/>
              <strong>Step 3:</strong> Enter the 6-digit code to confirm!!
            </p>
            <div style={{ background:'#fff', border:'2px solid #E8E6DF', borderRadius:12, padding:16, display:'inline-block', marginBottom:16 }}>
              <img src={qrCode} alt="QR Code for 2FA setup" style={{ width:200, height:200 }} />
            </div>
            <div style={{ fontSize:11, color:'#7A7870', marginBottom:12 }}>
              Can't scan?? Enter this code manually: <code style={{ background:'#F8F7F4', padding:'2px 6px', borderRadius:4, fontSize:12 }}>{secret}</code>
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={verifyCode}
                onChange={e => setVerifyCode(e.target.value.replace(/\D/g,''))}
                placeholder="000000"
                style={{ width:140, padding:'10px 14px', border:'2px solid #E0D9CC', borderRadius:8, fontSize:20, textAlign:'center', letterSpacing:'0.2em', fontFamily:'monospace' }}
              />
              <button onClick={verifyEnroll} disabled={verifying || verifyCode.length !== 6} className="btn btn-primary">
                {verifying ? 'Verifying...' : 'Verify & Activate'}
              </button>
              <button onClick={() => { setQrCode(null); setSecret(null); setFactorId(null) }} className="btn btn-outline">Cancel</button>
            </div>
          </div>
        )}

        {!loading && isEnabled && (
          <div>
            <p style={{ fontSize:13, color:'#2D7A4F', marginBottom:16 }}>
              ✅ Your account is protected with Two-Factor Authentication!! You'll need your authenticator app every time you log in!!
            </p>
            <button onClick={() => unenroll(factors.find(f => f.status === 'verified')?.id)} disabled={unenrolling} className="btn btn-outline" style={{ color:'#E24B4A', borderColor:'#E24B4A', fontSize:12 }}>
              {unenrolling ? 'Disabling...' : 'Disable Two-Factor Authentication'}
            </button>
          </div>
        )}
      </div>
    </section>
  )
}


function NotaryProfile() {
  const { profile, user, refreshProfile } = useAuth()
  const [notary,    setNotary]    = useState(null)
  const [creds,     setCreds]     = useState([])
  const [uploading, setUploading] = useState(false)
  const [saving,    setSaving]    = useState(false)
  const [showBioPreview, setShowBioPreview] = useState(false)
  const [form, setForm] = useState({
    full_name: '', phone: '', address: '', city: '', state: '', zip: '', bio: '',
  })

  useEffect(() => {
    if (!user) return
    supabase.from('notaries').select('*').eq('id', user.id).single()
      .then(({ data }) => setNotary(data))
    supabase.from('notary_docs').select('doc_type, status').eq('notary_id', user.id)
      .then(({ data }) => setCreds(data || []))
  }, [user])

  useEffect(() => {
    if (profile) setForm({
      full_name: profile.full_name || '',
      phone:     profile.phone     || '',
      address:   profile.address   || '',
      city:      profile.city      || '',
      state:     profile.state     || '',
      zip:       profile.zip       || '',
      bio:       profile.bio       || '',
    })
  }, [profile])

  const hasApproved = (type) => creds.some(c => c.doc_type === type && c.status === 'approved')

  // Build glance badges from registration data
  function buildGlanceBadges() {
    const badges = []
    if (notary?.commission_state) badges.push({ icon:'📜', label:`Commissioned in ${notary.commission_state}` })
    if (hasApproved('eo_insurance')) badges.push({ icon:'🛡️', label:`Bonded & Insured${notary?.eo_coverage ? ` — ${notary.eo_coverage}` : ''}` })
    if (hasApproved('background_check')) badges.push({ icon:'🔍', label:'Background Checked' })
    if (notary?.years_experience) badges.push({ icon:'⭐', label:`${notary.years_experience} Years Experience` })
    if (notary?.ron_certified) badges.push({ icon:'💻', label:'RON Certified' })
    if (notary?.languages?.length > 0) {
      const langs = notary.languages.filter(l => l !== 'English')
      if (langs.length > 0) badges.push({ icon:'🗣️', label:`Bilingual: ${langs.join(', ')}` })
    }
    if (notary?.nsa_certifications?.length > 0) {
      notary.nsa_certifications.forEach(cert => {
        const short = cert.replace('National Notary Association', 'NNA').replace('Loan Signing System', 'LSS')
        badges.push({ icon:'🏅', label:short })
      })
    }
    return badges
  }

  async function uploadHeadshot(e) {
    const file = e.target.files[0]
    if (!file || !user) return
    setUploading(true)
    try {
      const ext  = file.name.split('.').pop()
      const path = `headshots/${user.id}-${Date.now()}.${ext}`
      const { error } = await supabase.storage.from('credentials').upload(path, file, { upsert: true })
      if (error) throw error
      const { data: { publicUrl } } = supabase.storage.from('credentials').getPublicUrl(path)
      await supabase.from('profiles').update({ headshot_url: publicUrl }).eq('id', user.id)
      await refreshProfile()
      toast.success('Headshot uploaded!!')
    } catch (err) { toast.error(err.message) }
    finally { setUploading(false) }
  }

  async function save(e) {
    e.preventDefault()
    setSaving(true)
    try {
      await supabase.from('profiles').update(form).eq('id', user.id)
      await refreshProfile()
      toast.success('Profile updated!!')
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  return (
    <>
      <style>{NOTARY_STYLES}</style>
      <a href="#profile-main" className="lca-skip">Skip to profile</a>
      <main id="profile-main" tabIndex={-1}>
        <h1 className="page-title">My Profile</h1>

        {/* ── 1. VENDOR NUMBER HERO ── */}
        {notary?.vendor_number && (
          <div style={{ background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', borderRadius:16, padding:20, marginBottom:16, display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
            {/* Icon — Founders Badge or price tag */}
            <div title={notary.is_founder ? 'You are an LCA Founding Notary — one of the first 50!!' : 'Your unique LCA Vendor Number for all assignments'} style={{ cursor:'help', flexShrink:0 }}>
              {notary.is_founder
                ? <img src="https://utrvslwjxcvnctjfomit.supabase.co/storage/v1/object/public/credentials/founders-badge.png" alt="Founders Badge" style={{ width:52, height:52, objectFit:'contain' }} />
                : <div style={{ width:52, height:52, borderRadius:'50%', background:'rgba(201,168,76,0.15)', border:'2px solid rgba(201,168,76,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>🏷️</div>}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', letterSpacing:'0.08em', marginBottom:3 }}
                title="Use this number for all LCA assignments and communications">
                YOUR LCA VENDOR NUMBER <span style={{ color:'rgba(255,255,255,0.25)' }}>ⓘ</span>
              </div>
              <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:28, fontWeight:700, color:'#C9A84C', letterSpacing:'0.06em', lineHeight:1 }}>{notary.vendor_number}</div>
            </div>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
              <span style={{ background:'#2D7A4F', color:'#fff', fontSize:11, fontWeight:600, padding:'4px 12px', borderRadius:20 }}>✓ Active</span>
              {notary.is_vetted  && <span style={{ background:'#0D1B3E', border:'1px solid #C9A84C', color:'#C9A84C', fontSize:11, fontWeight:700, padding:'4px 12px', borderRadius:20 }}>★ LCA Vetted</span>}
              {notary.is_ron     && <span style={{ background:'linear-gradient(135deg,#1A4A6B,#2D7AB5)', color:'#fff', fontSize:11, fontWeight:700, padding:'4px 12px', borderRadius:20 }}>💻 RON Notary</span>}
              {notary.is_founder && <span style={{ background:'linear-gradient(135deg,#9A7A30,#C9A84C)', color:'#0D1B3E', fontSize:11, fontWeight:700, padding:'4px 12px', borderRadius:20 }}>🏅 Founder</span>}
              {notary.is_elite   && <span style={{ background:'#C9A84C', color:'#0D1B3E', fontSize:11, fontWeight:700, padding:'4px 12px', borderRadius:20 }}>⚡ Elite</span>}
            </div>
          </div>
        )}

        {/* ── 2. BIO CARD ── */}
        <section className="section-card" style={{ marginBottom:16 }}>
          <div className="section-header"><h2 className="section-title">My Bio</h2></div>
          <div className="section-body">
            <div style={{ display:'flex', alignItems:'center', gap:20, flexWrap:'wrap', marginBottom:14 }}>
              <div style={{ width:90, height:90, borderRadius:'50%', overflow:'hidden', background:'#F8F7F4', border:'2px solid #E8E6DF', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, cursor:'pointer', position:'relative' }}
                title="📸 THIS PHOTO &amp; BIO WILL BE SHOWN TO CLIENTS when you are assigned to their signing!!">
                {profile?.headshot_url
                  ? <img src={profile.headshot_url} alt={profile?.full_name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  : <span style={{ fontSize:32 }}>👤</span>}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, fontWeight:700, color:'#0D1B3E', marginBottom:6 }}>{profile?.full_name || '—'}</div>
                {notary?.avg_rating > 0 && <div style={{ fontSize:12, color:'#C9A84C', fontWeight:600, marginBottom:8 }}>{'★'.repeat(Math.round(notary.avg_rating))} {notary.avg_rating}/5</div>}
                <label style={{ cursor:'pointer' }}>
                  <input type="file" accept="image/*" onChange={uploadHeadshot} style={{ display:'none' }} disabled={uploading} />
                  <span className="btn btn-outline btn-sm">{uploading ? 'Uploading...' : profile?.headshot_url ? '📸 Update Photo' : '📸 Upload Photo'}</span>
                </label>
              </div>
            </div>
          </div>
        </section>

        {/* ── 3. CREDENTIALS AT A GLANCE + SHARE BIO ── */}
        {notary && buildGlanceBadges().length > 0 && (
          <section className="section-card" style={{ marginBottom:16 }}>
            <div className="section-header">
              <h2 className="section-title">My Credentials at a Glance</h2>
              <button onClick={() => setShowBioPreview(true)} style={{ background:'#C9A84C', color:'#0D1B3E', border:'none', borderRadius:8, padding:'7px 14px', fontSize:12, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6, whiteSpace:'nowrap' }}
                title="👁️ PREVIEW your public bio, then share it with any company — even if they are not on LCA!!">
                👁️ Preview & Share Bio
              </button>
            </div>
            <div className="section-body">
              <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                {buildGlanceBadges().map((b, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:6, background:'#F8F7F4', border:'1px solid #E8E6DF', borderRadius:20, padding:'6px 14px', fontSize:12, color:'#0D1B3E', fontWeight:500 }}>
                    <span>{b.icon}</span>
                    <span>{b.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── 4. MILESTONE BADGES ── */}
        <section className="section-card" style={{ marginBottom:16 }}>
          <div className="section-header"><h2 className="section-title">My LCA Badges</h2></div>
          <div className="section-body">
            <div style={{ display:'flex', flexWrap:'wrap', gap:16, justifyContent:'center' }}>
              {/* ✅ FOUNDERS BADGE — first and most exclusive!! */}
              {notary?.is_founder && (
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
                  <img
                    src="https://utrvslwjxcvnctjfomit.supabase.co/storage/v1/object/public/credentials/founders-badge.png"
                    alt="Founders Badge"
                    title="🏅 LCA Founding Notary — One of the first 50!! This badge is yours forever!!"
                    style={{ width:80, height:80, objectFit:'contain', cursor:'help' }}
                  />
                  <div style={{ fontSize:11, fontWeight:600, color:'#9A7A30', textAlign:'center' }}>Founder</div>
                  <div style={{ fontSize:10, color:'#B8B5AB', textAlign:'center' }}>First 50</div>
                  <div style={{ fontSize:10, color:'#2D7A4F', fontWeight:600 }}>✓ Earned</div>
                </div>
              )}
              {[
                { rank:'Bronze',   threshold:50,   signings: notary?.total_signings || 0 },
                { rank:'Silver',   threshold:100,  signings: notary?.total_signings || 0 },
                { rank:'Gold',     threshold:250,  signings: notary?.total_signings || 0 },
                { rank:'Platinum', threshold:500,  signings: notary?.total_signings || 0 },
                { rank:'Elite',    threshold:1000, signings: notary?.total_signings || 0 },
              ].map(b => {
                const earned = b.signings >= b.threshold
                return (
                  <div key={b.rank} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, opacity: earned ? 1 : 0.35, filter: earned ? 'none' : 'grayscale(100%)' }}>
                    <img
                      src={RANK_BADGES[b.rank]}
                      alt={`${b.rank} Badge`}
                      title={earned ? `🎉 ${b.rank} achieved — ${b.threshold}+ signings!!` : `🔒 Complete ${b.threshold} signings to unlock`}
                      style={{ width:80, height:80, objectFit:'contain', cursor:'help' }}
                    />
                    <div style={{ fontSize:11, fontWeight:600, color: earned ? '#0D1B3E' : '#B8B5AB', textAlign:'center' }}>{b.rank}</div>
                    <div style={{ fontSize:10, color:'#B8B5AB', textAlign:'center' }}>{b.threshold}+ signings</div>
                    {earned && <div style={{ fontSize:10, color:'#2D7A4F', fontWeight:600 }}>✓ Earned</div>}
                  </div>
                )
              })}
            </div>
            <div style={{ fontSize:11, color:'#B8B5AB', textAlign:'center', marginTop:16 }}>
              You have completed <strong style={{ color:'#0D1B3E' }}>{notary?.total_signings || 0}</strong> signings!!
              {(notary?.total_signings || 0) < 1000 && (
                <span> Keep going — next badge at <strong style={{ color:'#C9A84C' }}>{
                  (notary?.total_signings || 0) < 50 ? 50 :
                  (notary?.total_signings || 0) < 100 ? 100 :
                  (notary?.total_signings || 0) < 250 ? 250 :
                  (notary?.total_signings || 0) < 500 ? 500 : 1000
                } signings</strong>!!</span>
              )}
            </div>
          </div>
        </section>

        {/* ── 5. ACCOUNT DETAILS ── */}
        <section className="section-card">
          <div className="section-header"><h2 className="section-title">Account Details</h2></div>
          <div className="section-body">
            <form onSubmit={save} noValidate>
              <div className="np-form-grid">
                <div className="form-group">
                  <label className="form-label" htmlFor="prof-name">Full name</label>
                  <input id="prof-name" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="prof-phone">Phone</label>
                  <input id="prof-phone" type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                </div>
                <div className="form-group np-full">
                  <label className="form-label">Email (read only)</label>
                  <div style={{ display:'flex', alignItems:'center', gap:10, padding:'11px 14px', border:'1.5px solid #E0D9CC', borderRadius:10, background:'#F5F5F3' }}>
                    <span style={{ fontSize:14, color:'#4A4840', flex:1 }}>{profile?.email || ''}</span>
                    <span style={{ fontSize:14, color:'#B8B5AB' }} title="Contact support to change your email">🔒</span>
                  </div>
                </div>
                <div className="form-group np-full">
                  <label className="form-label" htmlFor="prof-bio">Short Bio <span style={{ fontSize:10, color:'#B8B5AB' }}>(shown to clients when assigned)</span></label>
                  <textarea id="prof-bio" rows={3} placeholder="Hi, I'm a certified notary signing agent with X years of experience serving the Greater [City] area..." value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} style={{ resize:'vertical', fontFamily:'inherit', padding:'10px 14px', border:'1.5px solid #E0D9CC', borderRadius:10, fontSize:13, width:'100%' }} maxLength={500} />
                  <div style={{ fontSize:11, color:'#B8B5AB', marginTop:4 }}>{form.bio.length}/500 characters</div>
                </div>
                <div className="form-group np-full">
                  <label className="form-label" htmlFor="prof-address">Street Address</label>
                  <input id="prof-address" placeholder="123 Main Street" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="prof-city">City</label>
                  <input id="prof-city" placeholder="Jacksonville" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="prof-state">State</label>
                  <input id="prof-state" placeholder="FL" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} maxLength={2} />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="prof-zip">ZIP</label>
                  <input id="prof-zip" placeholder="32202" value={form.zip} onChange={e => setForm(f => ({ ...f, zip: e.target.value }))} maxLength={5} />
                </div>
              </div>
              <button className="btn btn-primary" type="submit" disabled={saving} style={{ marginTop:16 }}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </form>

            {/* 🔐 TWO-FACTOR AUTHENTICATION */}
            <MFASection />

          </div>
        </section>

        {/* 👁️ BIO PREVIEW MODAL — see your public bio before sharing */}
        {showBioPreview && (
          <div
            onClick={() => setShowBioPreview(false)}
            style={{ position:'fixed', inset:0, background:'rgba(13,27,62,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
            <div
              onClick={e => e.stopPropagation()}
              style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:560, maxHeight:'90vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ background:'linear-gradient(135deg,#0D1B3E,#1A3A5E)', color:'#fff', padding:'16px 20px' }}>
                <div style={{ fontSize:16, fontWeight:700, color:'#C9A84C' }}>👁️ Preview Your Public Bio</div>
                <div style={{ fontSize:12, color:'rgba(255,255,255,0.75)', marginTop:3 }}>This is exactly what companies see when you share your link.</div>
              </div>
              <iframe
                title="Public bio preview"
                src={`/bio/${profile?.id}`}
                style={{ flex:1, width:'100%', border:'none', minHeight:360, background:'#F8F7F4' }}
              />
              <div style={{ display:'flex', gap:8, padding:'12px 16px', borderTop:'1px solid #E8E6DF', flexWrap:'wrap', justifyContent:'flex-end' }}>
                <button
                  onClick={() => window.open(`/bio/${profile?.id}`, '_blank', 'noopener')}
                  style={{ background:'#F8F7F4', color:'#0D1B3E', border:'1px solid #E0D9CC', borderRadius:8, padding:'8px 14px', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                  ↗ Open Full Page
                </button>
                <button
                  onClick={() => {
                    const bioUrl = `${window.location.origin}/bio/${profile?.id}`
                    navigator.clipboard.writeText(bioUrl).then(() => toast.success('Bio link copied!! Share it anywhere!!'))
                  }}
                  style={{ background:'#C9A84C', color:'#0D1B3E', border:'none', borderRadius:8, padding:'8px 14px', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                  📋 Copy Link
                </button>
                <button
                  onClick={() => setShowBioPreview(false)}
                  style={{ background:'#0D1B3E', color:'#fff', border:'none', borderRadius:8, padding:'8px 14px', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

      </main>
    </>
  )
}

// ROUTER + EXPORT
// 📄 PDF libraries loader — reliable UMD loading via <script> tags (window globals)
function loadScriptOnce(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-pdfsrc="${src}"]`)
    if (existing) {
      if (existing.getAttribute('data-loaded') === '1') return resolve()
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('Failed to load ' + src)))
      return
    }
    const s = document.createElement('script')
    s.src = src; s.async = true; s.setAttribute('data-pdfsrc', src)
    s.addEventListener('load', () => { s.setAttribute('data-loaded', '1'); resolve() })
    s.addEventListener('error', () => reject(new Error('Failed to load ' + src)))
    document.head.appendChild(s)
  })
}
async function ensurePdfLibs() {
  if (!window.html2canvas) await loadScriptOnce('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js')
  if (!window.jspdf)       await loadScriptOnce('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js')
}

// 📄 PAY STUB MODAL — branded notary payout statement + Download PDF + Print
function PayStubModal({ job, notary, onClose }) {
  const docRef = useRef(null)
  const [busy, setBusy] = useState(false)

  const fmtDateTime = (v) => {
    if (!v) return '—'
    const d = new Date(v)
    if (isNaN(d.getTime())) return '—'
    return d.toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' })
      + ' · ' + d.toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit', timeZoneName:'short' })
  }
  const money = (n) => Number(n || 0).toLocaleString('en-US', { style:'currency', currency:'USD' })

  const paid    = !!job.notary_paid_at
  const signerLoc = [job.signing_city, job.signing_state].filter(Boolean).join(', ')
  const safeNo  = (job.job_number || 'paystub').replace(/[^a-z0-9]+/gi, '-')

  async function downloadPDF() {
    setBusy(true)
    try {
      await ensurePdfLibs()
      const canvas = await window.html2canvas(docRef.current, { scale: 2, useCORS: true, backgroundColor: '#fff' })
      const imgData = canvas.toDataURL('image/png')
      const { jsPDF } = window.jspdf
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
      const pageW = pdf.internal.pageSize.getWidth()
      const pageH = pdf.internal.pageSize.getHeight()
      const ratio = Math.min(pageW / canvas.width, pageH / canvas.height)
      const w = canvas.width * ratio, h = canvas.height * ratio
      pdf.addImage(imgData, 'PNG', (pageW - w) / 2, 20, w, h)
      pdf.save(`LCA-PayStub-${safeNo}.pdf`)
    } catch (err) {
      toast.error('PDF generation failed: ' + err.message)
    } finally {
      setBusy(false)
    }
  }

  async function printRecord() {
    setBusy(true)
    try {
      await ensurePdfLibs()
      const canvas = await window.html2canvas(docRef.current, { scale: 2, useCORS: true, backgroundColor: '#fff' })
      const imgData = canvas.toDataURL('image/png')
      const win = window.open('', '_blank')
      if (!win) { toast.error('Please allow pop-ups to print.'); return }
      win.document.write(`<html><head><title>LCA Pay Stub ${job.job_number || ''}</title></head><body style="margin:0;text-align:center;"><img src="${imgData}" style="width:100%;max-width:760px;" onload="window.focus();window.print();" /></body></html>`)
      win.document.close()
    } catch (err) {
      toast.error('Print failed: ' + err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:3000, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-start', padding:20, overflowY:'auto' }}>
      <div style={{ display:'flex', gap:10, marginBottom:16, position:'sticky', top:0, zIndex:5 }}>
        <button onClick={downloadPDF} disabled={busy}
          style={{ padding:'10px 20px', borderRadius:8, background:'linear-gradient(135deg,#C9A84C,#E8C46A)', color:'#0D1B3E', border:'none', cursor: busy?'wait':'pointer', fontWeight:700, fontSize:13, opacity: busy?0.7:1 }}>
          {busy ? '⏳ Working…' : '⬇️ Download PDF'}
        </button>
        <button onClick={printRecord} disabled={busy}
          style={{ padding:'10px 20px', borderRadius:8, background:'#fff', color:'#0D1B3E', border:'1px solid #C9A84C', cursor: busy?'wait':'pointer', fontWeight:700, fontSize:13, opacity: busy?0.7:1 }}>
          🖨️ Print
        </button>
        <button onClick={onClose} disabled={busy}
          style={{ padding:'10px 20px', borderRadius:8, background:'rgba(255,255,255,0.1)', color:'#fff', border:'1px solid rgba(255,255,255,0.3)', cursor:'pointer', fontSize:13 }}>
          ✕ Close
        </button>
      </div>

      {/* PAY STUB DOCUMENT */}
      <div ref={docRef} style={{ width:720, maxWidth:'100%', background:'#fff', boxShadow:'0 18px 50px rgba(13,27,62,0.25)' }}>
        <div style={{ border:'12px solid #0D1B3E', position:'relative', padding:'38px 42px' }}>
          <div style={{ position:'absolute', inset:8, border:'3px solid #C9A84C', pointerEvents:'none' }} />

          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:20 }}>
            <div>
              <div style={{ fontFamily:'Cormorant Garamond, Georgia, serif', fontSize:13, fontWeight:600, color:'#C9A84C', letterSpacing:'0.18em', textTransform:'uppercase' }}>Loan Closing Agent, LLC. DBA</div>
              <div style={{ fontFamily:'Cormorant Garamond, Georgia, serif', fontSize:21, fontWeight:700, color:'#0D1B3E', letterSpacing:'0.03em' }}>Nationwide Notary Professionals Network</div>
            </div>
            <div style={{ textAlign:'right', flexShrink:0 }}>
              <div style={{ fontFamily:'Cormorant Garamond, Georgia, serif', fontSize:30, fontWeight:700, color:'#0D1B3E', lineHeight:1 }}>PAY STUB</div>
            </div>
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:10, margin:'16px 0 20px' }}>
            <div style={{ flex:1, height:2, background:'linear-gradient(90deg,#0D1B3E,#C9A84C)' }} />
            <div style={{ color:'#C9A84C', fontSize:13 }}>✦</div>
            <div style={{ flex:1, height:2, background:'linear-gradient(90deg,#C9A84C,#0D1B3E)' }} />
          </div>

          <div style={{ display:'flex', justifyContent:'space-between', gap:24, marginBottom:24 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:10, fontWeight:700, color:'#C9A84C', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:6 }}>Pay To</div>
              <div style={{ fontSize:15, fontWeight:700, color:'#0D1B3E' }}>{notary?.full_name || 'Notary'}</div>
              {notary?.vendor_number && <div style={{ fontSize:13, color:'#2C2C2C' }}>Vendor #: {notary.vendor_number}</div>}
              {notary?.phone && <div style={{ fontSize:13, color:'#2C2C2C' }}>{notary.phone}</div>}
              {notary?.email && <div style={{ fontSize:13, color:'#1F5C99' }}>{notary.email}</div>}
            </div>
            <div style={{ flex:1, textAlign:'right' }}>
              <table style={{ marginLeft:'auto', fontSize:12, color:'#2C2C2C', borderCollapse:'collapse' }}>
                <tbody>
                  <tr><td style={{ padding:'2px 14px 2px 0', color:'#7A7870', textAlign:'right' }}>Stub #</td><td style={{ padding:'2px 0', fontWeight:700, color:'#0D1B3E', fontFamily:'Courier New, monospace' }}>{job.job_number || '—'}</td></tr>
                  <tr><td style={{ padding:'2px 14px 2px 0', color:'#7A7870', textAlign:'right' }}>Payment Date</td><td style={{ padding:'2px 0', color:'#0D1B3E' }}>{paid ? fmtDateTime(job.notary_paid_at) : 'Pending'}</td></tr>
                  <tr><td style={{ padding:'2px 14px 2px 0', color:'#7A7870', textAlign:'right' }}>Service Date</td><td style={{ padding:'2px 0', color:'#0D1B3E' }}>{fmtDateTime(job.scheduled_at)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:6 }}>
            <thead>
              <tr style={{ background:'#0D1B3E' }}>
                <th style={{ textAlign:'left', color:'#fff', fontSize:10, letterSpacing:'0.08em', textTransform:'uppercase', padding:'9px 12px' }}>Description</th>
                <th style={{ textAlign:'right', color:'#fff', fontSize:10, letterSpacing:'0.08em', textTransform:'uppercase', padding:'9px 12px', width:120 }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom:'1px solid #EDEAE2' }}>
                <td style={{ padding:'13px 12px', fontSize:13, color:'#2C2C2C', verticalAlign:'top' }}>
                  <div style={{ fontWeight:600, color:'#0D1B3E' }}>Notary Signing Service{job.signing_type ? ` — ${job.signing_type}` : ''}</div>
                  <div style={{ fontSize:11.5, color:'#7A7870', marginTop:3 }}>{job.borrower_name ? `Signer: ${job.borrower_name}` : ''}{job.borrower_name && signerLoc ? '  ·  ' : ''}{signerLoc}</div>
                  <div style={{ fontSize:11.5, color:'#7A7870', marginTop:2 }}>Job #: <span style={{ fontWeight:600, color:'#0D1B3E' }}>{job.job_number}</span></div>
                  {(job.file_number || job.client_po) && <div style={{ fontSize:11.5, color:'#7A7870', marginTop:2 }}>Client File #: <span style={{ fontWeight:600, color:'#0D1B3E' }}>{job.file_number || job.client_po}</span></div>}
                </td>
                <td style={{ padding:'13px 12px', fontSize:14, color:'#0D1B3E', textAlign:'right', fontWeight:600, verticalAlign:'top' }}>{money(job.notary_pay)}</td>
              </tr>
            </tbody>
          </table>

          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', gap:20, marginTop:14 }}>
            <div style={{ flex:1, fontSize:11, color:'#7A7870', fontStyle:'italic', lineHeight:1.5, maxWidth:320 }}>
              Payment for notary services rendered, paid via Direct Deposit. Thank you for being part of our network.
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:11, color:'#7A7870', letterSpacing:'0.08em', textTransform:'uppercase' }}>Total Paid</div>
              <div style={{ fontFamily:'Cormorant Garamond, Georgia, serif', fontSize:30, fontWeight:700, color:'#0D1B3E', lineHeight:1.1 }}>{money(job.notary_pay)}</div>
              {paid
                ? <div style={{ display:'inline-block', marginTop:6, padding:'4px 14px', borderRadius:20, background:'#D7F0DE', color:'#2D7A4F', fontSize:11, fontWeight:700, letterSpacing:'0.05em' }}>PAID · {fmtDateTime(job.notary_paid_at)}</div>
                : <div style={{ display:'inline-block', marginTop:6, padding:'4px 14px', borderRadius:20, background:'#FCE9C8', color:'#9A7A30', fontSize:11, fontWeight:700, letterSpacing:'0.05em' }}>PAYMENT PENDING</div>}
            </div>
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:10, margin:'24px 0 16px' }}>
            <div style={{ flex:1, height:1, background:'linear-gradient(90deg,transparent,#C9A84C)' }} />
            <div style={{ color:'#C9A84C', fontSize:11 }}>✦ ✦ ✦</div>
            <div style={{ flex:1, height:1, background:'linear-gradient(90deg,#C9A84C,transparent)' }} />
          </div>

          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:12, color:'#2C2C2C', marginBottom:4 }}>Issued by <span style={{ fontWeight:600, color:'#0D1B3E' }}>Loan Closing Agent, LLC.</span></div>
            <div style={{ fontSize:11, color:'#7A7870' }}>7946 Meadow Walk Ln.  ·  Jacksonville, FL 32256</div>
            <div style={{ fontSize:11, color:'#7A7870' }}>Questions? bookings@loanclosingagent.com  ·  904-865-0133</div>
          </div>
        </div>
      </div>
      <div style={{ height:30 }} />
    </div>
  )
}

// 📄 NOTARY PAY STUBS — notary self-service: view, download, print their own stubs
function NotaryPayStubs() {
  const { user } = useAuth()
  const [jobs,    setJobs]    = useState([])
  const [notary,  setNotary]  = useState(null)
  const [loading, setLoading] = useState(true)
  const [filter,  setFilter]  = useState('all')
  const [stubJob, setStubJob] = useState(null)

  const PAY_STATUSES = ['completed','scanbacks_uploaded','client_approved','payment_received','notary_paid','closed']

  useEffect(() => {
    if (!user) return
    async function load() {
      const [{ data: jobsData }, { data: prof }, { data: nrec }] = await Promise.all([
        supabase.from('jobs').select('*').eq('notary_id', user.id).in('status', PAY_STATUSES).order('scheduled_at', { ascending:false }),
        supabase.from('profiles').select('full_name, email, phone').eq('id', user.id).single(),
        supabase.from('notaries').select('vendor_number').eq('id', user.id).single(),
      ])
      setJobs(jobsData || [])
      setNotary({ full_name: prof?.full_name, email: prof?.email, phone: prof?.phone, vendor_number: nrec?.vendor_number })
      setLoading(false)
    }
    load()
  }, [user])

  const money = (n) => Number(n || 0).toLocaleString('en-US', { style:'currency', currency:'USD' })
  const filtered = jobs.filter(j => filter==='all' ? true : filter==='paid' ? !!j.notary_paid_at : !j.notary_paid_at)
  const totalPaid    = jobs.filter(j => !!j.notary_paid_at).reduce((s,j) => s+(j.notary_pay||0), 0)
  const totalPending = jobs.filter(j => !j.notary_paid_at).reduce((s,j) => s+(j.notary_pay||0), 0)

  if (loading) return <div style={{ textAlign:'center', padding:60 }}><div className="spinner" style={{ margin:'auto' }} /></div>

  return (
    <>
      <h1 className="page-title">Pay Stubs</h1>
      <p className="page-sub">Download or print a pay stub for any of your signings</p>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
        <div className="stat-card"><div className="stat-label">Total Paid</div><div className="stat-value" style={{ color:'#2D7A4F' }}>{money(totalPaid)}</div></div>
        <div className="stat-card"><div className="stat-label">Pending</div><div className="stat-value" style={{ color:'#9A7A30' }}>{money(totalPending)}</div></div>
        <div className="stat-card"><div className="stat-label">Signings</div><div className="stat-value">{jobs.length}</div></div>
      </div>
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        {['all','paid','pending'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`btn ${filter===f ? 'btn-primary' : 'btn-outline'} btn-sm`} style={{ textTransform:'capitalize' }}>{f}</button>
        ))}
      </div>
      <div className="section-card" style={{ padding:0 }}>
        <table className="data-table">
          <thead><tr><th>Job #</th><th>Service</th><th>Date</th><th>Pay</th><th>Status</th></tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={5} style={{ textAlign:'center', color:'#B8B5AB', padding:24 }}>No pay stubs in this view</td></tr>}
            {filtered.map(j => (
              <tr key={j.id}>
                <td style={{ fontWeight:500 }}>
                  {j.job_number}
                  <div><button onClick={() => setStubJob(j)} style={{ marginTop:6, background:'#fff', color:'#9A7A30', border:'1px solid #C9A84C', borderRadius:6, padding:'4px 12px', fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>📄 Pay Stub</button></div>
                </td>
                <td style={{ fontSize:12 }}>{j.signing_type}</td>
                <td style={{ fontSize:12 }}>{j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : '—'}</td>
                <td style={{ color:'#2D7A4F', fontWeight:600 }}>{money(j.notary_pay)}</td>
                <td>{j.notary_paid_at
                  ? <span style={{ fontSize:11, color:'#2D7A4F', fontWeight:600 }}>✓ Paid</span>
                  : <span style={{ fontSize:11, color:'#9A7A30', fontWeight:600 }}>Pending</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {stubJob && <PayStubModal job={stubJob} notary={notary} onClose={() => setStubJob(null)} />}
    </>
  )
}

const MYLOUNGE_PATH = '/notary/tribe'

export default function NotaryPortal() {
  const { user } = useAuth()
  const location = useLocation()
  const [loungeBadge, setLoungeBadge] = useState(0)
  const seenKey = user?.id ? `lca_mylounge_last_seen_${user.id}` : null
  const onLounge = location.pathname === MYLOUNGE_PATH || location.pathname.startsWith(MYLOUNGE_PATH + '/')
  const enteredLounge = useRef(false)

  // Mark the Lounge as "seen" whenever it's open → clears the badge.
  // On ENTRY (once), snapshot the previous "last seen" so the feed can tag NEW items.
  useEffect(() => {
    if (onLounge && seenKey) {
      if (!enteredLounge.current) {
        enteredLounge.current = true
        try {
          const prev = localStorage.getItem(seenKey)
          if (prev) localStorage.setItem(seenKey.replace('last_seen', 'prev_seen'), prev)
        } catch (e) { /* ignore */ }
      }
      try { localStorage.setItem(seenKey, new Date().toISOString()) } catch (e) { /* ignore */ }
      setLoungeBadge(0)
    } else if (!onLounge) {
      enteredLounge.current = false
    }
  }, [onLounge, seenKey])

  // Count new posts + replies from OTHERS since last seen (re-checks every 60s)
  useEffect(() => {
    if (!seenKey) return
    let active = true
    async function checkUnread() {
      if (onLounge) { if (active) setLoungeBadge(0); return }
      let lastSeen
      try { lastSeen = localStorage.getItem(seenKey) } catch (e) { /* ignore */ }
      if (!lastSeen) {
        // First time: start fresh so we only flag genuinely new activity going forward
        try { localStorage.setItem(seenKey, new Date().toISOString()) } catch (e) { /* ignore */ }
        if (active) setLoungeBadge(0)
        return
      }
      try {
        const [{ count: postCount }, { count: commentCount }] = await Promise.all([
          supabase.from('tribe_posts').select('id', { count: 'exact', head: true }).gt('created_at', lastSeen).neq('author_id', user.id),
          supabase.from('tribe_post_comments').select('id', { count: 'exact', head: true }).gt('created_at', lastSeen).neq('author_id', user.id),
        ])
        if (active) setLoungeBadge((postCount || 0) + (commentCount || 0))
      } catch (e) { /* ignore */ }
    }
    checkUnread()
    const t = setInterval(checkUnread, 60000)
    return () => { active = false; clearInterval(t) }
  }, [onLounge, seenKey, user])

  const navWithBadge = NAV.map(item => item.to === MYLOUNGE_PATH ? { ...item, badge: loungeBadge } : item)

  return (
    <PortalLayout sidebarItems={navWithBadge}>
      <Routes>
        <Route index              element={<NotaryDashboard />} />
        <Route path="available"   element={<AvailableJobs />} />
        <Route path="signings"    element={<MySignings />} />
        <Route path="jobs/:jobId" element={<NotaryJobDetail />} />
        <Route path="credentials" element={<Credentials />} />
        <Route path="earnings"    element={<Earnings />} />
        <Route path="paystubs"    element={<NotaryPayStubs />} />
        <Route path="rewards"     element={<EliteRewards />} />
        <Route path="academy"     element={<NotaryAcademy />} />
        <Route path="resources"   element={<NotaryResources />} />
        <Route path="/tribe" element={<Tribe />} />
        <Route path="/podcast" element={<TribePodcast />} />
        <Route path="assignment/:jobId" element={<NotaryAssignment />} />
        <Route path="profile"     element={<NotaryProfile />} />
        <Route path="*"           element={<NotaryDashboard />} />
      </Routes>
    </PortalLayout>
  )
}
